import { Component, InputComponent, Mesh, MeshComponent, Physics, PhysXComponent, Property, TextComponent } from '@wonderlandengine/api';
import { HowlerAudioSource } from '@wonderlandengine/components';
import { vec3, vec4, quat } from 'gl-matrix';
import { Leaderboard } from './leaderboard.js';
import { ProgressionSystem } from './progression-system.js';
import { ProximityChat } from './proximity-chat.js';
import { LobbyHandler } from './lobby-handler.js';
import { KinematicBullet } from './kinematic-bullet.js';
import { WeaponSync } from './weapon-sync.js';
import { AnalyticsManager, TargetType, EndReason, GameMode } from './analytics-manager.js';
import { Arena } from './net/arena.js';
import { setEngine } from '../src/adsManager.js';

/**
 * Persistent save slot shape returned by `Arena.save.load(slot)`.
 */

/**
 * Individual save slot structure
 * @typedef {Object} SaveSlot
 * @property {string} name - Display name for the save slot
 * @property {Object} saveData - The actual game save data
 * @property {string} created_at - ISO 8601 timestamp when slot was created
 * @property {string} updated_at - ISO 8601 timestamp when slot was last updated
 */

/**
 * Game-specific save data structure
 * @typedef {Object} GameSaveData
 * @property {number} level - Player's current level
 * @property {number} totalXp - Player's total accumulated XP
 * @property {number} prestige - Player's current prestige level
 * @property {string} lastSaved - ISO timestamp of when data was saved
 * @property {string} gameVersion - Version of the game that created this save
 */

/**
 * All saves response structure (from Arena.save.all())
 * @typedef {Object.<string, SaveSlot>} AllSavesResponse
 * Example: { "1": SaveSlot, "2": SaveSlot, ... }
 */

/**
 * arena-handler
 * One-way matchmaking: If no lobby, create. Else join if not full.
 */
export class ArenaHandler extends Component {
    static TypeName = 'arena-handler';

    static Properties = {
        head: Property.object(),
        debug: Property.bool(false),
        debugBotMatch: Property.bool(false),
        health: Property.int(100),
        player: Property.object(),
        fadingMat: Property.material(),
        playerRig: Property.object(),
        playerPool: Property.object(),
        bulletPool: Property.object(),
        botPool: Property.object(), // Add this line
        hitMarker: Property.object(),
        healthBar: Property.object(),
        killsTextObject: Property.object(),
        rightController: Property.object(), // Right controller for direct haptic feedback
        leftController: Property.object(), // Left controller for direct haptic feedback
        progressionBar: Property.object(),
        progressionTextObject: Property.object(),
        levelTextObject: Property.object(),
        xpDetailTextObject: Property.object(),
        victoryTextObject: Property.object(),
        medalInfoTextObject: Property.object(),
        medalDetailsTextObject: Property.object(),
        spawnLocations: Property.object(),
        hitSoundObject: Property.object(),
        killSoundObject: Property.object(),
        getHitSoundObject: Property.object(),
        getKilledSoundObject: Property.object(),
        levelUpSoundObject: Property.object(),
        unlockSoundObject: Property.object(), // Unlock notification sound
        // Medal sound objects (each containing HowlerAudioSource component)
        doubleKillSoundObject: Property.object(),
        tripleKillSoundObject: Property.object(),
        quadKillSoundObject: Property.object(),
        pentaKillSoundObject: Property.object(),
        pistolSpecialistSoundObject: Property.object(),
        rifleSpecialistSoundObject: Property.object(),
        shotgunSpecialistSoundObject: Property.object(),
        sniperSpecialistSoundObject: Property.object(),
        mercilessSoundObject: Property.object(),
        rampageSoundObject: Property.object(),
        leaderboardObject: Property.object(),
        untouchableSoundObject: Property.object(),
        closeQuartersSoundObject: Property.object(),
        sniperEliteSoundObject: Property.object(),
        longShotSoundObject: Property.object(),
        lastStandSoundObject: Property.object(),
        medalAchievementSoundObject: Property.object(), // General medal achievement sound played with every medal
        // Distant gunshot emitter pools (parents containing multiple HowlerAudioSource children)
        distantPistolPoolParent: Property.object(),
        distantRiflePoolParent: Property.object(),
        distantShotgunPoolParent: Property.object(),
        distantSniperPoolParent: Property.object(),
        distantBotPoolParent: Property.object(),
        // Prestige symbol objects (to show/hide based on prestige level)
        prestigeSymbol1: Property.object(), // Levels 1-25 symbol
        prestigeSymbol2: Property.object(), // Prestige 1 symbol  
        prestigeSymbol3: Property.object(), // Prestige 2 symbol
        prestigeSymbol4: Property.object(), // Prestige 3 symbol
        // Persistent game-mode indicator (always visible during match)
        gameModeTextObject: Property.object(),
        // Displays current player name or team name
        teamNameTextObject: Property.object(),
        // Team name text color materials
        matTextRed: Property.material(),
        matTextBlue: Property.material(),
        matTextDefault: Property.material(),
        // Chaos Mode materials (drag pre-configured high-contrast materials in the editor)
        matPlayerRed: Property.material(),
        matPlayerBlue: Property.material(),
        matPlayerGold: Property.material(),
        matPlayerGreen: Property.material(),
        matPlayerPink: Property.material(),
        matPlayerPurple: Property.material(),
        matPlayerCyan: Property.material(),
        matPlayerOrange: Property.material(),
        maxPlayers: Property.int(8),
        team: Property.string('aaaa'),
        room: Property.string('_ArenaWars42'),
        // Save game management
        allowSaveWipe: Property.bool(false), // Allow wiping all save games when true
        lobbyHandler: Property.object(), // Reference to lobby handler for save wipe coordination
    };

    // Level system constants - Scaled so one game (15 kills) = 1-4 levels max
    static MAX_LEVEL = 250; // Increased cap before prestige (since levels are faster)
    static BASE_XP = 1500; // XP required for level 1 → 2 (equivalent to 15 kills)
    static XP_GROWTH = 1.15; // Moderate exponential curve (15% harder each level)

    // QUEST 2 OPTIMIZATION: Hoist constant damage/headshot tables to class level
    // Avoids re-creating object literals on every bullet hit
    static DAMAGE_TABLE = { default: 12, rifle: 18, shotgun: 15, sniper: 75, bot: 10 };
    static HEADSHOT_MULTIPLIERS = { default: 1.5, rifle: 1.5, shotgun: 1.5, sniper: 1.7, bot: 1.0 };
    // QUEST 2 OPTIMIZATION: Hoist haptic settings to class level (called per shot)
    static WEAPON_HAPTIC_SETTINGS = {
        'default': { intensity: 0.3, duration: 150 },
        'rifle': { intensity: 0.5, duration: 100 },
        'shotgun': { intensity: 0.8, duration: 80 },
        'sniper': { intensity: 0.7, duration: 120 },
    };

    // Calculate XP required for next level (now uses effective level for consistent prestige progression)
    static getXpForNextLevel(effectiveLevel) {
        // Use local level within prestige (1-25) for XP curve
        // effectiveLevel: 1-25, 26-50, 51-75, ...
        const localLevel = ((effectiveLevel - 1) % 25) + 1;
        return Math.floor(ArenaHandler.BASE_XP * Math.pow(ArenaHandler.XP_GROWTH, localLevel - 1));
    }

    // Calculate total XP required to reach a specific effective level
    static getTotalXpForLevel(effectiveLevel) {
        let totalXp = 0;
        for (let i = 1; i < effectiveLevel; i++) {
            totalXp += ArenaHandler.getXpForNextLevel(i);
        }
        return totalXp;
    }

    // Calculate effective level from total XP (continuous progression across prestiges)
    static getEffectiveLevelFromXp(totalXp) {
        let effectiveLevel = 1;
        let currentXp = 0;
        while (effectiveLevel < ArenaHandler.MAX_LEVEL && currentXp <= totalXp) {
            const xpForNext = ArenaHandler.getXpForNextLevel(effectiveLevel);
            if (currentXp + xpForNext > totalXp) break;
            currentXp += xpForNext;
            effectiveLevel++;
        }
        return effectiveLevel;
    }
    init() {
        if (this.engine) {
            setEngine(this.engine);
        }
        // ─── MEMORY ALLOCATION ───
        // Pre-allocate all math objects and internal state variables.
        // No scene lookups here — the scene graph may not be fully ready.

        // Reusable math temporaries (update loop, networking, gameplay)
        this.tempPos = vec3.create();
        this.tempRot = vec4.create();
        this.spawnPos = vec3.create();
        this.spawnRot = quat.create();
        this._sendPos = new Float32Array(3);
        this._sendRot = new Float32Array(4);
        this.tempEuler = vec3.create(); // For extracting yaw from head rotation
        this.up = vec3.fromValues(0, 1, 0); // Up vector for yaw rotation
        // preallocat weapon send pos and rot
        this._sendWeaponPos = new Float32Array(3);
        this._sendWeaponRot = new Float32Array(4);
        this._sendWeaponPosLeft = new Float32Array(3);
        this._sendWeaponRotLeft = new Float32Array(4);

        // AFK detection vectors
        this._previousPlayerPos = vec3.create();
        this._previousPlayerRot = vec4.create();
        this._afkCurrentPos = vec3.create();
        this._afkCurrentRot = vec4.create();

        // Fade system buffers
        this._fadeColor = new Float32Array(4);
        this._fadeColorArr = new Float32Array(4);
        this._fadeAlpha = 1.0;
        this._fadeDebugFrames = 0;
        this._fadeTargetAlpha = 0.0;
        this._fadeDuration = 1.0;
        this._fadeElapsed = 0;
        this._fadeCallback = null;
        // Fade token system: incrementing counter and active token tracking
        this._fadeTokenCounter = 0;
        this._fadeActiveToken = null; // token id of currently-active fade
        this._fadeCallbackToken = null; // token associated with stored callback
        this._fadeState = 'in';
        this._inCountdown = false;

        // Damage flash overlay buffers
        this._dmgFlashActive = false;
        this._dmgFlashElapsed = 0;
        this._dmgFlashDuration = 0.35;
        this._dmgFlashPeakAlpha = 0.5;
        this._dmgFlashRiseTime = 0.05;
        // Default damage flash color: red, initially transparent
        this._dmgFlashColorArr = new Float32Array([1.0, 0.0, 0.0, 0.0]);

        // Constant / reusable vectors (never mutated during gameplay)
        this._hitMarkerHidePos = new Float32Array([0, -1000, 0]);
        this._upVec = new Float32Array([0, 1, 0]);
        this._zeroVec3 = new Float32Array(3);
        this._cyanColor = new Float32Array([0, 1, 1, 1]);
        this._goldColor = new Float32Array([1, 0.84, 0, 1]);
        this._progressScale = new Float32Array([0, 0.005, 0.5]);
        this._healthBarScale = new Float32Array([0.5, 0.01, 0.5]);
        this._desktopUITmp = new Float32Array(3);

        // Gameplay hit / bullet helpers
        this._botHeadCheckPos = vec3.create();
        this._hitPosPayload = new Float32Array(3);

        // QUEST 2 OPTIMIZATION: Pre-allocate network update payload (reused every 100ms)
        this._updatePayload = {
            id: null, pos: null, rot: null, t: 0,
            justRespawned: false, weapon: null,
            weaponPos: null, weaponRot: null,
            weaponPosLeft: null, weaponRotLeft: null,
        };
        this._lastSentLevel = -1;
        this._lastSentPrestige = -1;

        // QUEST 2 OPTIMIZATION: Pre-allocate network message wrapper to prevent GC
        this._networkUpdateMsg = {
            type: 'playerUpdate',
            payload: this._updatePayload
        };

        // Remote player optimisation buffers
        this._remotePlayerKeys = [];
        this._freeRemoteSlots = [];
        this._remotePlayerMapSize = 0;

        // Proximity voice chat
        this.proximityChat = new ProximityChat();
        window._proxChatInstance = this.proximityChat;

        // Frame-level accumulators (avoid per-frame allocation)
        this._lastSpeedrunSeconds = -1;
        this._elapsedMs = 0;
        this._afkCheckTimer = 0;
        this._remoteValidationTimer = 0;
        this._updateTimer = 0;
        this._hitMarkerActiveTime = 0;

        // Scalar state flags
        this.isConnected = false;
        this._gameStarted = false;
        this._sessionGen = 0;
        this._matchActive = false;
        this._countdownStartedAt = null;
        this._hitMarkerActive = false;
        this._isImmortal = false;
        this._isDead = false;
        this.justRespawned = false;
        this.inSummary = false;
        this._botModeActive = false;
        this._setupFlag = false;
        this._dataHandlerRegistered = false;
        this._isLoadingProgress = false;
        this._desktopUIApplied = false;

        // Chaos Mode: FFA when exactly 5 or 7 players are in the lobby
        this.isChaosMode = false;

        // Per-mode config (set by updateGameMode, defaults to standard values)
        this._killsToWin = 15;
        this._xpPerKill = 100;

        // Player progression (bypasses setter — no scene refs needed yet)
        this.kills = 0;
        this.deaths = 0;
        this.xp = 0;
        this._level = 1;
        this.totalXp = 0;
        this.prestige = 0;
        // War Coins — in-game earned currency for the coin shop
        this.warCoins = 0;
        this.licenses = { weapons: {}, attachments: [] };
        this.loadout = { 
            primary: { id: 'default', attachments: { laser: 'none', crosshair: 'none' } },
            secondary: { id: 'none', attachments: { laser: 'none', crosshair: 'none' } }
        };
        // XP boost multiplier (default 1x) — updated from Arena inventory when available
        this._xpMultiplier = 1;

        // AFK / auto-heal / save throttle
        this._afkTimer = 0;
        this._afkTimeoutMs = 60000;
        this._healTimer = 0;
        this._healDelay = 3.0;
        this._healRate = 10; // HP per second
        this._lastSaveTime = 0;
        this._isUserLoggedIn = false;
        this.userId = null;
        this.userName = null;
        this._hasSyncedWithRemote = false;

        // Summary delay
        this._summaryDelayAttempted = false;
        this._gameEnded = false;
        this._pendingWinnerId = null;
        this._pendingWinnerKills = 0;

        // Mode notification guard
        this._modeNotifTimeout = null;

        // Medal tracking collections (pre-allocated once, .clear() on reset)
        this._killTimestamps = [];
        this._killTsStart = 0;
        this._awardedMultiKillMedals = new Set();
        this._multiKillResetTimer = 0;
        this._currentKillStreak = 0;
        this._currentWeaponKills = new Map();
        this._lastKillWeapon = null;
        this._consecutiveSameWeaponKills = 0;
        this._medalQueue = [];
        this._medalQueueSet = new Set();
        this._currentMedalDisplayTime = 0;
        this._medalDisplayDuration = 1.5;
        this._medalDelay = 0.1;
        this._processingMedal = false;
        this._unlockDetails = new Map();
        this._medalDelayTimer = 0;
        this._recentMedals = new Map();
        this._xpDisplayDuration = 1.5;

        // Tracking maps
        this._playerReadyMap = new Map();
        this.playerLevels = new Map();
        this.playerPrestiges = new Map();
        this.playerStats = new Map();
        this._playerActivity = new Map();
        this._activityInterval = null;
        this.remotePlayerMap = new Map();

        // Team assignment map: sessionId → 'A' | 'B' | null (null = FFA/chaos/bot)
        this._teamMap = new Map();
        // Deterministic color map: sessionId → color index (0–7), stable across list reorders
        this._playerColorMap = new Map();
        // Timestamp of last authoritative team update received/applied
        this._lastTeamUpdateTs = 0;

        // PERF: Pre-cache bot pool in init() so first bot match doesn't pay getComponent cost
        this._botObjects = [];
        this._botComponents = [];
    }

    start() {
        window.arenaHandler = this;
        if (this.progressionBar) this.progressionBar.setScalingLocal(this._progressScale);
        if (this.healthBar) this.healthBar.setScalingLocal([0.5, 0.01, 0.5]);
        if (!this.progressionBar) console.warn('Progression bar reference is missing!');
        // ─── CACHING & REFERENCES ───
        // Cache all component references, find UI elements, set up event listeners.
        // Runs before the user interacts with the "Enter Arena" button.
        this.spawns = this.spawnLocations.children;
        // XR session-start listener (re-initialize clean state on re-entry)
        if (this.engine && this.engine.onXRSessionStart) {
            this._boundOnXRSessionStart = this.onXRSessionStart.bind(this);
            this.engine.onXRSessionStart.add(this._boundOnXRSessionStart);
        }
        // XR / desktop session-end listeners
        if (this.engine && this.engine.onXRSessionEnd) {
            this._boundOnXRSessionEnd = this.onXRSessionEnd.bind(this);
            this.engine.onXRSessionEnd.add(this._boundOnXRSessionEnd);
        }
        // Hide/show proximity chat toggle during XR sessions
        if (this.engine) {
            const _micBtn = document.getElementById('mic-toggle');
            if (_micBtn) {
                if (this.engine.onXRSessionStart) {
                    this.engine.onXRSessionStart.add(() => { _micBtn.style.display = 'none'; });
                }
                if (this.engine.onXRSessionEnd) {
                    this.engine.onXRSessionEnd.add(() => { _micBtn.style.display = 'block'; });
                }
            }
        }
        this._boundOnDesktopSessionEnd = this.onDesktopSessionEnd.bind(this);
        window.addEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
        // Mobile uses the same FPS pipeline — mirror desktop end/start handlers.
        window.addEventListener('mobile-session-end',   this._boundOnDesktopSessionEnd);

        // Prevent tab-unfocus immortality: respawn player when tab becomes hidden during a match
        this._boundOnVisibilityChange = (e) => {
            // [ANALYTICS HOOK] Safely flush payload on rage-quits
            if (document.visibilityState === 'hidden' || e?.type === 'pagehide') {
                AnalyticsManager.instance._handleExit(e);
            }

            if (document.hidden) {
                this.engine.onXRSessionEnd.add(() => {
                    console.log("[VISIBILITY CHANGE] Document hidden, ending XR session to prevent tab-unfocus immortality.");
                });
            }
        };

        document.addEventListener('visibilitychange', this._boundOnVisibilityChange);
        window.addEventListener('pagehide', this._boundOnVisibilityChange);

        // Check if save wipe is requested via property
        if (this.allowSaveWipe) {
            setTimeout(() => { this.executeSaveWipe(); }, 1000);
        }

        // Start with fade overlay fully transparent (visible scene)
        // Use a very short fade-out so the update loop applies the material change
        // (direct assignment in start() can be missed on the first frame)
        // console.log('[FADE DEBUG] fadingMat:', this.fadingMat, 'type:', typeof this.fadingMat);
        if (this.fadingMat) {
            // console.log('[FADE DEBUG] diffuseColor before:', this.fadingMat.diffuseColor);
            this._fadeColorArr[0] = 0;
            this._fadeColorArr[1] = 0;
            this._fadeColorArr[2] = 0;
            this._fadeColorArr[3] = 0;
            this.fadingMat.diffuseColor = this._fadeColorArr;
            // console.log('[FADE DEBUG] diffuseColor after:', this.fadingMat.diffuseColor);
        } else {
            // console.warn('[FADE DEBUG] fadingMat is NULL/undefined in start()!');
        }
        this._fadeAlpha = 0.0;
        this._fadeState = 'none';

        // ─── Cache UI text components ───
        this.killsText = this.killsTextObject ? this.killsTextObject.getComponent(TextComponent) : null;
        this.progressionText = this.progressionTextObject ? this.progressionTextObject.getComponent(TextComponent) : null;
        this.levelText = this.levelTextObject ? this.levelTextObject.getComponent(TextComponent) : null;
        this.xpDetailText = this.xpDetailTextObject ? this.xpDetailTextObject.getComponent(TextComponent) : null;
        this.medalInfoText = this.medalInfoTextObject ? this.medalInfoTextObject.getComponent(TextComponent) : null;
        this.medalDetailsText = this.medalDetailsTextObject ? this.medalDetailsTextObject.getComponent(TextComponent) : null;
        this.leaderboard = this.leaderboardObject ? this.leaderboardObject.getComponent(Leaderboard) : null;
        this.victoryText = this.victoryTextObject ? this.victoryTextObject.getComponent(TextComponent) : null;
        if (this.victoryText) this.victoryText.active = false;
        this.gameModeText = this.gameModeTextObject ? this.gameModeTextObject.getComponent(TextComponent) : null;

        // Metrics
        this._saveAttemptCount = 0; // total number of times we attempted a cloud save during this session
        if (this.gameModeText) {
            this.gameModeText.text = '';
            this.gameModeText.active = false;
        }
        this.teamNameText = this.teamNameTextObject ? this.teamNameTextObject.getComponent(TextComponent) : null;
        if (this.teamNameText) {
            this.teamNameText.text = '';
            this.teamNameText.active = false;
            // Cache the original material as fallback if matTextDefault not assigned
            if (!this.matTextDefault) this.matTextDefault = this.teamNameText.material;
        }

        // ─── Cache sound components (editor properties are ready in start) ───
        // Core sounds needed on first hit/kill — cached eagerly
        this.hitSound = this.hitSoundObject ? this.hitSoundObject.getComponent(HowlerAudioSource) : null;
        this.killSound = this.killSoundObject ? this.killSoundObject.getComponent(HowlerAudioSource) : null;
        this.getHitSound = this.getHitSoundObject ? this.getHitSoundObject.getComponent(HowlerAudioSource) : null;
        this.getKilledSound = this.getKilledSoundObject ? this.getKilledSoundObject.getComponent(HowlerAudioSource) : null;
        this.levelUpSound = this.levelUpSoundObject ? this.levelUpSoundObject.getComponent(HowlerAudioSource) : null;
        this.unlockSound = this.unlockSoundObject?.getComponent(HowlerAudioSource) ?? null;
        // ─── Cache medal sound components eagerly (avoids 16 WASM calls on first medal mid-combat) ───
        this.doubleKillSound = this.doubleKillSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.tripleKillSound = this.tripleKillSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.quadKillSound = this.quadKillSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.pentaKillSound = this.pentaKillSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.pistolSpecialistSound = this.pistolSpecialistSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.rifleSpecialistSound = this.rifleSpecialistSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.shotgunSpecialistSound = this.shotgunSpecialistSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.sniperSpecialistSound = this.sniperSpecialistSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.mercilessSound = this.mercilessSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.rampageSound = this.rampageSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.untouchableSound = this.untouchableSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.closeQuartersSound = this.closeQuartersSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.sniperEliteSound = this.sniperEliteSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.longShotSound = this.longShotSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.lastStandSound = this.lastStandSoundObject?.getComponent(HowlerAudioSource) ?? null;
        this.medalAchievementSound = this.medalAchievementSoundObject?.getComponent(HowlerAudioSource) ?? null;
        // ─── Cache input components for haptic feedback ───
        this.rightInput = this.rightController?.getComponent(InputComponent) ?? null;
        this.leftInput = this.leftController?.getComponent(InputComponent) ?? null;
        this.rightGamepad = this.rightInput?.xrInputSource?.gamepad ?? null;
        this.leftGamepad = this.leftInput?.xrInputSource?.gamepad ?? null;

        // ─── Build distant gunshot emitter pools (round-robin) ───
        this._emitterPools = {
            pistol: { emitters: this._gatherEmitters(this.distantPistolPoolParent), index: 0 },
            rifle: { emitters: this._gatherEmitters(this.distantRiflePoolParent), index: 0 },
            shotgun: { emitters: this._gatherEmitters(this.distantShotgunPoolParent), index: 0 },
            sniper: { emitters: this._gatherEmitters(this.distantSniperPoolParent), index: 0 },
            bot: { emitters: this._gatherEmitters(this.distantBotPoolParent), index: 0 }
        };

        // ─── Cache player movement component ───
        this.playerMovement = this.player ? this.player.getComponent('fps_movement') : null;

        // Initialize game stats via setter (triggers UI update)
        this.level = 1;

        // Initialize prestige symbols
        this.updatePrestigeSymbols();

        if (this.levelText) {
            this.levelText.text = `${this.level}`;
        }

        // AFK: snapshot initial player pose (uses pre-allocated vectors from init)
        if (this.player) {
            this.player.getPositionWorld(this._previousPlayerPos);
            this.head.getRotationWorld(this._previousPlayerRot);
        }

        // Load saved game data (level and XP) - event-driven for reliability
        this._loadSavedProgressWhenReady();

        // DEBUG: Show all unlocks sequence after scene loads
        if (this.debug) {
            setTimeout(() => { this._debugShowAllUnlocks(); }, 2000);
        }

        // Desktop UI: offset left eye (parent of all UI) when DESKTOP button is pressed
        this._UIRig = this.healthBar ? this.healthBar.parent : null;
        if (this._UIRig) {
            this._UIRigOrigPos = new Float32Array(3);
            this._UIRig.getPositionLocal(this._UIRigOrigPos);
        }
        this._boundOnDesktopSessionStart = () => {
            this._applyDesktopEyeOffset();
            // [ANALYTICS HOOK]
            AnalyticsManager.instance.logEnterDesktop();
        };
        window.addEventListener('desktop-session-start', this._boundOnDesktopSessionStart);
        window.addEventListener('mobile-session-start',  this._boundOnDesktopSessionStart);

        // ─── Chaos Mode: material lookup array (indexed by join order) ───
        this._slotMats = [
            this.matPlayerRed,
            this.matPlayerBlue,
            this.matPlayerGold,
            this.matPlayerGreen,
            this.matPlayerPink,
            this.matPlayerPurple,
            this.matPlayerCyan,
            this.matPlayerOrange,
        ];
        // tempPos, tempRot, _sendPos, _sendRot pre-allocated in init()
        this.networkedBullets = this.bulletPool.children;
        this.bulletIndex = 0;
        // QUEST 2 OPTIMIZATION: Pre-cache getComponent on each bullet in the pool
        // Avoids 2x WASM getComponent calls per bullet spawn
        for (let i = 0; i < this.networkedBullets.length; i++) {
            const b = this.networkedBullets[i];
            // Cache the kinematic-bullet component once per pool slot.
            // No PhysXComponent needed — movement is now manual.
            if (!b._kinBulletCache) {
                b._kinBulletCache = b.getComponent(KinematicBullet);
            }
        }

        // QUEST 2 OPTIMIZATION: Cache playerRig children as a Set for O(1) isOwnBody checks
        this._playerRigChildSet = this.playerRig && this.playerRig.children
            ? new Set(this.playerRig.children) : new Set();

        // Hex colour values matching each chaos material (same order)
        this._chaosHexColors = [
            [1.0, 0.0, 0.0, 1.0],  // Red
            [0.0, 0.0, 1.0, 1.0],  // Blue
            [1.0, 0.84, 0.0, 1.0], // Gold
            [0.0, 1.0, 0.0, 1.0],  // Green
            [1.0, 0.41, 0.71, 1.0],// Pink
            [0.5, 0.0, 0.5, 1.0],  // Purple
            [0.0, 1.0, 1.0, 1.0],  // Cyan
            [1.0, 0.5, 0.0, 1.0],  // Orange
        ];
        this.remotePlayers = this.playerPool.children;
        this.remotePlayerMap.clear();
        this.lobbyH = this.lobbyHandler.getComponent(LobbyHandler);
        this._initializeRemotePlayerSlots();
        this._remoteToMaterial = new Map();
        this._slotOriginalColors = new Map();
        for (let i = 0; i < this.remotePlayers.length && i < this._slotMats.length; i++) {
            const mat = this._slotMats[i];
            this._remoteToMaterial.set(this.remotePlayers[i], mat);
            if (mat && mat.getDiffuseColor) {
                this._slotOriginalColors.set(mat, mat.getDiffuseColor(new Float32Array(4)));
            }
        }
        // Bot pool arrays pre-allocated in init(); re-populate if empty (first call)
        if (this._botComponents.length === 0) this._initBotPool();
    }

    // Override level setter to track changes (prestige now handled in applyXP)
    set level(newLevel) {
        const oldLevel = this._level;

        // Set the level directly (prestige calculation now happens in applyXP)
        this._level = newLevel;

        // Store local player's level in the playerLevels Map for sync with other players
        if (this.userId && typeof this._level === 'number') {
            this.playerLevels.set(this.userId, this._level);
        }

        // Always update level display when level changes (skip only during loading)
        if (this.levelText && typeof this._level === 'number' && !this._isLoadingProgress) {
            this._updateLevelText();
            // SAVE ON SIGNIFICANT PROGRESS: Level Up
            // Only trigger when level increased to avoid extra saves during restores
            try {
                if (newLevel > oldLevel) {
                    // non-forced save respects internal throttling
                    this.saveProgress();
                }
            } catch (e) {
                console.warn('[Arena Handler] saveProgress() threw in level setter:', e);
            }
        }

        // Local player level display is handled by this.levelText above;
        // PhysicsPlayerBody has no level-sync component, so skip setRemotePlayerLevel.
    }

    /**
     * Trigger direct haptic feedback using working gamepad approach
     * @param {number} intensity - Vibration intensity (0.0 - 1.0)
     * @param {number} duration - Duration in milliseconds
     * @param {string} hand - Which hand(s) to trigger ('left', 'right', 'both')
     */
    triggerHapticFeedback(intensity = 1.0, duration = 50, hand = 'both') {
        // Get the gamepads from the input components
        if (this.rightInput && this.rightInput.xrInputSource) {
            this.rightGamepad = this.rightInput.xrInputSource.gamepad;
        }
        if (this.leftInput && this.leftInput.xrInputSource) {
            this.leftGamepad = this.leftInput.xrInputSource.gamepad;
        }

        // Use the working direct gamepad approach for right hand
        if ((hand === 'right' || hand === 'both') && this.rightGamepad) {
            if (this.rightGamepad.hapticActuators && this.rightGamepad.hapticActuators.length) {
                this.rightGamepad.hapticActuators[0].pulse(intensity, duration);
            }
        }

        // Use the working direct gamepad approach for left hand
        if ((hand === 'left' || hand === 'both') && this.leftGamepad) {
            if (this.leftGamepad.hapticActuators && this.leftGamepad.hapticActuators.length) {
                this.leftGamepad.hapticActuators[0].pulse(intensity, duration);
            }
        }
    }

    /**
     * Trigger weapon-specific haptic feedback for shooting
     * @param {string} weaponType - Type of weapon ('default', 'rifle', 'shotgun', 'sniper')
     * @param {string} hand - Which hand fired ('left', 'right', 'both')
     */
    triggerWeaponHapticFeedback(weaponType = 'default', hand = 'right') {
        // QUEST 2 OPTIMIZATION: Use static class-level settings instead of per-call object creation
        const settings = ArenaHandler.WEAPON_HAPTIC_SETTINGS[weaponType] || ArenaHandler.WEAPON_HAPTIC_SETTINGS['default'];
        this.triggerHapticFeedback(settings.intensity, settings.duration, hand);
    }

    /**
     * Handle prestige upgrade
     * @param {number} newPrestige - New prestige level
     */
    handlePrestige(newPrestige) {

        // Get prestige rewards
        const rewards = ProgressionSystem.getPrestigeRewards(newPrestige);

        if (rewards.length > 0) {

            // Show compact prestige notification
            const prestigeName = ProgressionSystem.getPrestigeName(newPrestige);
            const rewardDescriptions = rewards.map(r => ProgressionSystem.getUnlockDescription(r)).join(' • ');
            this.showUnlockNotification(1, `${prestigeName} • ${rewardDescriptions}`);

            // Update movement system with prestige rewards
            if (this.player) {
                const movement = this.player.getComponent('fps_movement');
                if (movement && movement.updateProgressionUnlocks) {
                    movement.updateProgressionUnlocks(1, newPrestige);
                }
            }
        }

        // Special message for reaching max prestige
        if (newPrestige >= ProgressionSystem.MAX_PRESTIGE) {
            this._guardedTimeout(() => {
                this.showUnlockNotification(1, "Legend Status Achieved");
            }, 2000);
        }

        // Play special prestige sound if available
        if (this.levelUpSound) {
            this.levelUpSound.play();
        }

        // Strong haptic feedback for prestige
        this.triggerHapticFeedback(1.0, 500, 'both');
        // Award War Coins for prestige milestone
        this.addWarCoins(200, 'prestige');

        // Save
        try {
            this.saveProgress(true);
        } catch (e) {
            console.warn('[Arena Handler] saveProgress(true) threw in handlePrestige:', e);
        }
    }

    /**
     * Show special milestone celebration for Legend players
     * @param {number} level - Current level milestone
     */
    showLegendMilestone(level) {

        // Special celebrations for ultimate unlocks
        if (level === 25 && this.prestige >= 3) {
            this.showUnlockNotification(level, "Master\nSniper Unlocked");
            // Extra celebration for this epic milestone
            this._guardedTimeout(() => {
                this.showUnlockNotification(level, "Legend\nUltimate Mastery");
            }, 3000);
        } else {
            // Regular milestone celebration
            this.showUnlockNotification(level, `Legend Lv.${level}\nElite Warrior`);
        }

        // Play celebration sound
        if (this.levelUpSound) {
            this.levelUpSound.play();
        }

        // Epic haptic feedback for major milestones
        this.triggerHapticFeedback(1.0, 300, 'both');

        // Additional celebration for major milestones (50, 100, 150, etc.)
        if (level % 50 === 0) {
            this._guardedTimeout(() => {
                this.showUnlockNotification(level, `Elite Milestone\nLv.${level}`);
            }, 2000);
        }
    }

    /**
     * Update prestige symbol visibility
     */
    updatePrestigeSymbols() {
        // Cache MeshComponent lookups on first call
        if (!this._prestigeMeshes) {
            this._prestigeMeshes = [
                this.prestigeSymbol1 ? this.prestigeSymbol1.getComponent(MeshComponent) : null,
                this.prestigeSymbol2 ? this.prestigeSymbol2.getComponent(MeshComponent) : null,
                this.prestigeSymbol3 ? this.prestigeSymbol3.getComponent(MeshComponent) : null,
                this.prestigeSymbol4 ? this.prestigeSymbol4.getComponent(MeshComponent) : null,
            ];
        }
        // Hide all prestige symbols
        for (let i = 0; i < this._prestigeMeshes.length; i++) {
            if (this._prestigeMeshes[i]) this._prestigeMeshes[i].active = false;
        }
        // Show the correct one
        if (this.prestige >= 0 && this.prestige < this._prestigeMeshes.length && this._prestigeMeshes[this.prestige]) {
            this._prestigeMeshes[this.prestige].active = true;
        }
    }

    /**
     * Offset left eye (parent of all UI) for desktop flat-screen mode.
     */
    _applyDesktopEyeOffset() {
        if (!this._UIRig || this._desktopUIApplied) return;
        const tmp = this._desktopUITmp;
        tmp[0] = this._UIRigOrigPos[0];
        tmp[1] = this._UIRigOrigPos[1] + 0.18;   // raise up
        tmp[2] = this._UIRigOrigPos[2] + 0.42;   // bring closer
        this._UIRig.setPositionLocal(tmp);
        this._desktopUIApplied = true;
    }

    /**
     * Restore left eye position to original (VR) values.
     */
    _restoreDesktopEyeOffset() {
        if (!this._UIRig || !this._desktopUIApplied) return;
        this._UIRig.setPositionLocal(this._UIRigOrigPos);
        this._desktopUIApplied = false;
    }

    /**
     * Check for progression unlocks when player levels up
     * @param {number} oldLevel - Previous level
     * @param {number} newLevel - New level
     */
    checkProgressionUnlocks(oldLevel, newLevel) {
        // Collect all unlocks in order first
        const allUnlocks = [];
        // Check each level between old and new for unlocks
        for (let level = oldLevel + 1; level <= newLevel; level++) {
            const unlocks = ProgressionSystem.getUnlocksAtLevel(level, this.prestige);
            if (unlocks.length > 0) {
                for (const unlock of unlocks) {
                    // Only show unlocks that are not already granted by prestige
                    if (!ProgressionSystem.getPrestigeRewards(this.prestige).includes(unlock)) {
                        const description = ProgressionSystem.getUnlockDescription(unlock);
                        allUnlocks.push({ level, description });
                    }
                }
            }
        }
        // If just prestiged, show prestige rewards as unlocks
        if (this.justPrestiged) {
            const rewards = ProgressionSystem.getPrestigeRewards(this.prestige);
            for (const key of rewards) {
                allUnlocks.push({ level: newLevel, description: ProgressionSystem.getUnlockDescription(key) });
            }
            this.justPrestiged = false;
        }
        if (allUnlocks.length > 0) {
            this.showUnlockSequence(allUnlocks);
            if (this.player) {
                const movement = this.player.getComponent('fps_movement');
                if (movement && movement.updateProgressionUnlocks) {
                    movement.updateProgressionUnlocks(newLevel, this.prestige);
                }
            }
        }
    }

    /**
     * Show sequence of unlock notifications with proper ordering and delays
     * @param {Array} unlocks - Array of {level, description} objects
     */
    showUnlockSequence(unlocks) {
        if (!unlocks || unlocks.length === 0) return;

        // Unlocks are already in chronological order from debug function, don't sort
        // Queue all unlocks through the medal system for consistent display
        for (const unlock of unlocks) {
            this.showUnlockNotification(unlock.level, unlock.description);
        }
    }

    /**
     * Show unlock notification to player
     * @param {number} level - Level at which unlock occurred
     * @param {string} description - Description of what was unlocked
     */
    showUnlockNotification(level, description) {
        // Queue unlock notification through medal system for consistent display
        this.displayUnlockNotification(level, description);
        // Play unlock sound
        if (this.unlockSound) {
            try {
                this.unlockSound.play();
            } catch (error) {
                // console.error(`[Unlock Sound] Error playing unlock sound:`, error);
            }
        } else if (this.levelUpSound) {
            try {
                this.levelUpSound.play();
            } catch (error) {
                // console.error(`[Unlock Sound] Error playing level up sound:`, error);
            }
        }
    }

    /**
     * Queue unlock notification through medal system for consistent display
     * @param {number} level - Level at which unlock occurred
     * @param {string} description - Description of what was unlocked
     */
    displayUnlockNotification(level, description) {
        // Create simple unlock message
        const unlockMedalText = `New Unlock: ${description}`;
        // Treat unlocks like regular medals by queuing the display text directly
        const medalName = unlockMedalText;
        // Timestamp for recent medal tracking (dt-based elapsed ms)
        const now = this._elapsedMs;

        // Add unlock to medal queue if not already there
        if (!this._medalQueueSet.has(medalName)) {
            this._medalQueueSet.add(medalName);
            // In debug mode, append unlocks to maintain chronological order
            // In normal gameplay, unlocks get high priority - insert at front after any currently processing medal
            if (this.debug) {
                this._medalQueue.push(medalName);
            } else {
                const insertIndex = this._processingMedal ? 1 : 0;
                this._medalQueue.splice(insertIndex, 0, medalName);
            }
            this._recentMedals.set(medalName, now);
        }
    }

    get level() {
        return this._level;
    }

    /** Update levelText with current level + prestige display */
    _updateLevelText() {
        if (!this.levelText || typeof this._level !== 'number') return;
        const prestigeName = ProgressionSystem.getPrestigeName(this.prestige);
        if (this.prestige > 0) {
            this.levelText.text = `${prestigeName} ${this._level}`;
        } else {
            this.levelText.text = `${this._level}`;
        }

        // Live update frontend HUD overlay if available
        if (typeof window.updateHUDLevelDisplay === 'function') {
            window.updateHUDLevelDisplay(this._level, this.prestige, this.totalXp);
        }
    }

    /**
     * Called after save data is fully loaded and applied.
     * Sets the definitive level display and forces network re-broadcast.
     */
    _applyLoadedLevelDisplay() {
        // Definitive level text update
        this._updateLevelText();

        // Force level re-broadcast on next position update
        this._lastSentLevel = -1;
        this._lastSentPrestige = -1;

        // Also ensure playerLevels Map is populated (userId may not be set yet at boot)
        if (this.userId && typeof this._level === 'number') {
            this.playerLevels.set(this.userId, this._level);
        }
    }

    onDesktopSessionEnd = () => {
        this.saveProgress(true);
        this._restoreDesktopEyeOffset();
        this._resetSession();
    }

    onXRSessionStart = () => {
        // Re-entering XR after a previous session — ensure clean slate.
        // _resetSession() already ran during onXRSessionEnd, so we only handle
        // fresh-start concerns that aren't part of teardown
        
        // [ANALYTICS HOOK]
        let profiles = [];
        if (this.engine.xr?.session?.inputSources?.length > 0) {
            profiles = this.engine.xr.session.inputSources[0].profiles || [];
        }
        AnalyticsManager.instance.logEnterVR(profiles);

        // Ensure player is alive and at full health
        this._isDead = false;
        this._isImmortal = false;
        this.health = 100;
        if (this.healthBar) this.healthBar.setScalingLocal([0.5, 0.01, 0.5]);

        // Clear fade / damage flash so the view isn't stuck black
        this._fadeState = 'none';
        this._fadeAlpha = 0;
        this._fadeCallback = null;
        this._fadeCallbackToken = null;
        this._fadeActiveToken = null;
        this._dmgFlashActive = false;
        this._dmgFlashElapsed = 0;
        if (this.fadingMat) {
            this._fadeColorArr[0] = 0;
            this._fadeColorArr[1] = 0;
            this._fadeColorArr[2] = 0;
            this._fadeColorArr[3] = 0;
            this.fadingMat.diffuseColor = this._fadeColorArr;
        } else {
            // console.warn('[arena-handler] onXRSessionStart — fadingMat is NULL, could not clear fade overlay');
        }

        // Re-cache XR gamepad references (input sources change between sessions)
        this.rightInput = this.rightController?.getComponent(InputComponent) ?? null;
        this.leftInput = this.leftController?.getComponent(InputComponent) ?? null;
        this.rightGamepad = this.rightInput?.xrInputSource?.gamepad ?? null;
        this.leftGamepad = this.leftInput?.xrInputSource?.gamepad ?? null;

        // Reset AFK timer so the player isn't kicked immediately
        this._afkTimer = 0;

        // Allow setup() to run again for the new session
        this._setupFlag = false;
        if (this.playerMovement) {
            this.playerMovement.teleportToLobby();
        }
    }

    onXRSessionEnd = () => {
        this.saveProgress(true);
        this.playerMovement.teleportToLobby();
        this._resetSession();
    }

    _resetSession() {
        // Immediately clear match/game flags so that the visibilitychange handler
        // (anti-AFK random respawn) cannot fire during session teardown.
        this._matchActive = false;
        this._gameStarted = false;
        // Save progress before session ends
        this.saveProgress(true); // Force save on session end

        // Notify other players that this player is exiting XR
        if (this.lobby && this.lobby.data && this.lobby.data.sendToAll && this.userId) {
            this.lobby.data.sendToAll({
                type: "playerExitXR",
                payload: {
                    playerId: this.userId,
                    reason: "XR Session Ended"
                }
            });
        }

        // Destroy all proximity chat peers, AudioContext, and mic stream
        // before cleaning up remote tracking (prevents WebRTC/audio leaks)
        if (this.proximityChat) {
            this.proximityChat.destroy();
            this.proximityChat = new ProximityChat();
            window._proxChatInstance = this.proximityChat;
        }

        // Clean up own player from remote tracking if needed
        if (this.userId) {
            this.handlePlayerDisconnect(this.userId);
        }

        // Properly abandon the lobby so the server cleans up the connection.
        // This prevents ghost players and ensures getLobby() won't return the
        // stale instance on re-entry (which would stack onReceiveAll callbacks).
        if (this.lobby && this.lobby.actions && this.lobby.actions.abandon) {
            try {
                this.lobby.actions.abandon();
            } catch (e) {
                // console.warn('[arena-handler] Error abandoning lobby on session end:', e);
            }
        }

        // Fully clean up all remote player objects before clearing map
        if (this.remotePlayerMap && this.remotePlayerMap.size > 0) {
            for (const [, remote] of this.remotePlayerMap) {
                if (remote) {
                    const remoteSound = remote.getComponent(HowlerAudioSource);
                    if (remoteSound) remoteSound.stop();
                    // Hide avatar
                    remote.setPositionWorld([0, -1000, 0]);
                    // Hide weapons
                    const weaponSync = remote.getComponent('weapon-sync');
                    if (weaponSync) {
                        const hide = [0, -1000, 0];
                        const identQ = [0, 0, 0, 1];
                        weaponSync.setTransform(hide, identQ, hide, identQ);
                        if (typeof weaponSync.hideWeapons === 'function') weaponSync.hideWeapons();
                    }
                }
            }
        }

        // Reset all relevant state variables to initial values
        this.isConnected = false;
        this._isImmortal = false;
        this._dataHandlerRegistered = false;
        this.lobby = null;
        this.lobbyId = null;
        this.userId = null;
        this.remotePlayerMap.clear();
        this.remotePlayerIndex = 0;
        this._freeRemoteSlots.length = 0;
        this._updateTimer = 0;
        this.bulletIndex = 0;
        this.kills = 0;
        this.deaths = 0;
        // Don't reset level progression on session end - preserve progress
        // this.xp = 0;
        // this.level = 1;
        // this.totalXp = 0;
        this._isDead = false;
        this.justRespawned = false;
        this.inSummary = false;
        this.isChaosMode = false;
        this._setGameModeIndicator('');
        this._setTeamNameLabel('', null);
        this._botModeActive = false;
        // Cancel any pending mode transition timer
        if (this._modeTransitionTimer != null) {
            clearTimeout(this._modeTransitionTimer);
            this._modeTransitionTimer = null;
        }
        // Cancel any pending mode notification timeout
        if (this._modeNotifTimeout) {
            clearTimeout(this._modeNotifTimeout);
            this._modeNotifTimeout = null;
        }
        if (this.deactivateBots) {
            this.deactivateBots();
        }

        this.playerStats.clear();
        this._teamMap.clear();
        this._playerColorMap.clear();
        this.gameStartTime = 0; // dt-based elapsed ms, reset per session
        this._hitMarkerActive = false; // Reset hit marker state

        // Reset medal tracking
        if (this.resetMedalTracking) {
            this.resetMedalTracking();
        }
        // Clear medal queue fully
        this._medalQueue.length = 0;
        this._medalQueueSet.clear();
        // Reset medal processing state
        this._processingMedal = false;
        this._currentMedalDisplayTime = 0;
        this._medalDelayTimer = 0;

        // Reset multi-kill tracking
        this._multiKillResetTimer = 0;
        if (this._awardedMultiKillMedals) this._awardedMultiKillMedals.clear();

        // Reset bot speedrun timer
        this._botSpeedrunStart = null;
        this._botSpeedrunEnd = null;

        // Clear stale remote player key cache
        this._remotePlayerKeys.length = 0;
        this._remotePlayerMapSize = 0;

        // Clear activity tracking interval
        if (this._activityInterval) {
            clearInterval(this._activityInterval);
            this._activityInterval = null;
        }
        this._playerActivity.clear();

        // Clear debug kill interval
        if (this._debugKillInterval) {
            clearInterval(this._debugKillInterval);
            this._debugKillInterval = null;
        }

        // Clear debug bot match interval
        if (this._debugBotMatchInterval) {
            clearInterval(this._debugBotMatchInterval);
            this._debugBotMatchInterval = null;
        }

        // Reset player ready map
        this._playerReadyMap.clear();
        this.playerLevels.clear();
        this.playerPrestiges.clear();

        // Force level/prestige re-broadcast on next session
        this._lastSentLevel = -1;
        this._lastSentPrestige = -1;

        // Reset summary/game-end state
        this._summaryDelayAttempted = false;
        this._gameEnded = false;
        this._pendingWinnerId = null;
        this._pendingWinnerKills = 0;

        // Reset health
        this.health = 100;
        this._healTimer = 0;         // Seconds since last damage (auto-heal after 3s)
        this._healDelay = 3.0;       // Seconds before auto-heal kicks in
        this._healRate = 10;         // HP per second while healing

        // Allow setup() to be called again on next session
        this._setupFlag = false;
        // Ensure unlock notifications aren't blocked by a stale loading flag
        this._isLoadingProgress = false;

        // Reset UI elements if available
        if (this.killsText) this.killsText.text = '';
        if (this.progressionText) this.progressionText.text = '';
        this._updateLevelText();
        if (this.xpDetailText) this.xpDetailText.text = '';
        if (this.medalInfoText) this.medalInfoText.text = '';
        if (this.medalDetailsText) this.medalDetailsText.text = '';
        if (this.healthBar) this.healthBar.setScalingLocal([0.5, 0.01, 0.5]);
        if (this.victoryText) {
            this.victoryText.text = '';
            this.victoryText.active = false;
        }

        // Restore original UI positions (undo desktop mode adjustments)
        this._restoreDesktopEyeOffset();

        // Reset prestige symbols to default
        this.updatePrestigeSymbols();

        // Reset fade system fully so screen isn't stuck black between sessions
        this._fadeCallback = null;
        this._fadeCallbackToken = null;
        this._fadeActiveToken = null;
        this._fadeState = 'none';
        this._fadeAlpha = 0.0;
        this._fadeTargetAlpha = 0.0;
        this._fadeElapsed = 0;
        // Clear the fade overlay material to fully transparent so lobby is visible
        if (this.fadingMat) {
            this._fadeColorArr[0] = 0;
            this._fadeColorArr[1] = 0;
            this._fadeColorArr[2] = 0;
            this._fadeColorArr[3] = 0;
            this.fadingMat.diffuseColor = this._fadeColorArr;
        }

        // Reset damage flash so it doesn't persist into next session
        this._dmgFlashActive = false;
        this._dmgFlashElapsed = 0;

        // Reset AFK timer so player isn't kicked early in new session
        this._afkTimer = 0;

        // Reset hit marker timer
        this._hitMarkerActiveTime = 0;

        // Clear stale player list from previous lobby
        this._latestPlayerList = null;
        // Reset spawnPos to lobby so stale arena positions are never reused

        // Clean up pending Arena.ready continuation (if save load never completed)
        if (this._loadSaveAbort) {
            this._loadSaveAbort = true;
        }

        // Increment session generation so stale _guardedTimeout callbacks are discarded
        this._sessionGen++;
        // Stop any running countdown
        this._stopCountdown();
        this._countdownStartedAt = null;
        this.playerMovement.teleportToLobby();
        this.lobbyH._resetSession();
        // Notify the 2D UI to restore the initial menu / splash state.
        window.dispatchEvent(new CustomEvent('arena:back-to-menu'));
    }

    /**
     * Schedule a callback that is automatically discarded if the XR session ends
     * before it fires. Drop-in replacement for setTimeout in gameplay code.
     * @param {Function} fn - Callback to execute
     * @param {number} ms - Delay in milliseconds
     * @returns {number} The underlying timeout ID (can still be cleared manually)
     */
    _guardedTimeout(fn, ms) {
        const gen = this._sessionGen;
        return setTimeout(() => {
            if (this._sessionGen !== gen) return;
            fn();
        }, ms);
    }

    // _ensureMedalSoundsCached() removed — all 16 medal sounds now cached eagerly in start()

    /**
     * Event-driven save loading: waits on `Arena.ready` (REST auth) before
     * touching the save API. Aborts gracefully if the session is reset.
     */
    _loadSavedProgressWhenReady() {
        const doLoad = async () => {
            console.log('[Arena Handler] doLoad: starting saved progress sequence');
            try {
                await this._checkUserLogin();
                await this.debugSaveState();
                await this.loadSavedProgress();
                console.log('[Arena Handler] doLoad: finished saved progress sequence');
            } catch (err) {
                if (this.levelText) {
                    this.levelText.text = `Load failed: ${err.message || 'Unknown'}`;
                    this._guardedTimeout(() => {
                        if (this.levelText) this.levelText.text = `${this.level}`;
                    }, 3000);
                }
            }
        };

        this._loadSaveAbort = false;
        Arena.ready.then(() => {
            if (this._loadSaveAbort) return;
            return doLoad();
        });
    }

    async setup() {
        if (this._setupFlag) return;

        // 1. Initial State Setup
        this._setupFlag = true;
        this._isImmortal = true;
        this._gameStarted = true;
        this.lobby = null;
        this.lobbyId = null;
        this.userId = null;
        this.remotePlayerMap.clear();
        this.remotePlayerIndex = 0;
        this._freeRemoteSlots.length = 0;
        this._isDead = false;
        this.justRespawned = false;
        this.playerStats.clear();
        this.gameStartTime = 0;
        this.inSummary = false;
        this.isConnected = false;

        const MAX_RETRIES = 5;
        const RETRY_BASE_DELAY = 300;

        // Internal helper to finalize the connection state
        const _finalizeLobby = async (lobby) => {
            this.lobby = lobby;
            this.lobbyId = lobby.session.lobbyId;
            this.userId = lobby.session.userId;
            this.session = lobby.session;

            // Register data handler first to catch early messages
            this._registerDataHandler();

            // Execute heavy initialization
            this.setupLobby();
            this.listPlayers();

            // Await hardware access to prevent frame-drop during gameplay
            if (this.proximityChat) {
                await this.proximityChat.initMic();
            }

            // WebRTC peer creation is CPU intensive; doing it now prevents lag later
            this._initProxChatPeers();
            this.isConnected = true;
        };

        const _isAlreadyConnectedError = (err) => {
            const msg = err && (err.message || String(err));
            return msg && msg.includes('you are connected to the lobby');
        };

        const _clearStaleLobby = async () => {
            if (this.lobby?.actions?.reconnect) {
                try {
                    const reconnected = await this.lobby.actions.reconnect();
                    if (reconnected?.actions?.abandon) {
                        reconnected.actions.abandon();
                        this.lobby = null;
                        await new Promise(r => setTimeout(r, 1000));
                        return true;
                    }
                } catch (e) { }
            }
            try {
                const stale = await Arena.lobby.getLobby();
                if (stale?.actions?.abandon) {
                    stale.actions.abandon();
                    this.lobby = null;
                    await new Promise(r => setTimeout(r, 1000));
                    return true;
                }
            } catch (e) { }
            this.lobby = null;
            await new Promise(r => setTimeout(r, 2000));
            return false;
        };

        const _attemptMatchmaking = async (attempt = 0) => {
            try {
                if (attempt === 0 && this.lobby) await _clearStaleLobby();

                // Step 1: Reconnect Check
                try {
                    const existing = await Arena.lobby.getLobby(this.room);
                    if (existing) return existing;
                } catch (e) { }

                // Step 2: Query Lobbies
                const lobbies = await Arena.lobby.lobbies(this.room);
                const candidates = lobbies
                    .filter(l => l.players.length < this.maxPlayers)
                    .sort((a, b) => b.players.length - a.players.length);

                // Step 3: Join or Create
                if (candidates.length > 0) {
                    try {
                        return await Arena.lobby.joinLobby({
                            room: this.room,
                            lobby: candidates[0].id,
                            team: this.team,
                        });
                    } catch (joinErr) {
                        console.warn('[arena-handler] joinLobby failed:', joinErr);
                        if (_isAlreadyConnectedError(joinErr)) await _clearStaleLobby();
                    }
                } else {
                    try {
                        return await Arena.lobby.createLobby({
                            room: this.room,
                            team: this.team,
                        });
                    } catch (createErr) {
                        console.warn('[arena-handler] createLobby failed:', createErr);
                        if (_isAlreadyConnectedError(createErr)) await _clearStaleLobby();
                    }
                }

                // Step 4: Retry Logic
                if (attempt < MAX_RETRIES - 1) {
                    const delay = RETRY_BASE_DELAY * (attempt + 1) + (Math.random() * RETRY_BASE_DELAY);
                    await new Promise(r => setTimeout(r, delay));
                    return _attemptMatchmaking(attempt + 1);
                }
            } catch (err) {
                if (attempt < MAX_RETRIES - 1) {
                    return _attemptMatchmaking(attempt + 1);
                }
            }
            return null;
        };

        // --- EXECUTION FLOW ---

        // 1. Wait for network connection and heavy asset/peer initialization
        const successfulLobby = await _attemptMatchmaking();

        if (successfulLobby) {
            await _finalizeLobby(successfulLobby);
        } else {
            console.error('[arena-handler] Failed to connect after retries.');
            // Optional: Show UI error message to user here
        }

        // 2. Local Visual/Physics Setup (Only runs after heavy lifting is done)
        if (this.victoryTextObject) this.victoryTextObject.setScalingLocal([0.75, 0.75, 0.75]);

        this.getRandomSpawn();
        if (this.playerMovement) {
            this.playerMovement.respawn(this.spawnPos, this.spawnRot);
        }

        this.justRespawned = true;
        this.health = 100;
        if (this.healthBar) this.healthBar.setScalingLocal([0.5, 0.01, 0.5]);
        this._isDead = false;
        this._afkTimer = 0;

        // 3. Finalize game state
        if (this.lobby) {
            this.listPlayers();
        }

        if (this.debugBotMatch) {
            this._startDebugBotMatch();
        }

        this.setDisplayDuration(this._medalDisplayDuration);
    }

    async _updateXpBoostStatus(verified = false) {
        if (!this._isUserLoggedIn) return;
        if (!verified) {
            try {
                const inventory = await Arena.inventory.get();
                const boost = inventory.find(item => item.slug === 'xp_boost');
                if (boost && boost.expires_on) {
                    const cleanDateString = boost.expires_on.replace(/(\.\d{3})\d+/, '$1');
                    if (new Date(boost.expires_on) > new Date()) {
                        this._xpMultiplier = 2;
                        console.log('[Arena Handler] _updateXpBoostStatus: active boost expires_on=', cleanDateString);
                        return;
                    }
                }
                this._xpMultiplier = 1;
                console.log('[Arena Handler] _updateXpBoostStatus: no active boost, multiplier=1');
            } catch (err) {
                this._xpMultiplier = 1;
                console.warn('[Arena Handler] _updateXpBoostStatus: inventory check failed:', err && err.message ? err.message : err);
            }
        } else {
            // if already verified, just set the multiplier based on current state (used after buy)
            this._xpMultiplier = 2;
            console.log('[Arena Handler] _updateXpBoostStatus: verified=true, multiplier=2');
        }
    }
    getRandomSpawn() {
        // 1. Initialize or refill the bag if it is empty
        if (!this.spawnBag || this.spawnBag.length === 0) {
            // We use the spread operator [...] to create a clone of the array.
            // This ensures we don't accidentally destroy your original this.spawns array!
            this.spawnBag = [...this.spawns];
        }

        // 2. Pick a random index from the remaining items in the bag
        const randomIndex = Math.floor(Math.random() * this.spawnBag.length);

        // 3. Grab the selected spawn point
        const spawn = this.spawnBag[randomIndex];

        // 4. Remove it from the bag efficiently (Swap and Pop)
        // We overwrite the chosen item with the last item in the bag...
        this.spawnBag[randomIndex] = this.spawnBag[this.spawnBag.length - 1];
        // ...and then delete the last item.
        this.spawnBag.pop();

        // 5. Run your original spawn logic
        if (spawn) {
            spawn.getPositionWorld(this.spawnPos);
            spawn.getRotationWorld(this.spawnRot);
            this.spawnPos[1] += 2; // Adjust Y position slightly to avoid ground clipping
        }
    }
    fireBullet(origin, linearVelocity, t, weaponType, shooterId, hand) {
        if (this.inSummary || this._gameEnded) return;
        if (this._isDead) return; // Block shooting while dead
        if (!this.lobby || !this.lobby.data) {
            // console.warn('[ArenaHandler] fireBullet called before lobby is ready');
            return;
        }
        this.lobby.data.sendToAll({
            type: "bulletSpawn",
            payload: {
                id: shooterId || this.userId,
                pos: origin,
                linVel: linearVelocity,
                t: t,
                weaponType: weaponType, // NEW
                hand: hand // NEW
            }
        });
    }
    spawnBullet() {
        if (this.bulletIndex >= this.networkedBullets.length) {
            this.bulletIndex = 0;
        }
        const bullet = this.networkedBullets[this.bulletIndex++];

        return bullet;
    }
    handlePlayerHit(payload) {
        if (this.inSummary || this._gameEnded) return;
        const { id, shooterId, hitPos } = payload;
        // console.log(`[arena-handler] ${id} were hit by ${shooterId}`);

        if (shooterId === this.userId) {
            // [ANALYTICS HOOK]
            AnalyticsManager.instance.logHit(TargetType.REAL_PLAYER);

            this.hitSound.play();
            // Show hitmarker for hitting real players
            if (this.hitMarker && hitPos) {
                this.hitMarker.setPositionWorld(hitPos);
                this.playerRig.getPositionWorld(this.tempPos);
                this.hitMarker.lookAt(this.tempPos, this._upVec);
                this._hitMarkerActive = true;
            }

            // Trigger haptic feedback for successful hit
            this.triggerHapticFeedback(0.6, 100, 'both');

            // console.log(`[arena-handler] You hit ${id}`);
        }
        // If the hit player is us, we should handle the hit
        if (id === this.userId) {
            this.getHitSound.play();

            // Trigger haptic feedback for getting hit
            this.triggerHapticFeedback(0.7, 150, 'both');
            // console.log(`[arena-handler] You were hit by ${shooterId}`);
        }
    }
    handlePlayerKill(payload) {
        if (this.inSummary || this._gameEnded) return;
        const { victimId, killerId } = payload;

        // Track statistics for all players
        if (!this.playerStats.has(killerId)) {
            this.playerStats.set(killerId, { kills: 0, deaths: 0 });
        }
        if (!this.playerStats.has(victimId)) {
            this.playerStats.set(victimId, { kills: 0, deaths: 0 });
        }

        const killerStats = this.playerStats.get(killerId);
        const victimStats = this.playerStats.get(victimId);

        killerStats.kills++;
        victimStats.deaths++;

        if (killerId === this.userId) {
            // [ANALYTICS HOOK]
            AnalyticsManager.instance.logKill(TargetType.REAL_PLAYER);

            this.killSound.play();
            this.kills++;
            this.killsText.text = this.kills.toString() + '/' + this._killsToWin + ' Kills';
            this.updateKillProgressBar();

            // Trigger haptic feedback for kill
            this.triggerHapticFeedback(0.8, 200, 'both');

            // Award War Coins for kill
            this.addWarCoins(5, 'kill');

            // Award XP for kill (amount set by current game mode)
            this.applyXP(this._xpPerKill);

            // Medal system checks - get weapon type from payload or last used weapon
            const weaponType = payload.weaponType || this.playerMovement?.getCurrentWeaponId?.() || 'default';
            const isHeadshot = payload.isHeadshot || false;
            const distance = payload.distance || null;

            // Check if Last Stand (player health < 20%)
            if (this.health < 20) {
                this.displayMedal('LAST STAND!');
            }

            // Check for various medals (both player and bot kills count for multi-kill medals)
            this.checkMultiKillMedals();
            this.checkWeaponMedals(weaponType);
            this.checkKillStreakMedals();
            this.checkSituationalMedals(weaponType, distance);

            this._checkWinCondition();
            // Remove weapon unlock by kill logic
            //if (this.playerMovement) {
            //    this.playerMovement.onPlayerKill();
            //}
        }
    }
    applyXP(amount, isMedal = false) {
        const boostedAmount = Math.floor(amount * (this._xpMultiplier));

        // 2. Add the BOOSTED amount to total XP
        this.totalXp += boostedAmount;

        // Calculate effective level from total XP (continuous across prestiges)
        const effectiveLevel = ArenaHandler.getEffectiveLevelFromXp(this.totalXp);

        // Convert effective level to prestige + display level
        const prestigeResult = ProgressionSystem.calculatePrestigeAndLevel(effectiveLevel);
        const oldLevel = this.level;
        const oldPrestige = this.prestige;

        this._level = prestigeResult.level;
        const oldPrestigeValue = this.prestige;
        this.prestige = prestigeResult.prestige;

        // Update prestige symbols immediately if prestige changed
        if (this.prestige !== oldPrestigeValue) {
            this.updatePrestigeSymbols();
            if (this.playerMovement) {
                this.playerMovement._initLaserColors();
            }
        }

        // Calculate XP progress within current prestige tier
        const currentEffectiveLevel = effectiveLevel;
        const nextEffectiveLevel = effectiveLevel + 1;
        const totalXpForCurrentLevel = ArenaHandler.getTotalXpForLevel(currentEffectiveLevel);
        const totalXpForNextLevel = ArenaHandler.getTotalXpForLevel(nextEffectiveLevel);
        this.xp = this.totalXp - totalXpForCurrentLevel;
        const xpNeededForNext = totalXpForNextLevel - totalXpForCurrentLevel;

        // Update progression bar
        if (!this._botModeActive && this.progressionBar) {
            const progressionScale = Math.min(this.xp / xpNeededForNext, 1) * 0.5;
            this.progressionBar.setScalingLocal([progressionScale, this._progressScale[1], 0.5]);
        }

        // 3. Update XP detail text to show the BOOSTED amount
        if (this.xpDetailText && !this.inSummary) {
            const boostText = this._xpMultiplier > 1 ? ` (x${this._xpMultiplier})` : '';
            // Result: +200 (x2)
            this.xpDetailText.text = `+${boostedAmount}xp${boostText}`;
        }

        // Update level text
        this._updateLevelText();

        // Check for level up or prestige
        const leveledUp = this._level > oldLevel || this.prestige > oldPrestige;
        if (leveledUp) {
            if (this.prestige > oldPrestige) {
                this.handlePrestige(this.prestige);
            }
            this.handleLevelUp(oldLevel, this._level);
        }

        // Clear XP detail text after delay
        this._guardedTimeout(() => {
            if (this.xpDetailText && !this.inSummary) {
                this.xpDetailText.text = '';
            }
        }, this._xpDisplayDuration * 1000);
    }

    handleLevelUp(oldLevel, newLevel) {
        // Don't show level up messages during game summary
        if (this.inSummary) return;

        // Show level up message in progression text
        this._guardedTimeout(() => {
            if (this.progressionText && !this.inSummary) {
                this.progressionText.text = `LEVEL UP! ${newLevel}`;
            }
        }, (this._xpDisplayDuration + 0.1) * 1000); // Show after XP detail text clears

        // Clear level up message
        this._guardedTimeout(() => {
            if (this.progressionText && !this.inSummary) {
                this.progressionText.text = '';
            }
        }, (this._xpDisplayDuration * 2 + 0.1) * 1000); // Clear after showing for same duration as XP display

        // Play level up sound effect
        if (this.levelUpSound) {
            this.levelUpSound.play();
        }

        // Trigger haptic feedback for level up
        this.triggerHapticFeedback(1.0, 500, 'both');

        // Award War Coins for leveling up (not for prestige level resets)
        if (!this._isLoadingProgress) {
            this.addWarCoins(25, 'level-up');
        }

        // Check for progression unlocks when leveling up (but not during loading)
        if (!this._isLoadingProgress && typeof oldLevel === 'number' && typeof newLevel === 'number' && newLevel > oldLevel) {
            this.checkProgressionUnlocks(oldLevel, newLevel);
        }
    }

    /**
     * Add War Coins to the player's balance and notify the UI
     * @param {number} amount - Coins to add
     * @param {string} reason - Reason for the award (for UI toast display)
     */
    addWarCoins(amount, reason) {
        if (!amount || amount <= 0) return;
        this.warCoins = (this.warCoins || 0) + amount;
        if (typeof window.updateWarCoinsDisplay === 'function') {
            window.updateWarCoinsDisplay(this.warCoins);
        }
        if (typeof window.showCoinEarnToast === 'function') {
            window.showCoinEarnToast(amount, reason || '');
        }
    }

    /**
     * Purchase an item from the War Coins shop (called by UI layer)
     * @param {string} itemKey - Key from ProgressionSystem.COIN_PRICES
     * @returns {{success: boolean, message: string, newBalance: number}}
     */
    purchaseItem(itemKey, isPermanent = true) {
        // Find price based on new economy schema
        let price = 0;
        let isWeapon = false;
        
        if (ProgressionSystem.COIN_PRICES.weapons[itemKey]) {
            isWeapon = true;
            price = isPermanent ? ProgressionSystem.COIN_PRICES.weapons[itemKey].permanent : ProgressionSystem.COIN_PRICES.weapons[itemKey].rental;
        } else if (ProgressionSystem.COIN_PRICES.attachments[itemKey]) {
            price = ProgressionSystem.COIN_PRICES.attachments[itemKey];
        }

        if (!price) return { success: false, message: 'Item not for sale', newBalance: this.warCoins || 0 };
        
        // Build mock economy data for check
        const mockEconomy = { prestigeTier: this.prestige, licenses: this.licenses };
        if (ProgressionSystem.isUnlocked(itemKey, isWeapon ? 'weapon' : 'attachment', mockEconomy)) {
            // Already owned check (for weapons, it might be a rental upgrade to permanent)
            if (!isWeapon || (this.licenses.weapons[itemKey] && this.licenses.weapons[itemKey].isPermanent)) {
                return { success: false, message: 'Already owned', newBalance: this.warCoins || 0 };
            }
        }

        if ((this.warCoins || 0) < price) return { success: false, message: 'Not enough War Coins', newBalance: this.warCoins || 0 };
        
        this.warCoins -= price;
        
        if (isWeapon) {
            this.licenses.weapons[itemKey] = {
                isPermanent: isPermanent,
                matchesRemaining: isPermanent ? 0 : 5
            };
        } else {
            if (!this.licenses.attachments.includes(itemKey)) {
                this.licenses.attachments.push(itemKey);
            }
        }
        if (typeof window.updateWarCoinsDisplay === 'function') window.updateWarCoinsDisplay(this.warCoins);
        try { this.saveProgress(true); } catch (e) {}
        return { success: true, message: 'Purchase successful!', newBalance: this.warCoins };
    }

    /**
     * Migrate old level-unlocked crosshairs/lasers to purchasedItems (backwards compat)
     * Items the player already earned via leveling are auto-granted in the new coin system.
     * @param {Object} data - Raw save data
     */
    _migrateLegacyUnlocksToPurchased(data) {
        if (!this.licenses) this.licenses = { weapons: {}, attachments: [] };
        const pi = this.licenses.attachments;
        const addIfMissing = (key) => { if (pi.indexOf(key) === -1) pi.push(key); };
        
        if (Array.isArray(data.unlockedCrosshairs)) {
            data.unlockedCrosshairs.forEach(function(wt) {
                const key = (wt === 'default' ? 'akimbo' : wt) + '_crosshair';
                addIfMissing(key);
            });
        }
        if (Array.isArray(data.unlockedLasers)) {
            data.unlockedLasers.forEach(function(wt) {
                const key = (wt === 'default' ? 'akimbo' : wt) + '_laser';
                addIfMissing(key);
            });
        }
        
        // Legacy permanent weapons
        const migrateWeapon = (wt) => {
            if (!this.licenses.weapons[wt]) {
                this.licenses.weapons[wt] = { isPermanent: true, matchesRemaining: 0 };
            }
        };
        
        if (this.prestige >= 1) { addIfMissing('laser_blue'); migrateWeapon('rifle'); }
        if (this.prestige >= 2) { addIfMissing('laser_purple'); migrateWeapon('shotgun'); }
        if (this.prestige >= 3) { addIfMissing('laser_gold'); migrateWeapon('sniper'); }
    }

    handleBulletSpawn(payload) {
        if (this.inSummary) return;
        if (this._isImmortal) return;
        const { id, pos, linVel, t, weaponType, hand } = payload;
        vec3.normalize(this.tempPos, linVel);
        const rayHit = this.engine.physics.rayCast(pos, this.tempPos, 1000, 1000);
        if (rayHit.hitCount > 0) {
            const hitNames = [];
            for (let i = 0; i < rayHit.hitCount; i++) {
                const hitObj = rayHit.objects[i];
                hitNames.push(hitObj ? hitObj.name : 'null');
            }
        }

        // Trigger muzzle flash on the remote player's weapon
        const remotePlayer = this.remotePlayerMap.get(id);
        if (remotePlayer) {
            const weaponSync = remotePlayer.getComponent('weapon-sync');
            if (weaponSync) {
                weaponSync.playMuzzleFlash(weaponType, hand);
            }
        }
        // Play distant gunshot sound for non-local shots (including bots)
        if (id !== this.userId) {
            // try { console.log('[arena-handler] Non-local shot detected, calling _playDistantGunshot', { weaponType, pos, id }); } catch (_) {}
            this._playDistantGunshot(weaponType, pos, id);
        }

        // Get a bullet from the pool or instantiate one
        const bullet = this.spawnBullet();

        // Set initial position and velocity
        bullet.setPositionWorld(pos);
        if (!bullet._kinBulletCache) {
            bullet._kinBulletCache = bullet.getComponent(KinematicBullet);
        }
        const kBullet = bullet._kinBulletCache;
        if (kBullet) {
            kBullet.shooterId = id;   // mirrors the old bullet.getComponent('bullet').shooterId = id line
            kBullet.fire(linVel);     // activates, stores velocity, starts TTL
        }
        for (let i = 0; i < rayHit.hitCount; i++) {
            const hitObj = rayHit.objects[i];
            const hitPos = rayHit.locations[i];
            // Block bullet if it hits Map or Mesh (environment)
            if (hitObj && (hitObj.name === "Map" || hitObj.name === "Mesh")) {
                break;
            }
            // Ignore the shooter itself in hit detection (works for both bots and players)
            if (hitObj && id) {
                // If bot, skip if botId matches
                if (hitObj.getComponent && hitObj.getComponent('bot') && id.startsWith('bot') && hitObj.getComponent('bot').botId === id) continue;
                // If player, skip if userId matches
                if (hitObj.getComponent && hitObj.getComponent('fps_movement') && id === this.userId && hitObj === this.player) continue;
            }
            // --- HEADSHOT/BODYSHOT LOGIC ---
            // Only allow PhysicsPlayerBody for bot shots
            if (hitObj) {
                const isBotShot = id && id.startsWith('bot');
                const isPlayerBody = hitObj.name === "PlayerHead" || hitObj.name === "PlayerBody";
                if (isPlayerBody || (isBotShot && hitObj.name === "PhysicsPlayerBody")) {
                    const isLocalShooter = id === this.userId;
                    // Show hit marker if local player shot and hit another player (not themselves)
                    if (isLocalShooter && isPlayerBody && hitObj !== this.player && !(this.playerRig && this.playerRig.children && this.playerRig.children.includes(hitObj))) {
                        this.hitMarker.setPositionWorld(hitPos);
                        this.hitMarker.lookAt(pos, [0, 1, 0]);
                        this._hitMarkerActive = true;
                    }
                    // If the local player is the victim, apply damage but do NOT show hit marker
                    if (id !== this.userId && !this._isDead) {
                        // Use weapon-specific headshot multipliers instead of hardcoded 1.5
                        const headshotMultipliers = {
                            default: 1.5,
                            rifle: 1.5,
                            shotgun: 1.5,
                            sniper: 1.7, // Sniper headshot should be lethal
                            bot: 1.0
                        };
                        const critMultiplier = hitObj.name === "PlayerHead" ? (headshotMultipliers[weaponType] || 1.5) : 1.0;
                        this.playerHit(id, weaponType, critMultiplier, hitPos);
                    }
                }
            }
            // Add support for damaging bots only if bot mode is active
            if (this._botModeActive && hitObj && hitObj.name === "Bot") {
                const botComponent = hitObj.getComponent && hitObj.getComponent('bot');
                if (botComponent) {
                    // --- Comprehensive Weapon Damage Table for Bots ---
                    const damageTable = {
                        default: 12,    // Akimbo pistols
                        rifle: 18,      // Rifle
                        shotgun: 20,    // Shotgun (per pellet)
                        sniper: 100,    // Sniper (high damage)
                    };
                    // Custom headshot multipliers (not used for bots, but for consistency)
                    const headshotMultipliers = {
                        default: 1.5,
                        rifle: 1.5,
                        shotgun: 1.5,
                        sniper: 1.7
                    };

                    // Calculate damage - for shotguns, simulate multiple pellets hitting at close range
                    let damage = damageTable[weaponType] ?? 12;
                    if (weaponType === 'shotgun') {
                        // Shotgun fires 5 pellets, simulate more pellets hitting at closer range
                        const distance = pos && hitPos ? vec3.distance(pos, hitPos) : 10;
                        const pelletsHit = Math.min(5, Math.max(1, Math.ceil(5 - distance / 10))); // More pellets hit at closer range
                        damage = damage * pelletsHit;
                    }
                    const wasAlive = botComponent.health > 0;
                    botComponent.takeDamage(damage);
                    // Play hit sound if the shooter is the local player
                    if (id === this.userId && this.hitSound) {
                        // [ANALYTICS HOOK]
                        AnalyticsManager.instance.logHit(TargetType.BOT);

                        this.hitSound.play();
                        this.hitMarker.setPositionWorld(hitPos);
                        this.hitMarker.lookAt(pos, [0, 1, 0]);
                        this._hitMarkerActive = true;
                    }
                    // Play kill sound if the shooter is the local player and the bot just died
                    if (id === this.userId && this.killSound && botComponent.health <= 0 && wasAlive) {
                        // [ANALYTICS HOOK]
                        AnalyticsManager.instance.logKill(TargetType.BOT);

                        this.killSound.play();
                        this._hitMarkerActive = true;
                        this.hitMarker.setPositionWorld(hitPos);
                        this.hitMarker.lookAt(pos, this._upVec);
                        if (this.kills < this._killsToWin) {
                            this.kills++;
                            if (this.killsText) this.killsText.text = this.kills.toString() + "/" + this._killsToWin + " Kills";
                            this.updateKillProgressBar && this.updateKillProgressBar();

                            // Award standard XP for bot kill
                            this.addWarCoins(5, 'kill');
                            this.applyXP(100); // Lower XP for bot kills

                            // Medal system checks for bot kills
                            const distance = pos && hitPos ? vec3.distance(pos, hitPos) : null;
                            const isHeadshot = false; // Bots don't have headshot detection

                            // Check if Last Stand (player health < 20%)
                            if (this.health < 20) {
                                this.displayMedal('LAST STAND!');
                            }

                            // Check for various medals (bot kills should also count for multi-kill medals)
                            this.checkMultiKillMedals();
                            this.checkWeaponMedals(weaponType);
                            this.checkKillStreakMedals();
                            this.checkSituationalMedals(weaponType, distance);

                            if (!this.playerStats.has(this.userId)) {
                                this.playerStats.set(this.userId, { kills: 0, deaths: 0 });
                            }
                            const localStats = this.playerStats.get(this.userId);
                            localStats.kills++;
                            if (this._botModeActive && this.kills === this._killsToWin) {
                                this._botSpeedrunEnd = performance.now();
                                if (this.progressionText) this.progressionText.text = '';
                                // Show victory/game summary
                                this.showGameSummary(this.userId, this.kills);

                                // Game reset will be handled after summary is shown
                            }
                        }
                    }
                }
            }
        }
        // Optionally: use timestamp `t` for lag compensation
    }
    _playDistantGunshot(weaponType, pos, shooterId) {
        // Select pool by type
        const isBot = shooterId && typeof shooterId === 'string' && shooterId.startsWith('bot');
        const poolKey = isBot ? 'bot' : (weaponType || 'default');
        let key = 'pistol';
        if (poolKey === 'rifle') key = 'rifle';
        else if (poolKey === 'shotgun') key = 'shotgun';
        else if (poolKey === 'sniper') key = 'sniper';
        else if (poolKey === 'bot') key = 'bot';
        else key = 'pistol';

        const pool = this._emitterPools && this._emitterPools[key] ? this._emitterPools[key] : null;
        if (!pool || !pool.emitters || pool.emitters.length === 0) {
            // console.warn('[arena-handler] No distant gunshot emitters available for key:', key, 'pool exists:', !!pool);
            return;
        }

        // Round-robin pick from the list
        const src = pool.emitters[pool.index % pool.emitters.length];
        pool.index = (pool.index + 1) % Math.max(1, pool.emitters.length);

        if (!src) return;

        try {
            // Position the sound object at the specified world position
            if (src.object && src.object.setPositionWorld && pos) {
                src.object.setPositionWorld(pos);
                // console.log('[arena-handler] Emitter positioned at', pos, 'object:', src.object && src.object.name);
            }

            // Get the HowlerAudioSource component and play the sound
            src.play();
            // console.log('[arena-handler] Distant gunshot play() invoked on HowlerAudioSource');
        } catch (e) {
            // console.error('[arena-handler] Error playing distant gunshot:', e);
        }
    }

    /**
     * Generic method to play positional sound from a list of sound objects
     * @param {Array} soundObjectsList - Array of objects with HowlerAudioSource components
     * @param {Array} position - [x, y, z] world position to play sound at
     * @param {number} poolIndex - Current index for round-robin selection (optional)
     * @returns {number} - Next pool index for round-robin
     */
    playPositionalSound(soundObjectsList, position, poolIndex = 0) {
        if (!soundObjectsList || soundObjectsList.length === 0) {
            // console.warn('[arena-handler] No sound objects available in list');
            return poolIndex;
        }

        // Round-robin selection from the list
        const soundObject = soundObjectsList[poolIndex % soundObjectsList.length];
        const nextIndex = (poolIndex + 1) % soundObjectsList.length;

        if (!soundObject) {
            // console.warn('[arena-handler] Invalid sound object at index', poolIndex);
            return nextIndex;
        }

        try {
            // Position the object at the specified world position
            if (soundObject.setPositionWorld && position) {
                soundObject.setPositionWorld(position);
            }

            // Get the HowlerAudioSource component (cached on first access)
            if (!soundObject._audioSourceCache) {
                soundObject._audioSourceCache = soundObject.getComponent(HowlerAudioSource);
            }
            const audioSource = soundObject._audioSourceCache;
            if (audioSource) {
                // Play the sound
                audioSource.play();
                // console.log('[arena-handler] Positional sound played at', position);
            } else {
                // console.warn('[arena-handler] No HowlerAudioSource component found on object', soundObject.name);
            }
        } catch (e) {
            // console.error('[arena-handler] Error playing positional sound:', e);
        }

        return nextIndex;
    }
    _gatherEmitters(parentObj) {
        const list = [];

        if (parentObj && parentObj.children) {
            for (let i = 0; i < parentObj.children.length; i++) {
                const child = parentObj.children[i];
                if (child.getComponents) {
                    const sources = child.getComponents(HowlerAudioSource, { recursive: false });
                    if (sources && sources.length > 0) {
                        // QUEST 2 OPTIMIZATION: Avoid spread operator allocation
                        for (let j = 0; j < sources.length; j++) {
                            list.push(sources[j]);
                        }
                    }
                }
            }
        }

        // console.log('[arena-handler] _gatherEmitters found', list.length, 'sources under', parentObj && parentObj.name);

        return list;
    }

    /**
     * Register the lobby data handler (rawDataAll) as early as possible to
     * prevent the Colyseus "onMessage() not registered" race condition.
     */
    _registerDataHandler() {
        if (this._dataHandlerRegistered || !this.lobby) return;
        this._dataHandlerRegistered = true;
        this.lobby.data.onReceiveAll((data) => {
            if (!this._setupFlag) return; // Discard messages after session end
            switch (data.type) {
                case "playerUpdate":
                    this.handlePlayerUpdate(data.payload);
                    break;
                case "bulletSpawn":
                    this.handleBulletSpawn(data.payload);
                    break;
                case "playerHit":
                    this.handlePlayerHit(data.payload);
                    break;
                case "playerKill":
                    this.handlePlayerKill(data.payload);
                    break;
                case "playerWin":
                    this.handlePlayerWin(data.payload);
                    break;
                case "realModeInfo":
                    this._handleRealModeInfo(data.payload);
                    break;
                case "playerKicked":
                    this.handlePlayerKicked(data.payload);
                    break;
                case "teamUpdate":
                    this._handleTeamUpdate(data.payload, data.senderId);
                    break;
                case "playerExitXR":
                    this.handlePlayerExitXR(data.payload);
                    break;
                // AFTER
                case "rtcOffer":
                case "rtcAnswer":
                case "rtcIce":
                    if (this.proximityChat) {
                        this.proximityChat.handleSignal(data, this.lobby, this.userId);
                    }
                    break;
                case "activity":
                    this._playerActivity.set(data.senderId, data.timestamp);
                    break;
                default:
                // console.warn("Unknown data type received:", data.type);
            }
        });
    }


    setupLobby() {
        // console.log('[arena-handler] setupLobby() called');
        if (!this.lobby) {
            // console.error('[arena-handler] Lobby not initialized.');
            return;
        }

        // Initialize local player's level in playerLevels Map for synchronization
        if (this.userId && this.level && !this.playerLevels.has(this.userId)) {
            this.playerLevels.set(this.userId, this.level);
        }

        // Initialize stats for all current players
        const setupGen = this._sessionGen;
        this.lobby.actions.listPlayers().then(players => {
            if (this._sessionGen !== setupGen) return;
            // PERFORMANCE: Use for loop instead of forEach/Array operations
            const playerArray = Array.isArray(players) ? players : Object.values(players);
            for (let i = 0; i < playerArray.length; i++) {
                const player = playerArray[i];
                if (!this.playerStats.has(player.sessionId)) {
                    this.playerStats.set(player.sessionId, { kills: 0, deaths: 0 });
                }
            }
        }).catch(err => {
            // console.error('[arena-handler] Error listing players:', err);
        });

        if (!this._dataHandlerRegistered) {
            this._registerDataHandler();
        }
        // --- Add player join/leave event handling ---
        this.lobby.events.onPlayerSync((result) => {
            if (!this._setupFlag || !this.lobby) return; // Discard events after session end

            // Delegate to the canonical `listPlayers()` path so that both the
            // initial join and mid-match join flows perform identical
            // allocation/appearance/name/notification setup.
            try {
                this.listPlayers();
            } catch (e) {
                // Fall back to a lightweight refresh if listPlayers throws
                this.lobby.actions.listPlayers().then(players => {
                    this._latestPlayerList = Array.isArray(players) ? players : Object.values(players);
                }).catch(() => { });
            }

            // Then run the specific per-player connect/disconnect handlers
            if (result.event === 'join') {
                this.handlePlayerJoin(result.sessionId);
            } else if (result.event === 'leave') {
                this.handlePlayerDisconnect(result.sessionId);
            }
        });

        // Handle disconnections from the server
        this.lobby.events.onDisconnect((code) => {
            this._resetSession();
            this.isConnected = false;
        });

        this.lobby.events.onError((code, message) => {
            console.error('[arena-handler] Lobby error:', code, message);
        });

        // Set up activity tracking for ghost player cleanup
        this._setupActivityTracking();
    }

    // Handle player win message
    handlePlayerWin(payload) {
        // De-duplicate: ignore if we already triggered a game-end sequence
        if (this._gameEnded || this.inSummary) return;
        // Show victory/game summary
        this.showGameSummary(payload.id, payload.kills);

        // Game reset will be handled after summary is shown
    }

    /**
     * Handle an authoritative team update broadcast from another client.
     * Payload: { teams: Array<[id, team]> , generatedAt: number }
     */
    _handleTeamUpdate(payload, senderId) {
        if (!payload || !payload.teams) return;
        // NOTE: payload.generatedAt is the sender's `performance.now()` which
        // is NOT comparable across clients (unrelated time origins). The old
        // `ts <= _lastTeamUpdateTs` guard could silently drop legitimate
        // updates from peers whose clock-zero is later than ours, leaving us
        // with a stale/empty team map and breaking friendly-fire / damage.
        // Instead, dedupe by content: skip only if the incoming map matches
        // what we already have.
        try {
            // Build the authoritative map from the payload first.
            const incoming = new Map();
            for (let i = 0; i < payload.teams.length; i++) {
                const entry = payload.teams[i];
                if (!entry) continue;
                if (Array.isArray(entry)) {
                    if (entry[0]) incoming.set(entry[0], entry[1]);
                } else if (entry.id) {
                    incoming.set(entry.id, entry.team);
                }
            }

            // Content-based dedupe: same size and same (id→team) entries.
            if (incoming.size === this._teamMap.size) {
                let identical = true;
                for (const [id, team] of incoming) {
                    if (this._teamMap.get(id) !== team) { identical = false; break; }
                }
                if (identical) return;
            }

            // Apply the new map.
            this._teamMap.clear();
            for (const [id, team] of incoming) this._teamMap.set(id, team);
            this._lastTeamUpdateTs = payload.generatedAt || 0;

            // Reapply appearances to ensure visuals match authoritative map
            if (this._latestPlayerList) {
                if (this.isChaosMode) {
                    this._applyChaosAppearances(this._latestPlayerList);
                } else {
                    this._applyTeamAppearances(this._latestPlayerList, false); // false = don't override synced team map

                    // Ensure already-allocated remote avatars receive the
                    // authoritative appearance. Some remotes may have been
                    // allocated before the teamUpdate arrived and thus
                    // deferred appearance earlier — reapply to those.
                    try {
                        for (const [rid, remote] of this.remotePlayerMap.entries()) {
                            if (!remote) continue;
                            if (this._teamMap.has(rid)) {
                                this._applyAppearanceToPlayer(rid, remote);
                            }
                        }
                    } catch (e) {
                        console.warn('[arena-handler] _handleTeamUpdate: reapply to remotes failed', e);
                    }
                }
            }
        } catch (e) {
            console.warn('[arena-handler] _handleTeamUpdate failed', e);
        }
    }

    handlePlayerJoin(sessionId) {
        // Initialize stats for new player
        if (!this.playerStats.has(sessionId)) {
            this.playerStats.set(sessionId, { kills: 0, deaths: 0 });
        }
        // Ensure local player's level is in the playerLevels Map
        if (this.userId && this.level && !this.playerLevels.has(this.userId)) {
            this.playerLevels.set(this.userId, this.level);
        }
        // Set initial activity timestamp (dt-based elapsed ms)
        this._playerActivity.set(sessionId, this._elapsedMs);

        // Set up WebRTC voice connection (lower userId initiates to avoid duplicate offers)
        const isInitiator = this.userId < sessionId;
        this.proximityChat.createPeer(sessionId, isInitiator, this.lobby, this.userId);
    }

    /** Create proximity chat peers for all players already in the lobby on join */
    _initProxChatPeers() {
        if (!this.lobby) return;
        this.lobby.actions.listPlayers().then(players => {
            const arr = Array.isArray(players) ? players : Object.values(players);
            for (let i = 0; i < arr.length; i++) {
                const id = arr[i].sessionId;
                if (id && id !== this.userId) {
                    const isInitiator = this.userId < id;
                    this.proximityChat.createPeer(id, isInitiator, this.lobby, this.userId);
                }
            }
        }).catch(err => {
            console.warn('[proximity-chat] Failed to list players for peer init:', err);
        });
    }

    _setupActivityTracking() {
        if (!this.lobby) return;

        // Send activity every 5 seconds
        this._activityInterval = setInterval(() => {
            if (this.isConnected && this.lobby) {
                this.lobby.data.sendToAll({ type: 'activity', timestamp: this._elapsedMs });
            }
        }, 5000);

        // Activity messages are handled in the main onReceiveAll switch in setupLobby()

        // Initialize activity for current players
        const activityGen = this._sessionGen;
        this.lobby.actions.listPlayers().then(players => {
            if (this._sessionGen !== activityGen) return;
            const now = this._elapsedMs;
            // PERFORMANCE: Use for...in loop for objects
            const playerArray = Object.values(players);
            for (let i = 0; i < playerArray.length; i++) {
                this._playerActivity.set(playerArray[i].sessionId, now);
            }
        });
    }

    // Show game summary/victory screen
    showGameSummary(winnerId, winnerKills) {

        // [ANALYTICS HOOK]
        AnalyticsManager.instance.logMatchEnd(EndReason.COMPLETED, 0);

        // Store winner info for later display
        this._pendingWinnerId = winnerId;
        this._pendingWinnerKills = winnerKills;

        // Set game ended flag - summary will show after medals are done
        this._gameEnded = true;

        // Award War Coins for match completion + win bonus
        if (!this._botModeActive) {
            // Determine if local player won
            const isWinner = (winnerId === this.userId);
            const matchCoins = isWinner ? 50 : 20;
            this.addWarCoins(matchCoins, isWinner ? 'win' : 'match');
        }

        // Match completion hook for Rent-to-Own licenses
        const mockProfile = { loadout: this.loadout, economy: { licenses: this.licenses } };
        ProgressionSystem.decrementMatchLicenses(mockProfile);

        // SAVE ON SIGNIFICANT PROGRESS: Match Completion
        try {
            this.saveProgress();
        } catch (e) {
            console.warn('[Arena Handler] saveProgress() threw in showGameSummary:', e);
        }
    }

    _showSummaryContent(winnerId, winnerKills) {
        // Set inSummary flag to prevent new medals during summary
        this.inSummary = true;

        // Clear any pending XP text from medals awarded before summary
        if (this.xpDetailText) {
            this.xpDetailText.text = '';
        }

        // Ensure local player has stats entry so we can always show summary
        if (this.userId && !this.playerStats.has(this.userId)) {
            this.playerStats.set(this.userId, { kills: 0, deaths: 0 });
        }
        // Ensure any players we recently saw in the lobby also have stat entries
        if (this._latestPlayerList && Array.isArray(this._latestPlayerList)) {
            for (let i = 0; i < this._latestPlayerList.length; i++) {
                const p = this._latestPlayerList[i];
                const sid = p && (p.sessionId || p.userId);
                if (sid && !this.playerStats.has(sid)) {
                    this.playerStats.set(sid, { kills: 0, deaths: 0 });
                }
            }
        }

        // Convert player stats to array for sorting
        const statsArray = [];
        for (const [userId, stats] of this.playerStats.entries()) {
            statsArray.push({
                userId: userId,
                kills: stats.kills,
                deaths: stats.deaths,
                team: this._teamMap.get(userId) || null,
                kdr: stats.deaths > 0 ? (stats.kills / stats.deaths).toFixed(2) : stats.kills.toString()
            });
        }

        // Sort by kills (descending), then by KDR (descending)
        statsArray.sort((a, b) => {
            if (a.kills !== b.kills) return b.kills - a.kills;
            return parseFloat(b.kdr) - parseFloat(a.kdr);
        });

        // Find local player
        const localPlayerIndex = statsArray.findIndex(p => p.userId === this.userId);
        let summary = '';
        if (localPlayerIndex !== -1) {
            const localPlayer = statsArray[localPlayerIndex];
            if (this._botModeActive) {
                // Simple stats for bot matches
                if (this._botSpeedrunStart && this._botSpeedrunEnd) {
                    const elapsedMs = this._botSpeedrunEnd - this._botSpeedrunStart;
                    const seconds = Math.floor(elapsedMs / 1000);
                    summary += `Time: ${seconds}s | `;
                }
                summary += `Kills: ${localPlayer.kills} | Deaths: ${localPlayer.deaths} | K/D: ${localPlayer.kdr}`;
            } else if (!this.isChaosMode && this._teamMap.size > 0 && localPlayer.team) {
                // ─── Team mode summary ───
                const teamAKills = this.getTeamKills('A');
                const teamBKills = this.getTeamKills('B');
                const myTeam = localPlayer.team;
                const myTeamName = myTeam === 'A' ? 'BLUE' : 'RED';
                const myTeamKills = myTeam === 'A' ? teamAKills : teamBKills;
                const enemyTeamKills = myTeam === 'A' ? teamBKills : teamAKills;
                const teamWon = myTeamKills >= enemyTeamKills;
                // Find MVP (most kills across both teams)
                const mvp = statsArray[0];
                summary += `=== TEAM ${myTeamName} ===\n`;
                summary += teamWon ? 'VICTORY!\n' : 'DEFEAT\n';
                summary += `Score: ${myTeamKills} - ${enemyTeamKills}\n`;
                summary += `Your K/D: ${localPlayer.kills}/${localPlayer.deaths} (${localPlayer.kdr})\n`;
                if (mvp) summary += `MVP: ${mvp.kills} kills`;
            } else {
                // FFA / Chaos stats
                summary += `=== YOUR GAME STATS ===\n`;
                const placement = localPlayerIndex + 1;
                summary += `Placement: ${placement}\n`;
                summary += `Kills: ${localPlayer.kills}\n`;
                summary += `Deaths: ${localPlayer.deaths}\n`;
                summary += `K/D Ratio: ${localPlayer.kdr}\n`;
                if (winnerId === this.userId) {
                    summary += `YOU WON!`;
                } else {
                    summary += `Winner: ${winnerKills} kills`;
                }
            }
        } else {
            summary = 'No stats available.';
        }

        // Set victoryText
        if (this.victoryText) {
            this.victoryText.text = summary;
            // 1. Get the current player count from the latest cached player list
            const playerArr = Array.isArray(this._latestPlayerList) ? this._latestPlayerList : (this._latestPlayerList ? Object.values(this._latestPlayerList) : []);
            const playerCount = playerArr.length;

            // 2. Determine color: Cyan for 1 player (Bot Mode), Gold for 2+ (Real Players)
            // You can use the pre-allocated color vectors from the class
            const activeColor = (playerCount <= 1) ? this._cyanColor : this._goldColor;


            this.victoryText.color = activeColor;

            this.victoryText.active = true;
            // console.log("[arena-handler] Victory text set and activated");
        } else {
            // console.warn("[arena-handler] victoryText is not assigned!");
        }

        // Also log to console for debugging
        // console.log('[arena-handler] Game Summary:', summary);

        // After 4 seconds, replace stats with lobby return hint
        this._guardedTimeout(() => {
            if (this.victoryText) {
                this.victoryText.text = 'Returning to lobby...';
            }
        }, 4000);

        // After 5 seconds, fade out and return to main menu
        this._guardedTimeout(() => {
            if (window.adsManager) {
                window.adsManager.triggerMidroll(() => {
                    this.resetGameState();
                });
            } else {
                this.resetGameState();
            }
        }, 5000);
    }

    handlePlayerUpdate(payload) {
        if (!payload) {
            console.error(`[Arena Handler] handlePlayerUpdate called with null/undefined payload`);
            return;
        }

        const { id, pos, rot, t, justRespawned } = payload;

        if (!id) {
            console.error(`[Arena Handler] handlePlayerUpdate called with no player ID`);
            return;
        }

        if (id === this.userId) return;
        let remote = this.remotePlayerMap.get(id);

        if (!remote) {
            // Reuse a recycled slot from a disconnected player first
            if (this._freeRemoteSlots && this._freeRemoteSlots.length > 0) {
                remote = this._freeRemoteSlots.pop();
            } else if (this.remotePlayerIndex >= this.remotePlayers.length) {
                console.warn(`[Arena Handler] No more remote player slots available! Current index: ${this.remotePlayerIndex}, Max slots: ${this.remotePlayers.length}`);
                return;
            } else {
                remote = this.remotePlayers[this.remotePlayerIndex++];
            }

            if (!remote) {
                console.error(`[Arena Handler] Failed to get remote player object at index ${this.remotePlayerIndex - 1}`);
                return;
            }

            this.remotePlayerMap.set(id, remote);

            // Reactivate/show weapons ONCE when player is added to remotePlayerMap
            const weaponSync = remote.getComponent && remote.getComponent('weapon-sync');
            if (weaponSync && typeof weaponSync.showWeapons === 'function') {
                weaponSync.showWeapons();
                remote._showWeaponsOnce = true;
            }
            remote.active = true;
            if (!remote._physxCache) remote._physxCache = remote.getComponent(PhysXComponent);
            if (remote._physxCache) remote._physxCache.active = true;
            // 🔄 Allocate buffers ONCE per remote
            remote.currentPos = vec3.create();
            remote.targetPos = vec3.create();
            remote.currentRot = quat.create();
            remote.targetRot = quat.create();

            // 🔄 Initialize position to ensure avatar appears
            if (pos && pos.length >= 3) {
                remote.setPositionWorld(pos);
                vec3.copy(remote.currentPos, pos);
                vec3.copy(remote.targetPos, pos);
                remote._needsPositionInit = false;
            }

            // 🔄 Start looping movement sound for remote player
            const remoteSound = remote.getComponent(HowlerAudioSource);
            console.log('remote Sound playing: ', remoteSound.playing());
            if (remoteSound && !remoteSound.playing()) {
                remoteSound.play();
            }

            // 🔄 Try to set the player's name immediately if we have the info
            this._trySetPlayerName(id, remote);

            // console.log(`[Arena Handler] Successfully assigned remote avatar for player ${id} at slot ${this.remotePlayerIndex - 1}`);

            // 🔄 Additional validation
            this._validateRemotePlayer(remote, id);

            // Apply team/chaos appearance to newly allocated remote
            this._applyAppearanceToPlayer(id, remote);
        }

        // 🔄 CRITICAL: Validate remote object before proceeding
        if (!remote) {
            console.error(`[Arena Handler] Remote object is null/undefined for player ${id}`);
            return;
        }

        // Ensure buffers are initialized (safety check for existing remotes)
        if (!remote.currentPos) remote.currentPos = vec3.create();
        if (!remote.targetPos) remote.targetPos = vec3.create();
        if (!remote.currentRot) remote.currentRot = quat.create();
        if (!remote.targetRot) remote.targetRot = quat.create();

        // Recycled-slot first-packet handling: if the slot was returned to the
        // free pool (or pre-allocated by listPlayers without a position), warp
        // the avatar to the incoming position instead of lerping from stale
        // buffers (which could leave it at [0,-1000,0]).
        if (remote._needsPositionInit && pos && pos.length >= 3) {
            vec3.copy(remote.currentPos, pos);
            vec3.copy(remote.targetPos, pos);
            remote.setPositionWorld(pos);
            if (remote.snapshots) remote.snapshots.length = 0;
            if (remote.weaponSnapshots) remote.weaponSnapshots.length = 0;
            remote._needsPositionInit = false;
        }

        // QUEST 2 OPTIMIZATION: Cache weapon-sync component on remote object
        if (!remote._weaponSyncCache) {
            remote._weaponSyncCache = remote.getComponent('weapon-sync');
            if (!remote._weaponSyncCache) {
                console.warn(`[arena-handler] weapon-sync NOT FOUND on remote object: ${remote.name} for ${id}`);
            }
        }
        const sync = remote._weaponSyncCache;
        if (sync) {
            // Always sync weapon type so the correct model becomes visible
            if (payload.weapon) {
                sync.setWeapon(payload.weapon);
            }
            // Sync transform when position data is available
            if (payload.weaponPos && payload.weaponRot) {
                sync.setTransform(payload.weaponPos, payload.weaponRot, payload.weaponPosLeft, payload.weaponRotLeft);
            }
        }

        // QUEST 2 OPTIMIZATION: Cache level-sync component on remote object
        if (!remote._levelSyncCache) remote._levelSyncCache = remote.getComponent('level-sync');
        const levelSync = remote._levelSyncCache;

        // Sync level data
        if (payload.level !== undefined && payload.level !== null) {

            // Store the level data for this player
            this.playerLevels.set(id, payload.level);

            // Update the visual representation
            if (levelSync) {
                levelSync.setLevel(payload.level);
            }
        }

        // Sync prestige data
        if (payload.prestige !== undefined && payload.prestige !== null) {

            // Store the prestige data for this player
            this.playerPrestiges.set(id, payload.prestige);

            // Update the visual representation
            if (levelSync) {
                levelSync.setPrestige(payload.prestige);
            }
        } else {
            // console.log('[ArenaHandler] No prestige data in payload for player', id, '- payload.prestige:', payload.prestige);
        }

        if (remote.lastUpdateTime && t <= remote.lastUpdateTime) return;
        remote.lastUpdateTime = t;

        if (rot) {
            if (!remote.targetRot) {
                remote.targetRot = quat.create();
            }
            quat.set(remote.targetRot, rot[0], rot[1], rot[2], rot[3]);
        } else {
            // console.warn(`[arena-handler] Invalid rotation for user ${id}:`, rot);
        }

        if (justRespawned) {
            remote.justRespawnedTimer = 1.0; // 1 second countdown
            if (!remote.targetPos) {
                remote.targetPos = vec3.create();
            }
            if (!remote.currentPos) {
                remote.currentPos = vec3.create();
            }
            vec3.copy(remote.targetPos, pos);
            vec3.copy(remote.currentPos, pos);
            remote.setPositionWorld(pos);
            // Clear any buffered snapshots to avoid ghost interpolation after respawn
            if (remote.snapshots) remote.snapshots.length = 0;
            if (remote.weaponSnapshots) remote.weaponSnapshots.length = 0;

            // QUEST 2 OPTIMIZATION: Use cached PhysXComponent
            if (!remote._physxCache) remote._physxCache = remote.getComponent(PhysXComponent);
            const remotePhysx = remote._physxCache;
            if (remotePhysx) {
                remotePhysx.linearVelocity = this._zeroVec3;
                remotePhysx.angularVelocity = this._zeroVec3;
            }
        } else {
            if (!remote.targetPos) remote.targetPos = vec3.create();
            vec3.set(remote.targetPos, pos[0], pos[1], pos[2]);

            // Snapshot buffer for smooth interpolation at 20 Hz
            if (!remote.snapshots) remote.snapshots = [];
            remote.snapshots.push({
                t: performance.now(),
                pos: [pos[0], pos[1], pos[2]],
                rot: rot ? [rot[0], rot[1], rot[2], rot[3]] : null,
            });
            // Trim snapshots older than 600ms to bound memory
            const cutoff = performance.now() - 600;
            while (remote.snapshots.length > 2 && remote.snapshots[0].t < cutoff) {
                remote.snapshots.shift();
            }
        }
    }


    /**
     * Return the dedicated per-slot body material for a remote player object.
     * Each remote slot's sub-meshes share an engine-wide material (Player1–Player7)
     * which is pre-mapped at setup from the editor properties.
     */
    _getPlayerSlotMaterial(obj) {
        if (!obj) return null;
        return this._remoteToMaterial ? this._remoteToMaterial.get(obj) || null : null;
    }

    // 🔄 CRITICAL: Initialize all remote player slots properly
    _initializeRemotePlayerSlots() {
        if (!this.remotePlayers || this.remotePlayers.length === 0) {
            console.warn('[Arena Handler] No remote player objects available in player pool!');
            return;
        }

        for (let i = 0; i < this.remotePlayers.length; i++) {
            const remote = this.remotePlayers[i];
            if (remote) {
                // Pre-allocate position buffers if missing
                if (!remote.currentPos) remote.currentPos = vec3.create();
                if (!remote.targetPos) remote.targetPos = vec3.create();
                if (!remote.currentRot) remote.currentRot = quat.create();
                if (!remote.targetRot) remote.targetRot = quat.create();
                // Cache PlayerMesh MeshComponent for direct access (no child search)
                // Mark weapon-sync as remote so it ignores local session-end events
                const ws = remote.getComponent(WeaponSync);
                if (ws) ws.isRemote = true;
                // Ensure they are active objects (not disabled)
                remote.active = true;
            } else {
                console.error(`[Arena Handler] Remote player slot ${i} is null/undefined!`);
            }
        }
    }

    // 🔄 Validate remote player assignment
    _validateRemotePlayer(remote, playerId) {
        if (!remote) {
            console.error(`[Arena Handler] CRITICAL: Remote player object is null for ${playerId}!`);
            return false;
        }

        if (!remote.setPositionWorld) {
            console.error(`[Arena Handler] CRITICAL: Remote player object missing setPositionWorld method for ${playerId}!`);
            return false;
        }

        // Test position setting
        try {
            const testPos = [0, 0, 0];
            remote.getPositionWorld(testPos);
            // console.log(`[Arena Handler] Remote player ${playerId} position validation passed:`, testPos);
            return true;
        } catch (e) {
            console.error(`[Arena Handler] CRITICAL: Remote player ${playerId} position methods failed:`, e);
            return false;
        }
    }

    // 🔄 Periodic validation of all remote players
    _performRemotePlayerValidation() {
        let issuesFound = 0;

        for (const [id, remote] of this.remotePlayerMap.entries()) {
            if (!remote) {
                // console.error(`[Arena Handler] Validation: Remote player ${id} is null!`);
                this.remotePlayerMap.delete(id);
                issuesFound++;
                continue;
            }
            // Check if avatar is at the hidden position (might be stuck there)
            if (remote.getPositionWorld) remote.getPositionWorld(this.tempPos);
            if (this.tempPos[1] <= -999) {
                // console.warn(`[Arena Handler] Validation: Remote player ${id} appears to be hidden at Y=${currentPos[1]}`);
                if (remote.targetPos && remote.targetPos[1] > -999) {
                    // console.log(`[Arena Handler] Validation: Recovering remote player ${id} from hidden position`);
                    remote.setPositionWorld(remote.targetPos);
                    issuesFound++;
                }
            }
        }

        if (issuesFound > 0) {
            // console.log(`[Arena Handler] Validation completed: Found and fixed ${issuesFound} issues with remote players`);
        }
    }

    // 🔄 Debug function to check remote player status (call from console)
    debugRemotePlayers() {
        // console.log(`[Arena Handler] Debug: Total remote player slots: ${this.remotePlayers.length}`);
        // console.log(`[Arena Handler] Debug: Active remote players: ${this.remotePlayerMap.size}`);
        // console.log(`[Arena Handler] Debug: Next available slot: ${this.remotePlayerIndex}`);

        for (const [id, remote] of this.remotePlayerMap.entries()) {
            const pos = remote.getPositionWorld ? remote.getPositionWorld([]) : 'unknown';
            const active = remote.active;
            // console.log(`[Arena Handler] Debug: Player ${id} - Active: ${active}, Position: [${pos[0]?.toFixed(2)}, ${pos[1]?.toFixed(2)}, ${pos[2]?.toFixed(2)}]`);
        }

        return {
            totalSlots: this.remotePlayers.length,
            activeRemotes: this.remotePlayerMap.size,
            nextSlot: this.remotePlayerIndex,
            remotes: Array.from(this.remotePlayerMap.keys())
        };
    }

    /**
     * Wipe per-owner runtime state from a remote slot so it can be safely
     * recycled to a new player. Must be called BEFORE pushing the slot onto
     * `_freeRemoteSlots`. Without this, a recycled slot keeps the previous
     * owner's interpolation buffers, snapshot history and `lastUpdateTime`,
     * which causes the next joiner's first updates to be silently rejected
     * (timestamp guard) or rendered at the previous owner's stale position
     * (e.g. the [0,-1000,0] hidden spot), leaving the new player invisible.
     *
     * NOTE: We must NOT null out the vec3/quat buffers — the per-frame
     * update loop dereferences them without null checks (vec3.distance,
     * setPositionWorld, quat.slerp). Nulling causes the loop to throw
     * once `listPlayers()` re-inserts the recycled slot into the map, which
     * silently aborts remote-player updates (avatar AND weapon transforms).
     * Instead we zero/park the buffers at the hidden coordinate and rely on
     * `_needsPositionInit` to warp on the new owner's first packet.
     */
    _resetRemoteSlot(remote) {
        if (!remote) return;
        remote.lastUpdateTime = 0;
        // Ensure buffers exist, then park them at the hidden coordinate so the
        // per-frame interpolation loop keeps the avatar invisible (and doesn't
        // pull it back to the previous owner's last position) until the new
        // owner's first packet arrives.
        if (!remote.currentPos) remote.currentPos = vec3.create();
        if (!remote.targetPos) remote.targetPos = vec3.create();
        if (!remote.currentRot) remote.currentRot = quat.create();
        if (!remote.targetRot) remote.targetRot = quat.create();
        vec3.set(remote.currentPos, 0, -1000, 0);
        vec3.set(remote.targetPos, 0, -1000, 0);
        quat.identity(remote.currentRot);
        quat.identity(remote.targetRot);
        if (remote.snapshots) remote.snapshots.length = 0;
        if (remote.weaponSnapshots) remote.weaponSnapshots.length = 0;
        remote.justRespawnedTimer = 0;
        remote.justRespawnedUntil = 0;
        remote._showWeaponsOnce = false;
        remote._needsPositionInit = true;
    }

    // --- Add player disconnect cleanup ---
    handlePlayerDisconnect(sessionId) {
        const remote = this.remotePlayerMap.get(sessionId);
        if (remote) {
            // Stop any sounds associated with the remote player
            const remoteSound = remote.getComponent(HowlerAudioSource);
            if (remoteSound) {
                remoteSound.stop();
            }
            // Hide/reposition the weapons of the disconnected player
            const weaponSync = remote.getComponent('weapon-sync');
            if (weaponSync) {
                // Move weapons far away by setting positions to far-off coordinates
                const farAwayPos = [0, -1000, 0];
                const farAwayRot = [0, 0, 0, 1]; // Identity quaternion
                weaponSync.setTransform(farAwayPos, farAwayRot, farAwayPos, farAwayRot);
                if (typeof weaponSync.hideWeapons === 'function') {
                    weaponSync.hideWeapons();
                }
                // console.log(`[arena-handler] Hidden weapons for disconnected player ${sessionId}`);
            }
            // Move avatar far away or hide it
            remote.setPositionWorld([0, -1000, 0]);
            const remotePhysX = remote.getComponent(PhysXComponent);
            if (remotePhysX) {
                remotePhysX.active = false;
                // console.log(`[arena-handler] Disabled hit collider for disconnected player ${sessionId}`);
            }
            // Wipe per-owner state so the next player to occupy this slot
            // doesn't inherit stale interpolation buffers, snapshot history,
            // or a high `lastUpdateTime` (which would silently drop the new
            // joiner's first packets and leave their avatar invisible at the
            // hidden [0,-1000,0] position).
            this._resetRemoteSlot(remote);
            // Recycle the slot so it can be reused by the next player who joins
            this._freeRemoteSlots.push(remote);
            // Remove from map
            this.remotePlayerMap.delete(sessionId);
            // Clean up level data
            this.playerLevels.delete(sessionId);
            // console.log(`[arena-handler] Cleaned up remote avatar for disconnected player ${sessionId}`);
        }

        // Remove player stats — kills leave with the player
        this.playerStats.delete(sessionId);
        // Remove team assignment
        this._teamMap.delete(sessionId);
        // Remove from ready map
        this._playerReadyMap.delete(sessionId);
        // Remove from activity map
        this._playerActivity.delete(sessionId);
        // Tear down WebRTC voice peer
        this.proximityChat.removePeer(sessionId);

        // console.log(`[arena-handler] Player ${sessionId} disconnected and stats removed`);
        // Refresh canonical player list and mode UI to avoid leaving the
        // local client in a stale fade/countdown state. This ensures the
        // centralized `listPlayers()` logic runs and updates mode/fade.
        try {
            this.listPlayers();
        } catch (e) {
            // best-effort
        }

        // If a fade overlay was left opaque due to an interrupted
        // transition, ensure we fade back out so players aren't stuck
        // looking at a black screen.
        this._guardedTimeout(() => {
            try {
                if ((this._fadeAlpha || 0) >= 0.99 && this._fadeState === 'none') {
                    this._startFade(0, [0, 0, 0], 0.5);
                }
            } catch (e) { }
        }, 200);
    }

    // Handle player kicked messages
    handlePlayerKicked(payload) {
        if (payload && payload.playerId && payload.reason) {
            // console.log(`[Arena Handler] Player ${payload.playerId} was kicked: ${payload.reason}`);
            // You could display this notification in the UI if desired
            // For example, show a toast notification
        }
    }

    // Handle player exiting XR
    handlePlayerExitXR(payload) {
        if (payload && payload.playerId) {
            // console.log(`[XR System] Player ${payload.playerId} exited XR: ${payload.reason || 'Unknown reason'}`);

            // Clean up the player who exited XR using existing disconnect handler
            this.handlePlayerDisconnect(payload.playerId);

            // You could also display a notification in the UI if desired
            // For example: "Player disconnected - removed headset"
        }
    }

    listPlayers() {
        if (!this.lobby) {
            // console.error('[arena-handler] Lobby not initialized.');
            return;
        }
        // console.log('[arena-handler] listPlayers() called');
        this.lobby.actions.listPlayers().then(players => {
            // console.log('[arena-handler] listPlayers() returned:', players);
            const playerArr = Array.isArray(players) ? players : Object.values(players);
            this._updateBotMode(playerArr);
            this.updateGameMode(playerArr);

            this._latestPlayerList = playerArr; // Store for later use

            // --- Ensure all mapped remotes have updated names ---
            // PERFORMANCE: Use Map iterator directly instead of entries()
            for (const playerId of this.remotePlayerMap.keys()) {
                const remote = this.remotePlayerMap.get(playerId);
                if (remote && remote.children) {
                    // PERFORMANCE: Use for loop instead of .find()
                    let playerInfo = null;
                    for (let i = 0; i < playerArr.length; i++) {
                        if ((playerArr[i].sessionId || playerArr[i].userId) === playerId) {
                            playerInfo = playerArr[i];
                            break;
                        }
                    }
                    if (playerInfo) {
                        const playerName = playerInfo.name || playerInfo.username || "Player";
                        this.setRemotePlayerNameLabels(remote, playerName);
                    }
                }
            }

            // --- Set usernames on remote player objects (for all matches) ---
            // PERFORMANCE: Use for loop instead of forEach
            for (let i = 0; i < playerArr.length; i++) {
                const player = playerArr[i];
                // Skip local player
                if (player.sessionId === this.userId || player.userId === this.userId) continue;
                // Find the remote player object
                let remote = this.remotePlayerMap.get(player.sessionId || player.userId);
                const playerName = player.name || player.username || "Player";

                // If not in map, allocate via the normal pool path (free slots first, then next index)
                if (!remote) {
                    if (this._freeRemoteSlots && this._freeRemoteSlots.length > 0) {
                        remote = this._freeRemoteSlots.pop();
                    } else if (this.remotePlayerIndex < this.remotePlayers.length) {
                        remote = this.remotePlayers[this.remotePlayerIndex++];
                    }
                    if (remote) {
                        this.remotePlayerMap.set(player.sessionId || player.userId, remote);
                        remote.active = true;

                        // Ensure interpolation buffers exist BEFORE the slot
                        // enters remotePlayerMap, otherwise the per-frame
                        // update loop dereferences null buffers and throws,
                        // silently aborting all remote player & weapon
                        // syncing. Park them at the hidden coordinate so the
                        // avatar stays out of view until the new owner's
                        // first packet warps it via _needsPositionInit.
                        if (!remote.currentPos) remote.currentPos = vec3.create();
                        if (!remote.targetPos) remote.targetPos = vec3.create();
                        if (!remote.currentRot) remote.currentRot = quat.create();
                        if (!remote.targetRot) remote.targetRot = quat.create();
                        vec3.set(remote.currentPos, 0, -1000, 0);
                        vec3.set(remote.targetPos, 0, -1000, 0);
                        quat.identity(remote.currentRot);
                        quat.identity(remote.targetRot);
                        remote.lastUpdateTime = 0;
                        if (remote.snapshots) remote.snapshots.length = 0;
                        if (remote.weaponSnapshots) remote.weaponSnapshots.length = 0;
                        remote._needsPositionInit = true;

                        if (!remote._physxCache) remote._physxCache = remote.getComponent(PhysXComponent);
                        if (remote._physxCache) remote._physxCache.active = true;
                        // Reactivate/show weapons ONCE when player is added to remotePlayerMap
                        const weaponSync = remote.getComponent && remote.getComponent('weapon-sync');
                        if (weaponSync && typeof weaponSync.showWeapons === 'function') {
                            weaponSync.showWeapons();
                            remote._showWeaponsOnce = true;
                        }
                        // Apply team/chaos appearance to newly allocated remote
                        this._applyAppearanceToPlayer(player.sessionId || player.userId, remote);
                    }
                }

                // console.log(`[arena-handler] Setting name for player ${player.sessionId || player.userId}: "${playerName}" (from player data:`, player, ')');
                if (remote && remote.children) {
                    // console.log(`[arena-handler] Found remote object for player, setting name to: "${playerName}"`);
                    this.setRemotePlayerNameLabels(remote, playerName);
                    // Set level if we have it stored
                    const playerId = player.sessionId || player.userId;
                    if (this.playerLevels && this.playerLevels.has(playerId)) {
                        this.setRemotePlayerLevel(remote, this.playerLevels.get(playerId));
                    }
                    // Set prestige if we have it stored
                    if (this.playerPrestiges && this.playerPrestiges.has(playerId)) {
                        this.setRemotePlayerPrestige(remote, this.playerPrestiges.get(playerId));
                    }
                } else {
                    console.warn(`[arena-handler] No remote object found for player ${player.sessionId || player.userId} with name "${playerName}"`);
                }
            }
            // Set local player's name label (bulletproof)
            const localName = this.getLocalPlayerName();
            if (this.player && this.player.children) {
                this.setRemotePlayerNameLabels(this.player, localName);
                // Local player level/prestige display is handled by this.levelText;
                // PhysicsPlayerBody has no level-sync component, so skip.
            } else {
                console.warn('[arena-handler] Local player object or children missing, cannot set name label');
            }
            // --- NEW: If already in real player mode, show info and auto trigger countdown ---
            if (playerArr.length > 1 && this._botModeActive === false) {
                if (this.victoryText) {
                    // Show the current game mode label instead of a hardcoded string
                    const modeLabel = this._getCurrentGameModeLabel() || 'Game Mode';
                    this.victoryText.text = modeLabel;
                    // color: gold for real player notifications (use shared constant)
                    this.victoryText.color = this._goldColor;
                    this.victoryText.active = true;
                }

                // Schedule the countdown to start after the info display (consistent with _updateBotMode)
                if (this._modeTransitionTimer) clearTimeout(this._modeTransitionTimer);
                this._modeTransitionTimer = this._guardedTimeout(() => {
                    this._modeTransitionTimer = null;
                    // Only start the match if central flags allow it
                    if (this._setupFlag && !this._matchActive) {
                        this.startMatchCountdown();
                        return;
                    }
                    // Clear informational text and restore fade if no countdown started
                    if (this.victoryText) {
                        this.victoryText.active = false;
                        this.victoryText.text = '';
                    }
                    if (!this._countdownActive && !this._matchActive) {
                        this._startFade(0, [0, 0, 0], 0.5);
                    }
                }, 4000);
            }
        }).catch(err => {
            console.error('[arena-handler] Error listing players:', err);
        });
    }
    update(dt) {
        const t = this;

        // ---- TIME ----
        t._elapsedMs += dt * 1000;

        // ---- CORE SYSTEMS (always run) ----
        t._checkAfk(dt);
        t._updateAutoHeal(dt);
        t._processMedalQueue(dt);

        // ---- MULTI-KILL TIMER ----
        if (t._multiKillResetTimer > 0) {
            t._multiKillResetTimer -= dt;
            if (t._multiKillResetTimer <= 0) {
                t._multiKillResetTimer = 0;
                t._awardedMultiKillMedals.clear();
            }
        }

        // ---- HITMARKER ----
        if (t._hitMarkerActive) {
            let time = t._hitMarkerActiveTime + dt;
            if (time >= 0.5) {
                t._hitMarkerActive = false;
                t._hitMarkerActiveTime = 0;
                t.hitMarker.setPositionWorld([0, -1000, 0]);
            } else {
                t._hitMarkerActiveTime = time;
            }
        }

        // ---- DAMAGE FLASH (highest priority visual) ----
        const fadingMat = t.fadingMat;

        if (fadingMat && t._dmgFlashActive) {
            let elapsed = t._dmgFlashElapsed + dt;
            t._dmgFlashElapsed = elapsed;

            const rise = t._dmgFlashRiseTime;
            const total = t._dmgFlashDuration;

            const invRise = 1 / (rise || 1e-6);
            const invDecay = 1 / ((total - rise) || 1e-6);

            let alpha;
            if (elapsed < rise) {
                alpha = elapsed * invRise * t._dmgFlashPeakAlpha;
            } else {
                const decayT = (elapsed - rise) * invDecay;
                alpha = t._dmgFlashPeakAlpha * (1 - decayT);
                if (alpha < 0) alpha = 0;
            }

            const col = t._dmgFlashColorArr;
            col[0] = 1; col[1] = 0; col[2] = 0; col[3] = alpha;
            fadingMat.setDiffuseColor(col);

            if (elapsed >= total) {
                t._dmgFlashActive = false;
                t._dmgFlashElapsed = 0;
                fadingMat.setDiffuseColor([t._fadeColor[0], t._fadeColor[1], t._fadeColor[2], t._fadeAlpha]);
            }
        }
        // ---- FADE SYSTEM ----
        else if (fadingMat) {
            const state = t._fadeState;

            t._fadeColorArr[0] = t._fadeColor[0];
            t._fadeColorArr[1] = t._fadeColor[1];
            t._fadeColorArr[2] = t._fadeColor[2];
            t._fadeColorArr[3] = t._fadeAlpha;

            if (state === 'hold') {
                fadingMat.setDiffuseColor(t._fadeColorArr);
            }
            else if (state !== 'none') {
                let elapsed = t._fadeElapsed + dt;
                t._fadeElapsed = elapsed;

                const duration = t._fadeDuration || 1e-6;
                let progress = elapsed / duration;
                if (progress > 1) progress = 1;

                let alpha = t._fadeAlpha;

                if (state === 'in') {
                    t._fadeAlpha += (t._fadeTargetAlpha - t._fadeAlpha) * (dt / duration);
                } else {
                    t._fadeAlpha -= (t._fadeAlpha - t._fadeTargetAlpha) * (dt / duration);
                }

                if (progress >= 1) {
                    alpha = t._fadeTargetAlpha;
                    t._fadeState = 'none';

                    // Finalize fade and invoke callback only if its token
                    // still matches the currently-active fade token. This
                    // prevents newer fades from accidentally cancelling
                    // or overwriting earlier callbacks.
                    const cb = t._fadeCallback;
                    const cbToken = t._fadeCallbackToken;
                    if (cb && cbToken != null && cbToken === t._fadeActiveToken) {
                        t._fadeCallback = null;
                        t._fadeCallbackToken = null;
                        t._fadeActiveToken = null;
                        try { cb(); } catch (e) { console.warn('[arena-handler] _fadeCallback threw', e); }
                    } else {
                        // Clear any stored callback/token if they don't match
                        t._fadeCallback = null;
                        t._fadeCallbackToken = null;
                        t._fadeActiveToken = null;
                    }
                }
                // Clamp alpha to [0,1] to avoid negative/exponential drift
                t._fadeAlpha = Math.max(0, Math.min(1, alpha));
                fadingMat.setDiffuseColor(t._fadeColorArr);
            }
            else {
                fadingMat.setDiffuseColor(t._fadeColorArr);
            }
        }

        // ---- IMMORTALITY ----
        if (t._fadeAlpha >= 1 && t._fadeState !== 'none') {
            t._isImmortal = true;
        } else if (!t._inCountdown) {
            t._isImmortal = false;
        }

        // ---- NETWORK EARLY EXIT ----
        if (!t.isConnected) return;

        // ---- REMOTE VALIDATION ----
        let rvTimer = (t._remoteValidationTimer || 0) + dt;
        if (rvTimer >= 5) {
            rvTimer = 0;
            t._performRemotePlayerValidation();
        }
        t._remoteValidationTimer = rvTimer;

        const now = performance.now();
        // ms to delay rendering behind the latest received snapshot (tuneable)
        const INTERPOLATION_DELAY = 100; // ms behind latest snapshot; 2 packets at 20 Hz
        const remoteMap = t.remotePlayerMap;

        // ---- REMOTE PLAYERS ----
        for (const [id, remote] of remoteMap) {
            if (!remote) {
                remoteMap.delete(id);
                continue;
            }

            if (remote.justRespawnedUntil && now < remote.justRespawnedUntil) {
                t.teleportRemotePlayer(remote);
                continue;
            }

            if (t._isDead) {
                if (remote.currentPos) remote.setPositionWorld(remote.currentPos);
                continue;
            }

            // ---- POSITION + ROTATION (snapshot interpolation) ----
            const snaps = remote.snapshots;
            if (snaps && snaps.length >= 2) {
                const renderTime = now - INTERPOLATION_DELAY;
                // Find the highest snapshot index whose time <= renderTime
                let lo = 0;
                for (let si = 1; si < snaps.length - 1; si++) {
                    if (snaps[si].t <= renderTime) lo = si;
                    else break;
                }
                const s0 = snaps[lo];
                const s1 = snaps[lo + 1];
                const span = s1.t - s0.t;
                const alpha = span > 0 ? Math.max(0, Math.min(1, (renderTime - s0.t) / span)) : 1;
                vec3.lerp(remote.currentPos, s0.pos, s1.pos, alpha);
                remote.setPositionWorld(remote.currentPos);
                if (s0.rot && s1.rot) {
                    quat.slerp(remote.currentRot, s0.rot, s1.rot, alpha);
                    remote.setRotationWorld(remote.currentRot);
                }
            } else {
                // Fewer than 2 snapshots — fall back to direct snap (initial spawn)
                if (vec3.distance(remote.currentPos, remote.targetPos) < 100) {
                    vec3.lerp(remote.currentPos, remote.currentPos, remote.targetPos, dt * 10);
                } else {
                    vec3.copy(remote.currentPos, remote.targetPos);
                }
                remote.setPositionWorld(remote.currentPos);
                quat.slerp(remote.currentRot, remote.currentRot, remote.targetRot, dt * 10);
                remote.setRotationWorld(remote.currentRot);
            }

        }

        // ---- LOBBY SYNC ----
        if (!t.lobby) return;

        let ut = (t._updateTimer || 0) + dt;
        if (ut < 0.05) {
            t._updateTimer = ut;
        } else {
            t._updateTimer = 0;

            const pos = t.tempPos;
            const rot = t.tempRot;

            t.player.getPositionWorld(pos);
            t.head.getRotationWorld(rot);

            // Extract yaw only (optimized quaternion → yaw)
            const yaw = Math.atan2(
                2 * (rot[3] * rot[1] + rot[0] * rot[2]),
                1 - 2 * (rot[1] * rot[1] + rot[2] * rot[2])
            );

            quat.setAxisAngle(this.tempRot, t.up, yaw);

            t.proximityChat?.updatePositions(pos, this.tempRot, remoteMap);

            // Optimized payload reusing Float32Arrays instead of creating regular arrays [x,y,z]
            this._sendPos.set(pos);
            this._sendRot.set(this.tempRot);

            // Use pre-allocated payload and wrapper to prevent object allocations every 100ms
            this._updatePayload.id = t.userId;
            this._updatePayload.pos = this._sendPos;
            this._updatePayload.rot = this._sendRot;
            this._updatePayload.t = now;
            this._updatePayload.justRespawned = t.justRespawned || false;
            this._updatePayload.weapon = t.playerMovement?.getCurrentWeaponId?.();
            this._updatePayload.weaponPos = t.playerMovement?.getCurrentWeaponPosition?.();
            this._updatePayload.weaponRot = t.playerMovement?.getCurrentWeaponRotation?.();
            this._updatePayload.weaponPosLeft = t.playerMovement?.getCurrentWeaponPositionLeft?.();
            this._updatePayload.weaponRotLeft = t.playerMovement?.getCurrentWeaponRotationLeft?.();
            this._updatePayload.level = t._level;
            this._updatePayload.prestige = t.prestige;

            t.lobby.data.sendToAll(this._networkUpdateMsg);

            t.justRespawned = false;
        }

        // ---- BOT TIMER ----
        if (t.progressionText) {
            if (t._botModeActive && t._botSpeedrunStart && !t._botSpeedrunEnd) {
                const seconds = ((now - t._botSpeedrunStart) / 1000) | 0;
                t.progressionText.text = seconds + "s";
            } else {
                t.progressionText.text = '';
            }
        }
    }


    // Teleport a remote player instantly to their target position and sync currentPos
    teleportRemotePlayer(remote) {
        // If targetPos is undefined, do nothing
        if (!remote.targetPos) return;
        // Set both the object's position and sync both currentPos and targetPos
        remote.setPositionWorld(remote.targetPos);
        // If remote has a PhysXComponent, zero velocity (cached)
        if (!remote._physxCache) remote._physxCache = remote.getComponent(PhysXComponent);
        const remotePhysx = remote._physxCache;
        if (remotePhysx) {
            remotePhysx.linearVelocity = this._zeroVec3;
            remotePhysx.angularVelocity = this._zeroVec3;
        }
    }

    // Add a method to update the kill progress bar
    updateKillProgressBar() {
        // Calculate level progress instead of kill progress
        const totalXpForCurrentLevel = ArenaHandler.getTotalXpForLevel(this.level);
        const totalXpForNextLevel = ArenaHandler.getTotalXpForLevel(this.level + 1);
        const xpProgress = this.totalXp - totalXpForCurrentLevel;
        const xpNeededForNext = totalXpForNextLevel - totalXpForCurrentLevel;

        this._progressScale[0] = Math.min(xpProgress / xpNeededForNext, 1.0) * 0.5;
        if (this.progressionBar) this.progressionBar.setScalingLocal(this._progressScale);
    }

    // Synchronize display durations for XP and medals
    setDisplayDuration(duration) {
        this._medalDisplayDuration = duration;
        this._xpDisplayDuration = duration;
        // console.log(`[Arena Handler] Display durations synchronized to ${duration}s for both XP and medals`);
    }

    // Queue medal for display (prevents overlapping)
    displayMedal(medalName) {
        // Don't queue medals during game summary
        if (this.inSummary) {
            console.warn(`[Medal Debug] Blocked medal '${medalName}' - inSummary: true`);
            return;
        }

        // Check if this medal was recently awarded (within last 2 seconds) to prevent spam
        const now = this._elapsedMs;
        const lastTime = this._recentMedals.get(medalName);
        if (lastTime && (now - lastTime) < 2000) {
            return; // Skip duplicate medals too close together
        }

        // Add medal to queue if not already there (prevent duplicates in queue)
        if (!this._medalQueueSet.has(medalName)) {
            this._medalQueueSet.add(medalName);
            // For multi-kill medals, ensure they're in correct order
            const multiKillMedals = ['DOUBLE KILL!', 'TRIPLE KILL!', 'QUAD KILL!', 'PENTA KILL!'];
            const isMultiKill = multiKillMedals.includes(medalName);

            if (isMultiKill) {
                // Insert multi-kill medals in proper order
                const medalPriority = multiKillMedals.indexOf(medalName);
                let insertIndex = this._medalQueue.length;

                // Find the correct position to insert this medal
                for (let i = 0; i < this._medalQueue.length; i++) {
                    const queuedMedal = this._medalQueue[i];
                    if (multiKillMedals.includes(queuedMedal)) {
                        const queuedPriority = multiKillMedals.indexOf(queuedMedal);
                        if (medalPriority < queuedPriority) {
                            insertIndex = i;
                            break;
                        }
                    }
                }

                this._medalQueue.splice(insertIndex, 0, medalName);
            } else {
                // Regular medals go at the end
                this._medalQueue.push(medalName);
            }

            this._recentMedals.set(medalName, now);
        }
    }

    // Internal function to actually display a medal
    _showMedal(medalName) {
        // Check if this is an unlock notification
        if (medalName.startsWith('UNLOCK_')) {
            this._showUnlock(medalName);
            return;
        }

        // Award bonus XP for medal
        const medalXp = this._getMedalXpBonus(medalName);
        if (medalXp > 0) {
            this.applyXP(medalXp, true); // true indicates medal bonus
        }

        // Set medal text
        if (this.medalInfoText) {
            this.medalInfoText.active = true;
            this.medalInfoText.text = medalName;
        }

        // Set medal explanation
        if (this.medalDetailsText) {
            this.medalDetailsText.active = true;
            const explanation = this._getMedalExplanation(medalName);
            this.medalDetailsText.text = explanation;
        }

        // Play appropriate medal sound
        this.playMedalSound(medalName);
    }

    // Internal function to display unlock notifications
    _showUnlock(unlockId) {
        // Get unlock details
        const unlockDetails = this._unlockDetails?.get(unlockId);
        if (!unlockDetails) {
            console.warn(`[Unlock] No details found for unlock: ${unlockId}`);
            return;
        }

        const { unlockMedalText } = unlockDetails;

        // Set compact unlock text in medal info (primary display)
        if (this.medalInfoText) {
            this.medalInfoText.active = true;
            this.medalInfoText.text = unlockMedalText;
        }

        // Clear medal details since we have everything in one line
        if (this.medalDetailsText) {
            this.medalDetailsText.active = false;
            this.medalDetailsText.text = '';
        }

        // Play unlock sound
        if (this.unlockSound) {
            try {
                this.unlockSound.play();
            } catch (error) {
                console.error(`[Unlock Sound] Error playing unlock sound:`, error);
            }
        } else if (this.levelUpSound) {
            try {
                this.levelUpSound.play();
            } catch (error) {
                console.error(`[Unlock Sound] Error playing level up sound:`, error);
            }
        }

        // Also play general medal achievement sound for consistency
        if (this.medalAchievementSound) {
            try {
                this.medalAchievementSound.play();
            } catch (error) {
                console.error(`[Unlock Sound] Error playing medal achievement sound:`, error);
            }
        }

        // Trigger haptic feedback for unlock
        this.triggerHapticFeedback(0.5, 200, 'both');

        // Clean up unlock details after display
        this._guardedTimeout(() => {
            if (this._unlockDetails) {
                this._unlockDetails.delete(unlockId);
            }
        }, 100);
    }

    // Debug function to check medal and sound system state
    debugMedalSoundState() {
        console.log('[MEDAL SOUND DEBUG] Current State:', {
            inSummary: this.inSummary,
            medalQueueLength: this._medalQueue.length,
            medalQueue: this._medalQueue.slice(),
            processingMedal: this._processingMedal,
            medalDelayTimer: this._medalDelayTimer,
            mercilessSound: {
                exists: !!this.mercilessSound,
                hasPlay: !!this.mercilessSound?.play,
                soundObject: !!this.mercilessSoundObject,
                component: this.mercilessSound?.constructor?.name
            },
            allSounds: {
                doubleKill: !!this.doubleKillSound?.play,
                tripleKill: !!this.tripleKillSound?.play,
                quadKill: !!this.quadKillSound?.play,
                pentaKill: !!this.pentaKillSound?.play,
                merciless: !!this.mercilessSound?.play,
                rampage: !!this.rampageSound?.play,
                untouchable: !!this.untouchableSound?.play,
                medalAchievement: !!this.medalAchievementSound?.play
            }
        });
    }

    // Clear the medal text
    _clearMedal() {
        if (this.medalInfoText) {
            this.medalInfoText.active = false;
            this.medalInfoText.text = '';
        }
        if (this.medalDetailsText) {
            this.medalDetailsText.active = false;
            this.medalDetailsText.text = '';
        }
    }

    // Get XP bonus for different medal types
    // Scaled for new system: exceptional performance can add 1-3 extra levels per game
    _getMedalXpBonus(medalName) {
        const bonuses = {
            'DOUBLE KILL!': 100,    // Minor bonus
            'TRIPLE KILL!': 200,    // Quarter level
            'QUAD KILL!': 300,      // Third of a level  
            'PENTA KILL!': 400,     // Half a level
            'PISTOL SPECIALIST!': 150,
            'RIFLE SPECIALIST!': 150,
            'SHOTGUN SPECIALIST!': 150,
            'SNIPER SPECIALIST!': 150,
            'MERCILESS!': 500,      // Quarter level
            'RAMPAGE!': 600,        // Third of a level
            'UNTOUCHABLE!': 700,    // Over half a level
            'CLOSE QUARTERS!': 150,
            'SNIPER ELITE!': 450,
            'LONG SHOT!': 150,
            'LAST STAND!': 150
        };

        // Check if it's a standard medal
        if (bonuses[medalName]) {
            return bonuses[medalName];
        }

        // Handle dynamic specialist medals (weapon names)
        if (medalName.endsWith(' SPECIALIST!')) {
            return 200; // Standard specialist bonus
        }

        // Default bonus for unknown medals
        return 75;
    }

    // Get medal explanation based on medal name
    _getMedalExplanation(medalName) {
        const explanations = {
            'DOUBLE KILL!': 'Kill 2 enemies within 4 seconds',
            'TRIPLE KILL!': 'Kill 3 enemies within 8 seconds',
            'QUAD KILL!': 'Kill 4 enemies within 12 seconds',
            'PENTA KILL!': 'Kill 5 enemies within 16 seconds',
            'PISTOL SPECIALIST!': 'Get 5 kills with pistol',
            'RIFLE SPECIALIST!': 'Get 5 kills with rifle',
            'SHOTGUN SPECIALIST!': 'Get 5 kills with shotgun',
            'SNIPER SPECIALIST!': 'Get 5 kills with sniper',
            'MERCILESS!': 'Get 10 consecutive kills with same weapon',
            'RAMPAGE!': 'Get 10 kill streak without dying',
            'UNTOUCHABLE!': 'Get 12 kill streak without dying',
            'CLOSE QUARTERS!': 'Kill enemy within 6m using shotgun',
            'SNIPER ELITE!': 'Kill enemy from 100+ meters with sniper',
            'LONG SHOT!': 'Kill enemy from 75+ meters',
            'LAST STAND!': 'Kill enemy while having less than 20% health'
        };

        // Check if it's already in the static explanations
        if (explanations[medalName]) {
            return explanations[medalName];
        }

        // Handle dynamic weapon specialist medals
        if (medalName.endsWith(' SPECIALIST!')) {
            return 'Get 5 kills with this weapon type';
        }

        return 'Special achievement earned';
    }

    // Process the medal queue to display medals sequentially
    _processMedalQueue(dt) {
        // If we're currently processing a medal
        if (this._processingMedal) {
            this._currentMedalDisplayTime += dt;

            // If current medal has been displayed long enough
            if (this._currentMedalDisplayTime >= this._medalDisplayDuration) {
                this._clearMedal();
                this._processingMedal = false;
                this._currentMedalDisplayTime = 0;
                this._medalDelayTimer = 0; // Start delay timer
            }
        }
        // If we're in delay between medals
        else if (this._medalDelayTimer < this._medalDelay) {
            this._medalDelayTimer += dt;
        }
        // If we're ready to show the next medal and have medals in queue
        else if (this._medalQueue.length > 0) {
            const nextMedal = this._medalQueue.shift(); // Remove first medal from queue
            this._medalQueueSet.delete(nextMedal);
            this._showMedal(nextMedal);
            this._processingMedal = true;
            this._currentMedalDisplayTime = 0;
        }

        // If game has ended and medal queue is empty, show game summary
        if (this._gameEnded && this._medalQueue.length === 0 && !this._processingMedal) {
            this._gameEnded = false; // Reset flag
            const winnerId = this._pendingWinnerId;
            const winnerKills = this._pendingWinnerKills;
            this._pendingWinnerId = null;
            this._pendingWinnerKills = 0;
            this._startFade(1, [0, 0, 0], 0.5, () => {
                this._showSummaryContent(winnerId, winnerKills);
            });
        }
    }

    // Play medal sound based on medal name
    playMedalSound(medalName) {
        let soundToPlay = null;

        switch (medalName) {
            case 'DOUBLE KILL!':
                soundToPlay = this.doubleKillSound;
                break;
            case 'TRIPLE KILL!':
                soundToPlay = this.tripleKillSound;
                break;
            case 'QUAD KILL!':
                soundToPlay = this.quadKillSound;
                break;
            case 'PENTA KILL!':
                soundToPlay = this.pentaKillSound;
                break;
            case 'MERCILESS!':
                soundToPlay = this.mercilessSound;
                break;
            case 'RAMPAGE!':
                soundToPlay = this.rampageSound;
                break;
            case 'UNTOUCHABLE!':
                soundToPlay = this.untouchableSound;
                break;
            case 'CLOSE QUARTERS!':
                soundToPlay = this.closeQuartersSound;
                break;
            case 'SNIPER ELITE!':
                soundToPlay = this.sniperEliteSound;
                break;
            case 'LONG SHOT!':
                soundToPlay = this.longShotSound;
                break;
            case 'LAST STAND!':
                soundToPlay = this.lastStandSound;
                break;
            case 'PISTOL SPECIALIST!':
                soundToPlay = this.pistolSpecialistSound;
                break;
            case 'RIFLE SPECIALIST!':
                soundToPlay = this.rifleSpecialistSound;
                break;
            case 'SHOTGUN SPECIALIST!':
                soundToPlay = this.shotgunSpecialistSound;
                break;
            case 'SNIPER SPECIALIST!':
                soundToPlay = this.sniperSpecialistSound;
                break;
            default:
                // Handle any other specialist medals as fallback
                if (medalName.includes('SPECIALIST!')) {
                    soundToPlay = this.pistolSpecialistSound; // Use pistol as fallback
                }
                break;
        }

        // Play the specific medal sound if available
        if (soundToPlay?.play) {
            try {
                soundToPlay.play();
            } catch (error) {
                console.error(`[MEDAL SOUND] Error playing ${medalName}:`, error);
            }
        } else if (medalName === 'MERCILESS!' && this.mercilessSoundObject) {
            // Try to reinitialize merciless sound if missing
            this.mercilessSound = this.mercilessSoundObject.getComponent(HowlerAudioSource);
            if (this.mercilessSound?.play) {
                try {
                    this.mercilessSound.play();
                } catch (error) {
                    console.error(`[MERCILESS SOUND] Reinit failed:`, error);
                }
            }
        }

        // Always play the general medal achievement sound alongside the specific medal sound
        if (this.medalAchievementSound?.play) {
            try {
                this.medalAchievementSound.play();
            } catch (error) {
                console.error(`[Unlock Sound] Error playing medal achievement sound:`, error);
            }
        }
    }

    // Check for multi-kill medals (both player and bot kills count)
    checkMultiKillMedals() {
        const now = this._elapsedMs;
        this._killTimestamps.push(now);

        // Remove kills older than 16 seconds using index pointer (avoids O(n) shift)
        while (this._killTsStart < this._killTimestamps.length && now - this._killTimestamps[this._killTsStart] > 16000) {
            this._killTsStart++;
        }
        // Compact when pointer drifts far ahead
        if (this._killTsStart > 20) {
            this._killTimestamps.splice(0, this._killTsStart);
            this._killTsStart = 0;
        }

        // QUEST 2 OPTIMIZATION: Reference the tail of the array directly instead of .slice() allocation
        const recentKills = this._killTimestamps;
        const rkEnd = recentKills.length;
        const rkLen = rkEnd - this._killTsStart;

        // Check and award each medal progressively - must achieve previous level first
        let medalAwarded = false;

        // Double Kill: last 2 kills within 4 seconds (no prerequisite)
        if (rkLen >= 2 && recentKills[rkEnd - 1] - recentKills[rkEnd - 2] <= 4000 && !this._awardedMultiKillMedals.has('DOUBLE KILL!')) {
            this.displayMedal('DOUBLE KILL!');
            this._awardedMultiKillMedals.add('DOUBLE KILL!');
            medalAwarded = true;
        }

        // Triple Kill: last 3 kills within 8 seconds AND double kill already awarded
        if (rkLen >= 3 && recentKills[rkEnd - 1] - recentKills[rkEnd - 3] <= 8000 &&
            this._awardedMultiKillMedals.has('DOUBLE KILL!') && !this._awardedMultiKillMedals.has('TRIPLE KILL!')) {
            this.displayMedal('TRIPLE KILL!');
            this._awardedMultiKillMedals.add('TRIPLE KILL!');
            medalAwarded = true;
        }

        // Quad Kill: last 4 kills within 12 seconds AND triple kill already awarded
        if (rkLen >= 4 && recentKills[rkEnd - 1] - recentKills[rkEnd - 4] <= 12000 &&
            this._awardedMultiKillMedals.has('TRIPLE KILL!') && !this._awardedMultiKillMedals.has('QUAD KILL!')) {
            this.displayMedal('QUAD KILL!');
            this._awardedMultiKillMedals.add('QUAD KILL!');
            medalAwarded = true;
        }

        // Penta Kill: last 5 kills within 16 seconds AND quad kill already awarded
        if (rkLen >= 5 && recentKills[rkEnd - 1] - recentKills[rkEnd - 5] <= 16000 &&
            this._awardedMultiKillMedals.has('QUAD KILL!') && !this._awardedMultiKillMedals.has('PENTA KILL!')) {
            this.displayMedal('PENTA KILL!');
            this._awardedMultiKillMedals.add('PENTA KILL!');
            medalAwarded = true;
        }

        // Reset timer if any medal was awarded
        if (medalAwarded) {
            this._multiKillResetTimer = 4.0; // Reset after 4 seconds from last kill
        }
    }
    checkWeaponMedals(weaponType) {
        // Track kills per weapon type for Weapon Specialist
        if (!this._currentWeaponKills.has(weaponType)) {
            this._currentWeaponKills.set(weaponType, 0);
        }
        this._currentWeaponKills.set(weaponType, this._currentWeaponKills.get(weaponType) + 1);

        const currentCount = this._currentWeaponKills.get(weaponType);

        // Check for Weapon Specialist: 5 kills with same weapon
        if (currentCount >= 5) {
            const weaponName = weaponType === 'default' ? 'Pistol' : weaponType.charAt(0).toUpperCase() + weaponType.slice(1);
            this.displayMedal(`${weaponName.toUpperCase()} SPECIALIST!`);
            this._currentWeaponKills.set(weaponType, 0); // Reset to prevent spam
        }

        // Check for Merciless: 5 consecutive kills without switching weapons
        if (this._lastKillWeapon === weaponType) {
            this._consecutiveSameWeaponKills++;
        } else {
            this._consecutiveSameWeaponKills = 1;
            this._lastKillWeapon = weaponType;
        }

        if (this._consecutiveSameWeaponKills >= 10) {
            this.displayMedal('MERCILESS!');
            this._consecutiveSameWeaponKills = 0; // Reset to prevent spam
        }
    }

    // Check kill streak medals
    checkKillStreakMedals() {
        this._currentKillStreak++;

        // Check for specific streak milestones
        if (this._currentKillStreak === 10) {
            this.displayMedal('RAMPAGE!');
        } else if (this._currentKillStreak === 12) {
            this.displayMedal('UNTOUCHABLE!');
        }
    }

    // Check distance and situational medals
    checkSituationalMedals(weaponType, distance) {
        // Close Quarters: Kill within 6 meters with shotgun (case-insensitive weapon type check)
        if (weaponType && weaponType.toLowerCase().includes('shotgun') && distance && distance <= 6.0) {
            this.displayMedal('CLOSE QUARTERS!');
        }

        // Sniper Elite: Kill from 100+ meters (if we have distance tracking)
        if (weaponType === 'sniper' && distance && distance >= 100.0) {
            this.displayMedal('SNIPER ELITE!');
        }

        // Long Shot Kill: Kill from very far distance
        if (distance && distance >= 75.0) {
            this.displayMedal('LONG SHOT!');
        }
    }

    // Reset medal tracking on death
    resetMedalTracking() {
        this._currentKillStreak = 0;
        this._killTimestamps = [];
        this._killTsStart = 0;
        this._lastKillWeapon = null;
        this._consecutiveSameWeaponKills = 0;
        this._currentWeaponKills.clear();
        this._awardedMultiKillMedals.clear();
        this._multiKillResetTimer = 0;

        // Clear medal queue and reset processing state
        this._medalQueue.length = 0;
        this._processingMedal = false;
        this._currentMedalDisplayTime = 0;
        this._medalDelayTimer = 0;
        this._recentMedals.clear();
        this._unlockDetails.clear();
        this._clearMedal();

    }

    playerDied() {
        // Don't reset weapons here - respawn() will handle optimal loadout setup
        // This prevents double weapon resets during death/respawn cycle

        // Reset kill streak on death — RAMPAGE/UNTOUCHABLE require kills *without dying*
        this._currentKillStreak = 0;
        // Reset consecutive same-weapon kills (merciless streak breaks on death)
        this._consecutiveSameWeaponKills = 0;
        this._lastKillWeapon = null;
    }

    _updateAutoHeal(dt) {
        if (this._isDead || !Number.isFinite(this.health) || this.health <= 0 || this.health >= 100) return;

        this._healTimer += dt;
        if (this._healTimer < this._healDelay) return;

        // Block heal only while actively sprinting (burning stamina). Walking,
        // standing still, or moving with sprint locked-out all allow regen.
        // Reading the sprint state directly avoids false positives from a
        // velocity-magnitude heuristic (momentum after sprint, jump-pad boosts,
        // higher editor speed values, slope adjustments, etc.).
        const pm = this.playerMovement;
        if (pm && pm._isDraining) return;

        this.health = Math.min(100, this.health + this._healRate * dt);
        if (this.healthBar) {
            this._healthBarScale[0] = Math.max(0, 0.5 * (this.health / 100));
            this.healthBar.setScalingLocal(this._healthBarScale);
        }
    }

    playerHit(shooterId, weaponType = 'default', critMultiplier = 1.0, hitPos = null) {
        if (this.inSummary || this._gameEnded) return;
        const canTakeDamage = !this._isImmortal;
        if (this.health <= 0 || this._isDead) return; // Already dead, ignore further hits

        // Friendly-fire gate: ONLY block when we can positively confirm both
        // shooter and victim are on the same team. If either team lookup
        // fails (transient state during join/mode-switch/teamUpdate races),
        // treat as hostile and let the damage through — otherwise an
        // unsynced team map silently makes every player invulnerable, which
        // is exactly the "can't shoot the other team" symptom in 2v2.
        // Bots are always hostile and bypass the team check entirely.
        const isBotShooter = typeof shooterId === 'string' && shooterId.startsWith('bot');
        if (!this.isChaosMode && !this._botModeActive && !isBotShooter) {
            const shooterTeam = this._getPlayerTeam(shooterId);
            const localTeam = this._getPlayerTeam(this.userId);
            if (shooterTeam && localTeam && shooterTeam === localTeam) {
                // Confirmed teammate — block.
                return;
            }
            // Otherwise (one or both unknown, or different teams): allow damage.
            if (!shooterTeam || !localTeam) {
                console.warn(
                    `[arena-handler] playerHit: incomplete team map ` +
                    `(shooter=${shooterId}→${shooterTeam}, local=${this.userId}→${localTeam}); ` +
                    `treating as hostile`
                );
            }
        }
        // Immortality while dead: do not play getHitSound, do not decline health

        // QUEST 2 OPTIMIZATION: Use static class-level tables (no per-hit object allocation)
        let multiplier = critMultiplier;
        let damage = ArenaHandler.DAMAGE_TABLE[weaponType] ?? 12;
        damage *= multiplier;
        damage = Math.round(damage); // Optionally round to nearest integer

        this.health -= damage;
        this._healTimer = 0; // Reset auto-heal countdown on damage
        this.getHitSound.play();
        // Flash the fading sphere red to indicate damage
        this._startDamageFlash();
        // Health Bar: only update on non-lethal hits (lethal hits skip to full bar on respawn)
        if (this.health > 0) {
            if (this.healthBar) {
                this._healthBarScale[0] = 0.5 * (this.health / 100);
                this.healthBar.setScalingLocal(this._healthBarScale);
            }
            // QUEST 2 OPTIMIZATION: Reuse pre-allocated array for hitPos copy
            if (hitPos) {
                this._hitPosPayload[0] = hitPos[0];
                this._hitPosPayload[1] = hitPos[1];
                this._hitPosPayload[2] = hitPos[2];
            }
            this.lobby.data.sendToAll({
                type: "playerHit",
                payload: {
                    id: this.userId,
                    shooterId: shooterId,
                    hitPos: hitPos ? this._hitPosPayload : null
                }
            });
        }

        if (this.health <= 0) {
            // [ANALYTICS HOOK]
            AnalyticsManager.instance.logDeath();

            this._isDead = true; // Mark as dead to avoid multiple triggers
            try {
                this.getKilledSound.play();

                // Calculate distance if positions are available
                let distance = null;
                if (hitPos && this.player) {
                    this.player.getPositionWorld(this.tempPos);
                    distance = vec3.distance(this.tempPos, hitPos);
                }

                this.lobby.data.sendToAll({
                    type: "playerKill",
                    payload: {
                        victimId: this.userId,
                        killerId: shooterId,
                        weaponType: weaponType,
                        isHeadshot: critMultiplier > 1.0,
                        distance: distance
                    }
                });

                this.deaths++;

                // Reset medal tracking on death
                this.resetMedalTracking();

                // Track local player death in stats
                if (!this.playerStats.has(this.userId)) {
                    this.playerStats.set(this.userId, { kills: 0, deaths: 0 });
                }
                const localStats = this.playerStats.get(this.userId);
                localStats.deaths++;

                this.getRandomSpawn(); // Get a new spawn position
                this.justRespawned = true;
                // Immediately respawn the player at the new spawn position, no fade or timeout
                if (this.playerMovement) {
                    this.playerDied();
                    this.playerMovement.respawn(this.spawnPos, this.spawnRot);
                }
                if (this.victoryText) this.victoryText.active = false;
            } catch (e) {
                console.error('[arena-handler] Error in death handling, forcing respawn:', e);
            } finally {
                // ALWAYS reset health and bar, even if death handling fails
                this.health = 100;
                if (this.healthBar) this.healthBar.setScalingLocal([0.5, 0.01, 0.5]);
                this._isDead = false;
            }
        }

        // console.log(`[arena-handler] Player ${this.userId} hit by ${shooterId}. Health: ${this.health}`);
    }

    /**
     * Trigger a brief red damage flash on the fading sphere.
     * Uses a fast rise + smooth decay curve so the hit feels impactful
     * without blocking the player's view for too long.
     */
    _startDamageFlash() {
        this._dmgFlashActive = true;
        this._dmgFlashElapsed = 0;
    }

    _startFade(targetAlpha, color, duration, callback) {
        // Issue a new fade token so callbacks cannot be lost by overwrites
        this._fadeTokenCounter = (this._fadeTokenCounter || 0) + 1;
        const token = this._fadeTokenCounter;
        this._fadeActiveToken = token;

        this._fadeState = (targetAlpha > this._fadeAlpha) ? 'in' : 'out';
        this._fadeTargetAlpha = targetAlpha;
        // QUEST 2 OPTIMIZATION: Manual copy instead of .slice() allocation
        const c0 = (color && typeof color[0] === 'number') ? color[0] : 0;
        const c1 = (color && typeof color[1] === 'number') ? color[1] : 0;
        const c2 = (color && typeof color[2] === 'number') ? color[2] : 0;
        const c3 = (color && typeof color[3] === 'number') ? color[3] : 1.0;
        this._fadeColor[0] = c0; this._fadeColor[1] = c1;
        this._fadeColor[2] = c2; this._fadeColor[3] = c3;
        this._fadeDuration = duration;
        this._fadeElapsed = 0;
        this._fadeCallback = callback || null;
        this._fadeCallbackToken = this._fadeCallback ? token : null;
    }

    _initBotPool() {
        // console.log('[arena-handler] _initBotPool called');
        // Use botPool property if set, otherwise search the scene
        this._botObjects = [];
        this._botComponents = [];
        if (this.botPool && this.botPool.children) {
            this._botObjects = this.botPool.children;
        } else if (this.engine && this.engine.scene && this.engine.scene.objects) {
            // PERFORMANCE: Use for loop instead of filter for scene object search
            const sceneObjects = this.engine.scene.objects;
            for (let i = 0; i < sceneObjects.length; i++) {
                if (sceneObjects[i].name === 'Bot') {
                    this._botObjects.push(sceneObjects[i]);
                }
            }
        }
        for (let i = 0; i < this._botObjects.length; ++i) {
            const botComp = this._botObjects[i].getComponent && this._botObjects[i].getComponent('bot');
            if (botComp) this._botComponents.push(botComp);
        }
        // console.log('[arena-handler] _initBotPool found', this._botObjects.length, 'bot objects and', this._botComponents.length, 'bot components');
    }
    activateBots() {
        if (!this._botComponents || this._botComponents.length === 0) {
            this._initBotPool();
        }
        // console.log('[arena-handler] Activating bots. _botComponents:', this._botComponents.length, this._botComponents);
        for (let i = 0; i < this._botComponents.length; ++i) {
            const bot = this._botComponents[i];
            if (bot && typeof bot.activate === 'function') bot.activate();
        }
    }
    deactivateBots() {
        if (!this._botComponents) return;
        // console.log('[arena-handler] Deactivating bots. _botComponents:', this._botComponents.length, this._botComponents);
        for (let i = 0; i < this._botComponents.length; ++i) {
            const botComp = this._botComponents[i];
            if (botComp && typeof botComp.deactivate === 'function') botComp.deactivate();
        }
    }

    // Helper to forcefully interrupt any countdown/summary and fade in
    _interruptAndFadeIn(callback) {
        // 1. Kill all pending logic loops
        this._stopCountdown();
        this._clearCountdownTimeouts();

        // 2. Clear state flags that block gameplay
        this.inSummary = false;
        this._gameEnded = false;
        this._isImmortal = true; // Stay immortal until the NEW "Go!" signal

        // 3. Clear UI
        if (this.victoryText) {
            this.victoryText.active = false;
            this.victoryText.text = '';
        }
        if (this.xpDetailText) {
            this.xpDetailText.text = '';
        }

        // Ensure we clear the red damage flash if it was stuck
        this._dmgFlashActive = false;

        // 4. Reset Fade Tokens to prevent old callbacks from firing
        this._fadeCallback = null;
        this._fadeCallbackToken = null;
        this._fadeActiveToken = null;

        // 5. Start the fresh fade
        this._startFade(1, [0, 0, 0], 0.5, () => {
            this._fadeState = 'hold';
            this._fadeAlpha = 1.0;
            if (callback) callback();
        });
    }

    _updateBotMode(players) {
        let playerCount = players ? players.length : 0;
        const wantBotMode = playerCount <= 1;

        if (wantBotMode === this._botModeActive) {
            this.updateKillProgressBar();
            return;
        }

        this._botModeActive = wantBotMode;

        // --- CRITICAL CLEANUP FOR RESTART ---
        // Stop any active multiplayer countdowns or summaries immediately
        this._stopCountdown();
        this.deactivateBots();
        this._matchActive = false;
        this._countdownActive = false;
        this.inSummary = false;
        this._gameEnded = false; // Reset game ended flag

        // Interrupt immediately to prevent overlapping transitions
        if (this._modeTransitionTimer != null) {
            clearTimeout(this._modeTransitionTimer);
            this._modeTransitionTimer = null;
        }

        if (wantBotMode) {
            // Switching to Bot Mode (Solo)
            this._interruptAndFadeIn(() => {
                // Force reset stats so the bot match doesn't start with old kills
                this._resetPlayerStateForCountdown();

                if (this.victoryText) {
                    this.victoryText.text = 'Bot Mode Activated';
                    this.victoryText.color = this._cyanColor;
                    this.victoryText.active = true;
                }

                this._modeTransitionTimer = this._guardedTimeout(() => {
                    this._modeTransitionTimer = null;
                    if (this.victoryText) {
                        this.victoryText.active = false;
                    }

                    // Force start the match countdown now that we are solo
                    if (this._setupFlag) {
                        this.startMatchCountdown();
                    }
                }, 3000);

                this.updateKillProgressBar();
            });
        } else {
            // Switching to Real Player Mode
            this._interruptAndFadeIn(() => {
                this.deactivateBots();

                if (this.victoryText) {
                    const modeLabel = this._getCurrentGameModeLabel() || 'Game Mode';
                    this.victoryText.text = modeLabel;
                    this.victoryText.color = this._goldColor;
                    this.victoryText.active = true;
                }

                this._modeTransitionTimer = this._guardedTimeout(() => {
                    this._modeTransitionTimer = null;
                    if (this.victoryText) {
                        this.victoryText.active = false;
                    }
                    if (this._setupFlag) {
                        this.startMatchCountdown();
                    }
                }, 4000);
            });
        }
    }
    // ─── CHAOS MODE (FFA) ─────────────────────────────────────────────
    // ─── Game Mode Labels ──────────────────────────────────────────
    // Players | Mode           | Constellation
    //    1    | PvE Training   | 1 vs Bots
    //    2    | Duel           | 1 vs 1
    //    3    | Chaos          | 1v1v1 (FFA)
    //    4    | Skirmish       | 2 vs 2
    //    5    | Chaos          | 5-Player FFA
    //    6    | Battle         | 3 vs 3
    //    7    | Chaos          | 7-Player FFA
    //    8    | War            | 4 vs 4

    // GAME_MODE_TABLE optimized as an indexed array for faster numeric lookups
    // Index = player count. Index 0 is unused (null) so indexes align with counts.
    static GAME_MODE_TABLE = [
        null,
        { name: 'PvE Training', chaos: false, label: 'PvE Training', killsToWin: 25, xpPerKill: 75 },
        { name: 'Duel', chaos: false, label: 'Duel 1 vs 1', killsToWin: 7, xpPerKill: 100 },
        { name: 'Chaos', chaos: true, label: 'Chaos 1v1v1', killsToWin: 10, xpPerKill: 100 },
        { name: 'Skirmish', chaos: false, label: 'Skirmish  2 vs 2', killsToWin: 15, xpPerKill: 100 },
        { name: 'Chaos', chaos: true, label: 'Chaos 5-Player FFA', killsToWin: 18, xpPerKill: 100 },
        { name: 'Battle', chaos: false, label: 'Battle  3 vs 3', killsToWin: 20, xpPerKill: 100 },
        { name: 'Chaos', chaos: true, label: 'Chaos 7-Player FFA', killsToWin: 25, xpPerKill: 100 },
        { name: 'ArenaWars', chaos: false, label: 'ArenaWars  4 vs 4', killsToWin: 30, xpPerKill: 100 },
    ];

    // Derived lookup arrays for hot-path access (boolean and simple scalars)
    static GAME_MODE_CHAOS = ArenaHandler.GAME_MODE_TABLE.map(m => !!(m && m.chaos));
    static GAME_MODE_LABEL = ArenaHandler.GAME_MODE_TABLE.map(m => (m ? (m.label || m.name || '') : ''));
    static GAME_MODE_KILLS = ArenaHandler.GAME_MODE_TABLE.map(m => (m ? (m.killsToWin || 0) : 0));
    static GAME_MODE_XP = ArenaHandler.GAME_MODE_TABLE.map(m => (m ? (m.xpPerKill || 0) : 0));

    /**
     * Check the current player count and toggle the game mode accordingly.
     * Odd counts (3/5/7) activate Chaos Mode (FFA); even counts are team-based.
     */
    updateGameMode(playersList) {
        const playerArr = Array.isArray(playersList) ? playersList : Object.values(playersList);
        const playerCount = playerArr.length;

        const wantChaos = (ArenaHandler.GAME_MODE_CHAOS && ArenaHandler.GAME_MODE_CHAOS[playerCount]) || false;
        const mode = (ArenaHandler.GAME_MODE_TABLE && ArenaHandler.GAME_MODE_TABLE[playerCount]) || null;

        if (wantChaos) {
            if (!this.isChaosMode) {
                this.isChaosMode = true;
                this.triggerChaosUI();
            }
            this._applyChaosAppearances(playerArr);
            this._setTeamNameLabel(this.getLocalPlayerName(), null);
        } else {
            if (this.isChaosMode) {
                this.isChaosMode = false;
                this.triggerTeamUI();
            }
            // Update team map based on current players and their assigned teams with override to ensure broadcasted team map is correct
            this._applyTeamAppearances(playerArr, true); // true = override synced team map
            // Broadcast authoritative team map to all clients to avoid transient mismatches
            try {
                if (this.lobby && this.lobby.data && typeof this.lobby.data.sendToAll === 'function') {
                    const payload = { teams: Array.from(this._teamMap.entries()), generatedAt: performance.now() };
                    // record timestamp for our own acceptance guard
                    this._lastTeamUpdateTs = payload.generatedAt;
                    this.lobby.data.sendToAll({ type: 'teamUpdate', payload: payload });
                }
            } catch (e) {
                console.warn('[arena-handler] Failed to broadcast teamUpdate', e);
            }
            if (playerCount <= 1) {
                this._setTeamNameLabel(this.getLocalPlayerName(), null);
            } else {
                const myTeam = this._teamMap.get(this.userId);
                this._setTeamNameLabel(
                    myTeam === 'A' ? 'Team Blue' : myTeam === 'B' ? 'Team Red' : this.getLocalPlayerName(),
                    myTeam
                );
            }
        }

        // Apply per-mode kill target and XP config
        const newKillsToWin = mode ? mode.killsToWin : 15;
        const oldKillsToWin = this._killsToWin;
        this._xpPerKill = mode ? mode.xpPerKill : 100;

        // Scale everyone's kills proportionally when the target changes mid-match
        if (newKillsToWin !== oldKillsToWin && this._matchActive) {
            this._scaleKillsForModeSwitch(oldKillsToWin, newKillsToWin);
        }
        this._killsToWin = newKillsToWin;
        this.updateKillProgressBar();
        if (this.killsText) {
            this.killsText.text = this.kills.toString() + '/' + this._killsToWin + ' Kills';
        }

        this._setGameModeIndicator(mode ? mode.name : '');

        // Immediate win check after mode switch (scaling may push someone over)
        if (this._matchActive && !this.inSummary) {
            this._checkWinCondition();
        }
    }

    /** Show a brief on-screen notification that Chaos Mode (FFA) is active. */
    triggerChaosUI() {
        // Don't overwrite victory/summary text
        if (this.inSummary || this._gameEnded) return;
        if (this._modeNotifTimeout) clearTimeout(this._modeNotifTimeout);
        if (this.victoryText) {
            this.victoryText.text = 'CHAOS MODE ACTIVATED:\nFriendly Fire ON — Every player for themselves!';
            this.victoryText.active = true;
            this._modeNotifTimeout = this._guardedTimeout(() => {
                if (this.victoryText && !this.inSummary && !this._gameEnded) {
                    this.victoryText.active = false;
                    this.victoryText.text = '';
                }
                this._modeNotifTimeout = null;
            }, 4000);
        }
        console.log('[arena-handler] Chaos Mode ON — FFA enabled');
    }

    /** Revert the on-screen notification back to standard team play. */
    triggerTeamUI() {
        // Don't overwrite victory/summary text
        if (this.inSummary || this._gameEnded) return;
        if (this._modeNotifTimeout) clearTimeout(this._modeNotifTimeout);
        if (this.victoryText) {
            this.victoryText.text = 'Teams Restored:\nStandard team rules are back in effect.';
            this.victoryText.active = true;
            this._modeNotifTimeout = this._guardedTimeout(() => {
                if (this.victoryText && !this.inSummary && !this._gameEnded) {
                    this.victoryText.active = false;
                    this.victoryText.text = '';
                }
                this._modeNotifTimeout = null;
            }, 4000);
        }
        console.log('[arena-handler] Chaos Mode OFF — Teams restored');
    }

    /**
     * Update the persistent game-mode indicator.
     * Pass an empty string to hide it.
     * @param {string} label
     */
    _setGameModeIndicator(label) {
        if (!this.gameModeText) return;
        if (label) {
            this.gameModeText.text = label;
            this.gameModeText.active = true;
        } else {
            this.gameModeText.text = '';
            this.gameModeText.active = false;
        }
    }

    /**
     * Update the persistent team/player name label.
     * Shows team name in team modes, player name in chaos/PvE.
     */
    _setTeamNameLabel(label, team) {
        if (!this.teamNameText) return;
        if (label) {
            this.teamNameText.text = label;
            this.teamNameText.active = true;
            // Apply team color material
            if (team === 'A' && this.matTextBlue) {
                this.teamNameText.material = this.matTextBlue;
            } else if (team === 'B' && this.matTextRed) {
                this.teamNameText.material = this.matTextRed;
            } else if (this.matTextDefault) {
                this.teamNameText.material = this.matTextDefault;
            }
        } else {
            this.teamNameText.text = '';
            this.teamNameText.active = false;
            if (this.matTextDefault) {
                this.teamNameText.material = this.matTextDefault;
            }
        }
    }

    /**
     * Compute the total kills for a team from currently-present players.
     * Kills leave with disconnected players automatically since they're
     * removed from playerStats on disconnect.
     * @param {'A'|'B'} team
     * @returns {number}
     */
    getTeamKills(team) {
        let total = 0;
        for (const [id, t] of this._teamMap.entries()) {
            if (t !== team) continue;
            const stats = this.playerStats.get(id);
            if (stats) total += stats.kills;
        }
        return total;
    }

    /**
     * Scale every player's kill count proportionally when _killsToWin changes
     * mid-match (player join/leave triggers a mode switch).
     * Preserves each player's percentage progress toward the goal.
     * Uses playerStats as single source of truth — this.kills is read back
     * from it to prevent rounding desync between the two counters.
     */
    _scaleKillsForModeSwitch(oldTarget, newTarget) {
        if (oldTarget <= 0 || newTarget <= 0) return;
        const ratio = newTarget / oldTarget;

        for (const [id, stats] of this.playerStats.entries()) {
            stats.kills = Math.round(stats.kills * ratio);
        }
        // Derive local kills from playerStats (single source of truth)
        const localStats = this.playerStats.get(this.userId);
        if (localStats) {
            this.kills = localStats.kills;
        } else {
            this.kills = Math.round(this.kills * ratio);
        }
    }

    /**
     * Unified win-condition check. Call after kills change or mode switches.
     * - Team modes: first team to reach _killsToWin (sum of present members' kills).
     * - Chaos / Duel / Bot: individual kills >= _killsToWin.
     */
    _checkWinCondition() {
        if (!this._matchActive || this.inSummary || this._gameEnded) return;

        const isTeamMode = !this.isChaosMode && !this._botModeActive && this._teamMap.size > 0;

        if (isTeamMode) {
            const teamAKills = this.getTeamKills('A');
            const teamBKills = this.getTeamKills('B');

            if (teamAKills >= this._killsToWin || teamBKills >= this._killsToWin) {
                const winningTeam = teamAKills >= this._killsToWin ? 'A' : 'B';
                const winKills = winningTeam === 'A' ? teamAKills : teamBKills;

                // Find the top killer on the winning team for the summary
                let topKillerId = this.userId;
                let topKills = 0;
                for (const [id, t] of this._teamMap.entries()) {
                    if (t !== winningTeam) continue;
                    const stats = this.playerStats.get(id);
                    if (stats && stats.kills > topKills) {
                        topKills = stats.kills;
                        topKillerId = id;
                    }
                }

                this.showGameSummary(topKillerId, winKills);
                if (this.lobby) {
                    this.lobby.data.sendToAll({
                        type: 'playerWin',
                        payload: { id: topKillerId, kills: winKills }
                    });
                }
            }
        } else {
            // FFA / Chaos / Bot: individual kills
            if (this.kills >= this._killsToWin) {
                this.showGameSummary(this.userId, this.kills);
                if (this.lobby) {
                    this.lobby.data.sendToAll({
                        type: 'playerWin',
                        payload: { id: this.userId, kills: this.kills }
                    });
                }
            }
        }
    }

    /**
     * Chaos Mode (odd player counts): assign each player a unique colour
     * derived from their sessionId so it stays stable across list reorders.
     */
    _applyChaosAppearances(playerArr) {
        // Collect which color indices are already taken
        const usedColors = new Set();
        for (const [id, ci] of this._playerColorMap) {
            // Only keep if still in this lobby
            if (playerArr.some(p => (p.sessionId || p.userId) === id)) {
                usedColors.add(ci);
            }
        }
        // Prune stale entries
        for (const id of [...this._playerColorMap.keys()]) {
            if (!playerArr.some(p => (p.sessionId || p.userId) === id)) {
                this._playerColorMap.delete(id);
            }
        }

        for (let i = 0; i < playerArr.length; i++) {
            const id = playerArr[i].sessionId || playerArr[i].userId;
            // FFA: no team allegiance
            this._teamMap.set(id, null);

            // Assign a stable color index if not already assigned
            if (!this._playerColorMap.has(id)) {
                let ci = this._hashSessionId(id) % this._slotMats.length;
                // Resolve collisions: find next free slot
                const total = this._slotMats.length;
                let attempts = 0;
                while (usedColors.has(ci) && attempts < total) {
                    ci = (ci + 1) % total;
                    attempts++;
                }
                this._playerColorMap.set(id, ci);
                usedColors.add(ci);
            }

            const colorIndex = this._playerColorMap.get(id);

            // Remote players only — local player has no visible mesh (first-person)
            const remote = this.remotePlayerMap.get(id);
            if (remote) {
                console.log(`[arena-handler] assignChaosAppearance -> id=${id} ci=${colorIndex}`);
                this.assignChaosAppearance(remote, colorIndex);
            }
        }
        // After assigning/allocating color indices, ensure any already-allocated
        // remote avatars that previously deferred appearance receive the
        // authoritative chaos appearance as well.
        try {
            for (const [rid, remoteObj] of this.remotePlayerMap.entries()) {
                if (!remoteObj) continue;
                const ci = this._playerColorMap.get(rid);
                if (ci != null) this.assignChaosAppearance(remoteObj, ci);
            }
        } catch (e) {
            console.warn('[arena-handler] _applyChaosAppearances: reapply to remotes failed', e);
        }
    }

    /**
     * Team Mode (even player counts — 4v4, 3v3, 2v2): split the lobby in
     * half and paint the first half blue (Team A) and the second half red
     * (Team B).  Preserves existing team assignments for players who already
     * have one, and only assigns new players to whichever team has fewer.
     */
    _applyTeamAppearances(playerArr, rebuildMap = true) {
        if (rebuildMap) {
            // Collect all current session IDs and sort for determinism
            const sorted = [];
            for (let i = 0; i < playerArr.length; i++) {
                sorted.push(playerArr[i].sessionId || playerArr[i].userId);
            }
            sorted.sort();

            // Rebuild _teamMap from scratch — even indices = A (Blue), odd = B (Red)
            this._teamMap.clear();
            for (let i = 0; i < sorted.length; i++) {
                this._teamMap.set(sorted[i], i % 2 === 0 ? 'A' : 'B');
            }
        }

        // Apply materials — set diffuseColor on per-slot body material based on CURRENT this._teamMap
        for (let i = 0; i < playerArr.length; i++) {
            const id = playerArr[i].sessionId || playerArr[i].userId;
            const team = this._teamMap.get(id);
            if (!team) continue; // Skip if they don't have a team assigned yet

            const isTeamA = team === 'A';
            const color = isTeamA
                ? this._chaosHexColors[1]   // Blue
                : this._chaosHexColors[0];  // Red

            // Remote players only — local player has no visible mesh (first-person)
            const remote = this.remotePlayerMap.get(id);
            if (remote) {
                this.assignTeamAppearance(remote, team);
            }
        }
    }

    /**
     * Simple deterministic hash for a sessionId string → unsigned integer.
     * Uses djb2 algorithm for fast, well-distributed output.
     */
    _hashSessionId(id) {
        let hash = 5381;
        for (let i = 0; i < id.length; i++) {
            hash = ((hash << 5) + hash + id.charCodeAt(i)) >>> 0;
        }
        return hash;
    }

    /**
     * Look up a player's game-logic team from _teamMap (assigned by _applyTeamAppearances).
     * Returns the team string ('A'/'B') or null if unknown.
     */
    _getPlayerTeam(sessionId) {
        if (!this._teamMap) return null;
        return this._teamMap.get(sessionId) || null;
    }

    /**
     * Return the current game mode label (derived from player count).
     * Falls back to an empty string if unknown.
     */
    _getCurrentGameModeLabel() {
        try {
            const playerArr = Array.isArray(this._latestPlayerList) ? this._latestPlayerList : (this._latestPlayerList ? Object.values(this._latestPlayerList) : []);
            const playerCount = playerArr.length;
            return (ArenaHandler.GAME_MODE_LABEL && ArenaHandler.GAME_MODE_LABEL[playerCount]) || '';
        } catch (e) {
            return '';
        }
    }

    /**
     * Apply the correct appearance (team or chaos) to a single player object
     * based on the current mode state. Call after allocating a new remote.
     */
    _applyAppearanceToPlayer(id, playerObject) {
        if (!playerObject) return;
        if (this.isChaosMode) {
            const colorIndex = this._playerColorMap.get(id);
            if (colorIndex != null) {
                this.assignChaosAppearance(playerObject, colorIndex);
            }
        } else {
            let team = this._teamMap.get(id);
            if (!team) {
                // Defer appearance if we don't yet have an authoritative team.
                // Defaulting here can hide race conditions where the team
                // assignment arrives later (teamUpdate). We'll wait for the
                // authoritative update to reapply visuals.
                console.warn(`[arena-handler] _applyAppearanceToPlayer: no team for ${id}, deferring appearance`);
                return;
            }
            // Delegate to centralized team appearance assignment
            this.assignTeamAppearance(playerObject, team);
        }
    }

    /**
     * Assign a unique chaos material + name-tag colour to a player object.
     * @param {Object} playerObject - The WLE scene object representing the player
     * @param {number} colorIndex   - 0-based colour index (from _playerColorMap)
     */
    assignChaosAppearance(playerObject, colorIndex) {
        if (!playerObject) return;
        const slotMat = this._getPlayerSlotMaterial(playerObject);
        if (!slotMat) return;
        // Apply chaos material color derived from index
        const color = this.getHexForIndex(colorIndex);
        slotMat.setDiffuseColor(color);
        // Apply slot material only to the 3rd MeshComponent on the
        // child object named 'PlayerMesh' (index 2). Rely on editor
        // layout: PlayerMesh contains 4 MeshComponents.
        if (playerObject.children) {
            for (const child of playerObject.children) {
                if (child.name === 'PlayerMesh' && child.getComponents) {
                    const meshes = child.getComponents(MeshComponent);
                    if (meshes.length > 2 && meshes[2]) meshes[2].material = slotMat;
                    break;
                }
            }
        }

        // Update the name-tag material to the default (neutral) text material
        const nameTag = playerObject.getComponent(TextComponent);
        if (nameTag) {
            nameTag.material = this.matTextDefault;
        } else if (playerObject.children) {
            for (const child of playerObject.children) {
                if (child.name === 'PlayerNameFront' || child.name === 'PlayerNameBack') {
                    const tag = child.getComponent(TextComponent);
                    if (tag) tag.material = this.matTextDefault;
                }
            }
        }
    }

    /**
     * Apply team appearance (A/B) to a player object.
     * @param {Object} playerObject
     * @param {'A'|'B'} team
     */
    assignTeamAppearance(playerObject, team) {
        if (!playerObject) return;
        const slotMat = this._getPlayerSlotMaterial(playerObject);
        if (slotMat) {
            const isTeamA = team === 'A';
            const color = isTeamA ? this._chaosHexColors[1] : this._chaosHexColors[0];
            slotMat.setDiffuseColor(color);
        }
        // Apply slot material only to the PlayerMesh child's 3rd MeshComponent
        if (slotMat && playerObject.children) {
            for (const child of playerObject.children) {
                if (child.name === 'PlayerMesh' && child.getComponents) {
                    const meshes = child.getComponents(MeshComponent);
                    if (meshes.length > 2 && meshes[2]) meshes[2].material = slotMat;
                    break;
                }
            }
        }

        if (playerObject.children) {
            for (const child of playerObject.children) {
                if (child.name === 'PlayerNameFront' || child.name === 'PlayerNameBack') {
                    const tag = child.getComponent(TextComponent);
                    if (tag) tag.material = team === 'A' ? this.matTextBlue : this.matTextRed;
                }
            }
        } else {
            const nameTag = playerObject.getComponent(TextComponent);
            if (nameTag) nameTag.material = team === 'A' ? this.matTextBlue : this.matTextRed;
        }
    }

    /**
     * Return the RGBA colour array for a given chaos material index.
     * @param {number} index
     * @returns {number[]}
     */
    getHexForIndex(index) {
        return this._chaosHexColors[index % this._chaosHexColors.length];
    }

    // Debug: Show all unlocks sequence for testing
    _debugShowAllUnlocks() {
        console.log('[Debug] Showing full progression unlock sequence');

        // Import ProgressionSystem if not already available
        const ProgressionSystem = (typeof window !== 'undefined' && window.ProgressionSystem) || require('./progression-system.js').ProgressionSystem;

        const allUnlocks = [];

        // Simulate progression through each prestige level in chronological order
        for (let prestige = 0; prestige <= ProgressionSystem.MAX_PRESTIGE; prestige++) {
            // Show level-based unlocks for this prestige (levels 1-25)
            for (let level = 1; level <= ProgressionSystem.MAX_LEVEL; level++) {
                // Always use the explicit order for unlocks
                for (const unlockKey of ProgressionSystem.ORDERED_UNLOCK_KEYS) {
                    const baseLevel = ProgressionSystem.BASE_UNLOCK_LEVELS[unlockKey];
                    if (baseLevel === 999) continue;
                    // Skip if already unlocked by prestige reward
                    const prestigeRewards = ProgressionSystem.PRESTIGE_REWARDS[prestige];
                    if (prestigeRewards && prestigeRewards.includes(unlockKey)) continue;
                    // Calculate adjusted unlock level for current prestige
                    const shiftAmount = prestige * 2;
                    const adjustedUnlockLevel = Math.max(1, baseLevel - shiftAmount);
                    if (adjustedUnlockLevel === level) {
                        allUnlocks.push({
                            level: `P${prestige + 1} L${level}`,
                            description: ProgressionSystem.getUnlockDescription(unlockKey)
                        });
                    }
                }
            }
            // After completing levels in this prestige, show prestige rewards for next prestige
            if (prestige < ProgressionSystem.MAX_PRESTIGE) {
                const nextPrestige = prestige + 1;
                const rewards = ProgressionSystem.getPrestigeRewards(nextPrestige);
                for (const rewardKey of rewards) {
                    allUnlocks.push({
                        level: `Prestige ${nextPrestige}`,
                        description: ProgressionSystem.getUnlockDescription(rewardKey)
                    });
                }
            }
        }

        // Debug: Log the unlocks to verify order
        console.log('[Debug] All unlocks in order:', allUnlocks);

        // Queue all unlocks for display in the order they were earned
        this.showUnlockSequence(allUnlocks);
    }
    // DEBUG: Kill the player after 15 seconds to test death/reset logic
    _startDebugBotMatch() {
        if (this._debugBotMatchInterval) return; // Already running
        let debugKills = 0;
        this._debugBotMatchInterval = setInterval(() => {
            if (!this._botModeActive || this.kills >= this._killsToWin) {
                clearInterval(this._debugBotMatchInterval);
                this._debugBotMatchInterval = null;
                return;
            }
            // Find a live bot
            const bot = this._botComponents && this._botComponents.find(b => b && !b.isDead);
            if (bot) {
                // Simulate a bullet hit from the player to the bot so the kill is counted
                const botPos = bot.object.getPositionWorld ? bot.object.getPositionWorld([]) : [0, 0, 0];
                this.handleBulletSpawn({
                    id: this.userId,
                    pos: botPos,
                    linVel: [0, 0, 1], // arbitrary direction
                    t: this._elapsedMs,
                    weaponType: 'rifle' // or any weapon type
                });
            }
        }, 200);
    }

    startMatchCountdown() {
        this._stopCountdown();
        this._updateXpBoostStatus();
        this._resetPlayerStateForCountdown();
        this._countdownActive = true;
        this._inCountdown = true;
        this._isImmortal = true;
        if (!this._botModeActive) this.deactivateBots();
        else this.activateBots();
        for (const [id, remote] of this.remotePlayerMap.entries()) {
            if (remote) {
                remote.justRespawnedTimer = 10.0; // 10 second countdown during game start
            }
        }
        if (!this.victoryText) {
            console.warn('[arena-handler] victoryText is not assigned, UI will be skipped but logic will proceed.');
        }
        this._countdownSteps = [
            'Game starts in 3',
            '2',
            '1',
            'Go!'
        ];
        this._countdownStepIndex = 0;
        // 1. Get the current player count from the latest cached player list
        const playerArr = Array.isArray(this._latestPlayerList) ? this._latestPlayerList : (this._latestPlayerList ? Object.values(this._latestPlayerList) : []);
        const playerCount = playerArr.length;

        // 2. Determine color: Cyan for 1 player (Bot Mode), Gold for 2+ (Real Players)
        // You can use the pre-allocated color vectors from the class
        const activeColor = (playerCount <= 1) ? this._cyanColor : this._goldColor;

        if (this.victoryText) {
            this.victoryText.color = activeColor;
        }

        // Screen should already be faded to black from summary, just ensure it's held
        this._fadeState = 'hold';
        this._fadeAlpha = 1.0;
        this._startCountdownSteps();
    }

    _startCountdownSteps() {
        this._clearCountdownTimeouts();
        this._countdownActive = true;
        const steps = this._countdownSteps || [
            'Game starts in 3',
            '2',
            '1',
            'Go!'
        ];
        let i = 0;
        // 1. Get the current player count from the latest cached player list
        const playerArr = Array.isArray(this._latestPlayerList) ? this._latestPlayerList : (this._latestPlayerList ? Object.values(this._latestPlayerList) : []);
        const playerCount = playerArr.length;

        // 2. Determine color: Cyan for 1 player (Bot Mode), Gold for 2+ (Real Players)
        // You can use the pre-allocated color vectors from the class
        const activeColor = (playerCount <= 1) ? this._cyanColor : this._goldColor;

        if (this.victoryText) {
            this.victoryText.active = true;
            this.victoryText.color = activeColor;
        }

        const showStep = () => {
            if (!this._countdownActive) return;
            // State reset + respawn already handled by _resetPlayerStateForCountdown() in startMatchCountdown()
            if (this.victoryText) {
                this.victoryText.active = true;
                this.victoryText.text = steps[i];
            }
            if (i < steps.length - 1) {
                const gen = this._sessionGen;
                const t = setTimeout(() => {
                    if (this._sessionGen !== gen) return;
                    if (!this._countdownActive) return;
                    i++;
                    showStep();
                }, 1000);
                this._countdownTimeouts.push(t);
            } else {
                const gen = this._sessionGen;
                const t = setTimeout(() => {
                    if (this._sessionGen !== gen) return;
                    if (!this._countdownActive) return;
                    this.getRandomSpawn();
                    if (this.playerMovement) {
                        this.playerMovement.respawn(this.spawnPos, this.spawnRot);
                    }
                    this.justRespawned = true; // Alerts network to sync position immediately
                    this._fadeAlpha = 1.0;
                    this.inSummary = false;
                    this._isImmortal = false;
                    this._elapsedMs = 0;
                    // Only now, on Go!, reset weapons and start bot timer if in bot mode
                    if (this.playerMovement && typeof this.playerMovement.resetWeapons === 'function') {
                        this.playerMovement.resetWeapons();
                    }
                    for (const [id, remote] of this.remotePlayerMap.entries()) {
                        if (remote && remote.justRespawnedTimer) {
                            remote.justRespawnedTimer = 0;
                        }
                    }
                    // Only start bot timer on Go!
                    if (this._botModeActive) {
                        this.activateBots();
                        this._botSpeedrunStart = this._elapsedMs;
                        this._botSpeedrunEnd = null;
                    } else if (this.progressionText) {
                        this.progressionText.text = '';
                    }
                    this._matchActive = true;
                    
                    // [ANALYTICS HOOK]
                    AnalyticsManager.instance.logMatchStart(this._botModeActive ? GameMode.BOT : GameMode.REAL_PLAYER);

                    this._startFade(0, [0, 0, 0], 1.0, () => {
                        if (!this._countdownActive) return;
                        if (this.victoryText) {
                            this.victoryText.active = false;
                            this.victoryText.text = '';
                        }
                        this._inCountdown = false;
                        this._countdownActive = false;
                        this._countdownStartedAt = null;
                    });
                }, 500);
                this._countdownTimeouts.push(t);
            }
        };
        this._countdownTimeouts = [];
        showStep();
    }

    /**
     * Stop any running countdown and clear all state/timeouts
     */
    _stopCountdown() {
        this._countdownActive = false;
        this._inCountdown = false; // CRITICAL: Release input/immortality block
        this._isImmortal = false;
        this._countdownStartedAt = null;
        if (this._countdownTimeouts) {
            for (const t of this._countdownTimeouts) {
                clearTimeout(t);
            }
        }
        this._countdownTimeouts = [];
        if (this.victoryText) {
            this.victoryText.active = false;
            this.victoryText.text = '';
        }
    }

    /**
     * Clear any pending countdown timeouts to prevent overlap
     */
    _clearCountdownTimeouts() {
        if (this._countdownTimeouts) {
            for (const t of this._countdownTimeouts) clearTimeout(t);
        }
        this._countdownTimeouts = [];
    }

    /**
     * Handle the networked real player mode info message and fade
     */
    _handleRealModeInfo(payload) {
        // Skip if a mode transition is already in progress (local _updateBotMode handles it)
        if (this._modeTransitionTimer != null) return;
        if (this.victoryText) {
            this.victoryText.color = this._goldColor;
            // Show the current game mode label instead of a hardcoded string
            const modeLabel = this._getCurrentGameModeLabel() || 'Game Mode';
            this.victoryText.text = modeLabel;
            this.victoryText.active = true;
            // Schedule the countdown to start after the info display (consistent with _updateBotMode)
            if (this._modeTransitionTimer) clearTimeout(this._modeTransitionTimer);
            this._modeTransitionTimer = this._guardedTimeout(() => {
                this._modeTransitionTimer = null;
                // Only start the match if central flags allow it
                if (this._setupFlag && !this._matchActive) {
                    this.startMatchCountdown();
                    return;
                }
                // Clear informational text and restore fade if no countdown started
                this.victoryText.active = false;
                this.victoryText.text = '';
                if (!this._countdownActive && !this._matchActive) {
                    this._startFade(0, [0, 0, 0], 0.5);
                }
            }, 4000);
        }
    }

    /**
     * Handle the networked playerReady message and update ready state
     */
    // REMOVE _handlePlayerReady and all playerReady logic

    /**
     * Set both PlayerNameFront and PlayerNameBack labels for a remote player object
     */
    setRemotePlayerNameLabels(remote, name) {
        if (!remote || !remote.children) return;
        for (let child of remote.children) {
            if (child.name === "PlayerNameFront" || child.name === "PlayerNameBack") {
                const label = child.getComponent(TextComponent);
                if (label) label.text = name;
            }
        }
    }

    /**
     * Set the level display for a remote player object using the LevelSync component
     */
    setRemotePlayerLevel(remote, level) {
        if (!remote) {
            console.warn('[ArenaHandler] setRemotePlayerLevel: remote is null');
            return;
        }
        const levelSync = remote.getComponent('level-sync');
        if (levelSync) {
            levelSync.setLevel(level);
        } else {
            console.warn('[ArenaHandler] No level-sync component found on remote player:', remote.name || 'unnamed');
        }
    }

    /**
     * Set the prestige display for a remote player object using the LevelSync component
     */
    setRemotePlayerPrestige(remote, prestige) {
        if (!remote) {
            console.warn('[ArenaHandler] setRemotePlayerPrestige: remote is null');
            return;
        }
        const levelSync = remote.getComponent('level-sync');
        if (levelSync) {
            levelSync.setPrestige(prestige);
        } else {
            console.warn('[ArenaHandler] No level-sync component found on remote player:', remote.name || 'unnamed');
        }
    }

    /**
     * Get the local player's display name from session, with fallback
     */
    getLocalPlayerName() {
        if (this.session && (this.session.name || this.session.username)) {
            return this.session.name || this.session.username || 'Player';
        }
        // Fallback: try to find in latest player list
        if (this._latestPlayerList && this.userId) {
            // PERFORMANCE: Use for loop instead of find
            for (let i = 0; i < this._latestPlayerList.length; i++) {
                if (this._latestPlayerList[i].sessionId === this.userId) {
                    const localPlayerInfo = this._latestPlayerList[i];
                    return localPlayerInfo.name || localPlayerInfo.username || 'Player';
                }
            }
        }
        return 'Player';
    }

    /**
     * Try to set a player's name if we have their information in the latest player list
     */
    _trySetPlayerName(playerId, remoteObject) {
        if (!this._latestPlayerList || !remoteObject || !remoteObject.children) return;

        // PERFORMANCE: Use for loop instead of find
        for (let i = 0; i < this._latestPlayerList.length; i++) {
            if ((this._latestPlayerList[i].sessionId || this._latestPlayerList[i].userId) === playerId) {
                const playerInfo = this._latestPlayerList[i];
                const playerName = playerInfo.name || playerInfo.username || "Player";
                this.setRemotePlayerNameLabels(remoteObject, playerName);
                return;
            }
        }
    }

    _handleStartCountdown(payload) {
        if (this._countdownActive || this._countdownStartedAt || this._matchActive) return;
        this._countdownActive = true;
        this._countdownStartedAt = payload.startTime;
        // payload.startTime is relative elapsed ms from the sender
        // Display the current game mode label immediately so players see it
        if (this.victoryText) {
            const modeLabel = this._getCurrentGameModeLabel() || 'Game Mode';
            // Determine color: cyan for bot mode (1 or fewer), gold for real players
            const playerArrLocal = Array.isArray(this._latestPlayerList) ? this._latestPlayerList : (this._latestPlayerList ? Object.values(this._latestPlayerList) : []);
            const playerCountLocal = playerArrLocal.length;
            this.victoryText.text = modeLabel;
            this.victoryText.color = (playerCountLocal <= 1) ? this._cyanColor : this._goldColor;
            this.victoryText.active = true;
        }

        const now = this._elapsedMs;
        const delay = Math.max(0, payload.startTime - now);
        this._guardedTimeout(() => {
            this.startMatchCountdown();
        }, delay);
    }

    _resetPlayerStateForCountdown() {
        // Reset all relevant state/UI for a new match/countdown
        this.kills = 0;
        this.deaths = 0;
        this.health = 100;

        // Reset player stats for new match
        this.playerStats.clear();

        // Reset medal tracking (but keep progression unlocks)
        this.resetMedalTracking();
        this.health = 100;
        this._isDead = false;
        this.justRespawned = true;
        this.inSummary = false;
        this.getRandomSpawn();
        if (this.healthBar) this.healthBar.setScalingLocal([0.5, 0.01, 0.5]);
        if (this.killsText) this.killsText.text = '';
        if (this.progressionText) this.progressionText.text = '';
        // Update progression bar based on mode
        if (this.progressionBar) {
            // Normal mode: show XP level progress
            const totalXpForCurrentLevel = ArenaHandler.getTotalXpForLevel(this.level);
            const totalXpForNextLevel = ArenaHandler.getTotalXpForLevel(this.level + 1);
            this.xp = this.totalXp - totalXpForCurrentLevel;
            const xpNeededForNext = totalXpForNextLevel - totalXpForCurrentLevel;
            const progressionScale = Math.min(this.xp / xpNeededForNext, 1) * 0.5;
            this.progressionBar.setScalingLocal([progressionScale, this._progressScale[1], 0.5]);
        }
        // Update level display
        if (this.levelText) {
            this.levelText.text = `${this.level}`;
        }
        if (this.victoryText) {
            this.victoryText.active = false;
            this.victoryText.text = '';
        }
        // Always reset bot timer state
        this._botSpeedrunStart = null;
        this._botSpeedrunEnd = null;
    }

    /**
     * Check if the user is logged in via the Arena REST API.
     * Single attempt — if it fails, fall back to guest / session-based progression.
     */
    async _checkUserLogin() {
        try {
            const name = await Arena.user.getName();
            this._isUserLoggedIn = !!name;
            this.userName = name || null;
            this.userId = Arena.user.id || null;
        } catch (e) {
            this._isUserLoggedIn = false;
            console.warn('[Arena Handler] _checkUserLogin failed:', e?.message || e);
        }
    }

    /**
     * Check if cloud save features are available (auth resolved + save API ready).
     */
    isCloudFeatureAvailable() {
        return !!Arena.save && this._isUserLoggedIn;
    }

    /**
     * Debug method to verify cloud save state.
     */
    async debugSaveState() {
        if (!this.isCloudFeatureAvailable()) {
            console.log('[Arena Handler] Skipping save state debug: User not logged in (Guest).');
            return null;
        }

        try {
            // Try to get all saves (returns object with numbered string keys)
            const allSaves = await Arena.save.all();
            //console.log('[Arena Handler] DEBUG: All saves:', allSaves);

            // Count saves and log structure
            const saveCount = allSaves && typeof allSaves === 'object' ? Object.keys(allSaves).length : 0;
            //console.log('[Arena Handler] DEBUG: Save count:', saveCount);

            // Try to load slot 1
            let slot1 = null;
            try {
                slot1 = await Arena.save.load(1);
                //console.log('[Arena Handler] DEBUG: Slot 1 content:', slot1);
            } catch (loadError) {
                console.warn('[Arena Handler] DEBUG: Slot 1 load failed:', loadError.message);
            }

            if (this.levelText) {
                this.levelText.text = `Found ${saveCount} saves`;
            }

            return { allSaves, slot1, saveCount };
        } catch (error) {
            console.warn('[Arena Handler] Failed checking save state:', error);
            return null;
        }
    }

    /**
         * Optimized load: Minimal UI blocking and single-path execution.
         */
    async loadSavedProgress() {
        console.log('[Arena Handler] loadSavedProgress: entry');
        if (!this.isCloudFeatureAvailable()) {
            console.log('[Arena Handler] loadSavedProgress: cloud feature not available - applying local display');
            this._applyLoadedLevelDisplay();
            return;
        }

        try {
            if (this.levelText) this.levelText.text = `Loading...`;
            const saveSlot = await Arena.save.load(1);
            console.log('[Arena Handler] loadSavedProgress: saveSlot=', saveSlot ? '(found)' : '(none)');
            const data = saveSlot?.save_data;

            if (data && this.validateSaveData(data)) {
                console.log('[Arena Handler] loadSavedProgress: loaded valid save data, applying');
                this._isLoadingProgress = true;

                // Set core values with fallbacks
                this.prestige = Math.min(Number(data.prestige) || 0, ProgressionSystem.MAX_PRESTIGE);
                this.level = Number(data.level) || 1;
                this.totalXp = Number(data.totalXp) || 0;
                // Restore War Coins and economy
                if (data.economy) {
                    this.warCoins = Math.max(0, Number(data.economy.warCoins) || 0);
                    this.prestige = Math.min(Number(data.economy.prestigeTier) || 0, ProgressionSystem.MAX_PRESTIGE);
                    this.licenses = data.economy.licenses || { weapons: {}, attachments: [] };
                } else {
                    // Legacy migration
                    this.warCoins = Math.max(0, Number(data.warCoins) || 0);
                    this.licenses = { weapons: {}, attachments: Array.isArray(data.purchasedItems) ? data.purchasedItems.slice() : [] };
                    this._migrateLegacyUnlocksToPurchased(data);
                }
                
                // Restore Loadout
                if (data.loadout) {
                    this.loadout = data.loadout;
                } else {
                    // Legacy weaponSlots migration
                    const prim = (data.weaponSlots && data.weaponSlots[0]) || 'default';
                    const sec = (data.weaponSlots && data.weaponSlots[1]) || 'none';
                    this.loadout = {
                        primary: { id: prim, attachments: { laser: 'none', crosshair: 'none' } },
                        secondary: { id: sec, attachments: { laser: 'none', crosshair: 'none' } }
                    };
                }
                
                // Validate
                this.loadout = ProgressionSystem.validateLoadout(this.loadout, { prestigeTier: this.prestige, licenses: this.licenses });

                if (typeof window.updateWarCoinsDisplay === 'function') {
                    window.updateWarCoinsDisplay(this.warCoins);
                }

                this.updatePrestigeSymbols();

                // Restore custom loadout selections to localStorage
                if (typeof window !== 'undefined' && window.localStorage) {
                    try {
                        const localSettings = JSON.parse(window.localStorage.getItem('arena.settings') || '{}');
                        if (data.selectedWeapon) localSettings.selectedWeapon = data.selectedWeapon;
                        if (data.laserColor) localSettings.laserColor = data.laserColor;
                        if (data.crosshairSettings) {
                            for (const key in data.crosshairSettings) {
                                localSettings['crosshair_' + key] = data.crosshairSettings[key];
                            }
                        }
                        window.localStorage.setItem('arena.settings', JSON.stringify(localSettings));
                    } catch (e) {
                        console.warn('[Arena Handler] Error restoring loadout settings to localStorage:', e);
                    }
                }

                if (this.playerMovement) this.playerMovement._initLaserColors();

                this.restoreUnlocksFromSave(data);
                this._isLoadingProgress = false;
                this._applyLoadedLevelDisplay();
                // Mark that we've synced with remote save so saveProgress() won't double-load
                this._hasSyncedWithRemote = true;
                // Ensure leaderboard score reflects loaded progress (catches missed posts from prior sessions)
                if (this.leaderboard) {
                    this.leaderboard.updateLeaderboard(this.level, null, this.prestige);
                }
            }
        } catch (error) {
            console.error('[Arena Handler] Load Error:', error.message);
            this._applyLoadedLevelDisplay(); // Fallback to current state
        }
    }

    /**
     * Optimized save: Fetch-before-save with strict monotonicity.
     * Prevents over-frequent writes and avoids overwriting better progress.
     */
    /**
         * Optimized Save Logic
         * Resolves race conditions and reduces API overhead
         */
    async saveProgress(force = false) {
        // 1. Instant Exit: Check availability or if a save is already processing
        if (!this.isCloudFeatureAvailable() || this._isSaving) {
            return;
        }

        const now = this._elapsedMs;

        // 2. Immediate Throttle: Update attempt count and check timer
        this._saveAttemptCount = (this._saveAttemptCount || 0) + 1;

        // Throttle to 3 minutes unless forced
        if (!force && this._lastSaveTime && (now - this._lastSaveTime) < 180000) {
            return;
        }

        // 3. Lock the function
        this._isSaving = true;
        // Pre-emptively set the last save time to block rapid-fire "forced" calls
        const previousSaveTime = this._lastSaveTime;
        this._lastSaveTime = now;

        console.log(`[Arena Handler] saveProgress: Starting attempt #${this._saveAttemptCount} (force=${force})`);

        try {
            /**
             * OPTIMIZATION: We only perform the "Better Progress" check once at the 
             * start of the session (inside loadSavedProgress). Once the session is 
             * active, the local state is the source of truth.
             */
            if (!this._hasSyncedWithRemote) {
                console.log('[Arena Handler] saveProgress: Initial sync checking remote slot...');
                const existingSlot = await Arena.save.load(1).catch(() => null);

                if (existingSlot?.save_data) {
                    // SDK usually returns objects. Only parse if it's actually a string.
                    const remote = typeof existingSlot.save_data === 'string'
                        ? JSON.parse(existingSlot.save_data)
                        : existingSlot.save_data;

                    if (Number(remote.totalXp) > this.totalXp) {
                        console.log('[Arena Handler] Sync: Remote progress is better. Updating local state.');
                        this._applySaveDataToLocal(remote);
                        return;
                    }
                }
                this._hasSyncedWithRemote = true;
            }

            // 4. Prepare Data
            let localSettings = {};
            try {
                if (typeof window !== 'undefined' && window.localStorage) {
                    localSettings = JSON.parse(window.localStorage.getItem('arena.settings') || '{}');
                }
            } catch (e) {}

            const saveData = {
                level: Math.floor(this.level),
                totalXp: Math.floor(this.totalXp),
                lastSaved: new Date().toISOString(),
                gameVersion: '1.4.0', // Updated version for new economy
                
                economy: {
                    warCoins: Math.floor(this.warCoins || 0),
                    prestigeTier: Math.floor(this.prestige),
                    licenses: this.licenses
                },
                loadout: this.loadout
            };

            // 5. Persist to backend.
            // The level is encoded into the slot `name` for easy debugging.
            const success = await Arena.save.write(saveData, true, 1, `Lvl ${saveData.level}`);

            if (success) {
                console.log(`[Arena Handler] saveProgress: Success. Lvl ${saveData.level} / XP ${saveData.totalXp}`);
                if (this.levelText) this.levelText.text = `${saveData.level}`;
                // Sync leaderboard score whenever progress is successfully saved
                if (this.leaderboard) {
                    this.leaderboard.updateLeaderboard(saveData.level, null, saveData.prestige);
                }
            }

        } catch (error) {
            // Revert save time on failure so it can try again sooner
            this._lastSaveTime = previousSaveTime;
            console.error('[Arena Handler] Save Error:', error?.message || error);
        } finally {
            // 6. Release the Lock
            this._isSaving = false;
        }
    }

    /** Internal helper to apply data safely */
    _applySaveDataToLocal(data) {
        this._isLoadingProgress = true;
        this.totalXp = Number(data.totalXp) || 0;
        this.prestige = Math.min(Number(data.prestige) || 0, ProgressionSystem.MAX_PRESTIGE);
        this.level = Number(data.level) || 1;

        // Restore War Coins and economy
        if (data.economy) {
            this.warCoins = Math.max(0, Number(data.economy.warCoins) || 0);
            this.prestige = Math.min(Number(data.economy.prestigeTier) || 0, ProgressionSystem.MAX_PRESTIGE);
            this.licenses = data.economy.licenses || { weapons: {}, attachments: [] };
        } else {
            // Legacy migration
            this.warCoins = Math.max(0, Number(data.warCoins) || 0);
            this.licenses = { weapons: {}, attachments: Array.isArray(data.purchasedItems) ? data.purchasedItems.slice() : [] };
            this._migrateLegacyUnlocksToPurchased(data);
        }
        
        // Restore Loadout
        if (data.loadout) {
            this.loadout = data.loadout;
        } else {
            const prim = (data.weaponSlots && data.weaponSlots[0]) || 'default';
            const sec = (data.weaponSlots && data.weaponSlots[1]) || 'none';
            this.loadout = {
                primary: { id: prim, attachments: { laser: 'none', crosshair: 'none' } },
                secondary: { id: sec, attachments: { laser: 'none', crosshair: 'none' } }
            };
        }
        this.loadout = ProgressionSystem.validateLoadout(this.loadout, { prestigeTier: this.prestige, licenses: this.licenses });

        if (typeof window.updateWarCoinsDisplay === 'function') {
            window.updateWarCoinsDisplay(this.warCoins);
        }

        this.updatePrestigeSymbols();

        // Restore custom loadout selections to localStorage
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                const localSettings = JSON.parse(window.localStorage.getItem('arena.settings') || '{}');
                if (data.selectedWeapon) localSettings.selectedWeapon = data.selectedWeapon;
                if (data.laserColor) localSettings.laserColor = data.laserColor;
                if (data.crosshairSettings) {
                    for (const key in data.crosshairSettings) {
                        localSettings['crosshair_' + key] = data.crosshairSettings[key];
                    }
                }
                window.localStorage.setItem('arena.settings', JSON.stringify(localSettings));
            } catch (e) {
                console.warn('[Arena Handler] Error restoring loadout settings to localStorage:', e);
            }
        }

        if (this.playerMovement) this.playerMovement._initLaserColors();
        this.restoreUnlocksFromSave(data);
        this._applyLoadedLevelDisplay();
        this._hasSyncedWithRemote = true;
        this._isLoadingProgress = false;
    }
    /**
     * Get all save game information (for debugging)
     */
    async getSaveInfo() {
        try {
            const allSaves = await Arena.save.all();
            console.log('[Arena Handler] getSaveInfo: retrieved saves count=', allSaves ? Object.keys(allSaves).length : 0);

            // Log detailed information about each slot (slots are numeric strings)
            if (allSaves && typeof allSaves === 'object') {
                const slotKeys = Object.keys(allSaves);
                for (let i = 0; i < slotKeys.length; i++) {
                    const slotKey = slotKeys[i];
                    const slot = allSaves[slotKey];
                    //console.log(`[Arena Handler] Slot "${slotKey}" (string key):`, {
                    //    slotType: typeof slotKey,
                    //    name: slot.name,
                    //    hasData: !!slot.save_data,
                    //    dataKeys: slot.save_data ? Object.keys(slot.save_data) : [],
                    //    created: slot.created_at,
                    //    updated: slot.updated_at
                    //});
                }
            }

            return allSaves;
        } catch (error) {
            console.error('[Arena Handler] Error getting save info:', error.message || error);
            return null;
        }
    }

    /**
     * Execute save data wipe when allowSaveWipe property is set to true
     * Automatically called from start() if property is enabled
     */
    async executeSaveWipe() {
        try {
            console.log('[Arena Handler] executeSaveWipe: starting wipe');
            if (this.levelText) {
                this.levelText.text = `Wiping saves...`;
            }

            const success = await Arena.save.wipe();

            if (success) {
                // Reset local progression to match the wiped save state
                this.level = 1;
                this.totalXp = 0;
                this.xp = 0;
                this.prestige = 0;

                // Update UI to reflect reset state
                if (this.levelText) {
                    this.levelText.text = `Saves wiped!`;
                    setTimeout(() => {
                        if (this.levelText) {
                            this.levelText.text = `1`;
                        }
                    }, 3000);
                }
                this.updatePrestigeSymbols();
            } else {
                console.warn('[Arena Handler] Failed to clear save data');
                if (this.levelText) {
                    this.levelText.text = `Wipe failed!`;
                    setTimeout(() => {
                        if (this.levelText) {
                            this.levelText.text = `${this.level}`;
                        }
                    }, 3000);
                }
            }

            return success;
        } catch (error) {
            console.error('[Arena Handler] Error clearing save data:', error.message || error);
            if (this.levelText) {
                this.levelText.text = `Wipe error!`;
                setTimeout(() => {
                    if (this.levelText) {
                        this.levelText.text = `${this.level}`;
                    }
                }, 3000);
            }
            return false;
        }
    }

    /**
     * Clear all save data (for debugging/reset purposes).
     */
    async clearAllSaves() {
        try {
            const success = await Arena.save.wipe();
            return success;
        } catch (error) {
            console.error('[Arena Handler] Error clearing save data:', error.message || error);
            return false;
        }
    }

    /**
     * Manual test save and load (for debugging in VR)
     */
    async testSaveLoad() {
        if (this.levelText) {
            this.levelText.text = `Testing save/load...`;
        }

        try {
            // Force save current state
            await this.saveProgress(true);
            // Wait a moment
            await new Promise(resolve => setTimeout(resolve, 1000));
            // Try to load it back
            await this.loadSavedProgress();


            if (this.levelText) {
                this.levelText.text = `Test complete: Lvl${this.level}`;
                setTimeout(() => {
                    if (this.levelText) {
                        this.levelText.text = `${this.level}`;
                    }
                }, 5000);
            }

        } catch (error) {
            console.error('[Arena Handler] Manual test failed:', error);
            if (this.levelText) {
                this.levelText.text = `Test failed: ${error.message}`;
            }
        }
    }

    /**
     * Validate save data structure and content
     * @param {Object} data - Save data to validate
     * @returns {boolean} - True if data is valid, false otherwise
     */
    validateSaveData(data) {
        if (!data || typeof data !== 'object') {
            console.warn('[Arena Handler] Save data is not an object:', typeof data);
            return false;
        }

        // Validate JSON serialization (required by save API)
        try {
            const jsonString = JSON.stringify(data);
            // Also test that it can be parsed back
            JSON.parse(jsonString);
        } catch (jsonError) {
            console.warn('[Arena Handler] Save data is not JSON serializable:', jsonError);
            return false;
        }

        // Check required fields and types
        if (typeof data.level !== 'number') {
            console.warn('[Arena Handler] Save data missing or invalid level:', data.level);
            return false;
        }

        // Backwards compatibility: derive totalXp from level if missing (old save format)
        if (typeof data.totalXp !== 'number') {
            if (typeof data.level === 'number' && data.level >= 1) {
                const prestige = (typeof data.prestige === 'number') ? data.prestige : 0;
                const effectiveLevel = data.level + (prestige * ProgressionSystem.MAX_LEVEL);
                data.totalXp = ArenaHandler.getTotalXpForLevel(effectiveLevel);
                //console.log('[Arena Handler] Backwards compat: derived totalXp', data.totalXp, 'from level', data.level, 'prestige', prestige);
            } else {
                console.warn('[Arena Handler] Save data missing totalXp and cannot derive it');
                return false;
            }
        }

        // Validate numeric ranges and ensure no NaN/Infinity values
        if (data.level < 1 || !Number.isFinite(data.level)) {
            console.warn('[Arena Handler] Level must be >= 1 and finite:', data.level);
            return false;
        }

        if (data.totalXp < 0 || !Number.isFinite(data.totalXp)) {
            console.warn('[Arena Handler] Total XP must be non-negative and finite:', data.totalXp);
            return false;
        }

        // Validate prestige level
        if (data.prestige !== undefined && (typeof data.prestige !== 'number' || data.prestige < 0 || data.prestige > ProgressionSystem.MAX_PRESTIGE || !Number.isFinite(data.prestige))) {
            console.warn('[Arena Handler] Prestige out of valid range or not finite (0-' + ProgressionSystem.MAX_PRESTIGE + '):', data.prestige);
            return false;
        }

        // Validate level vs totalXp consistency
        const expectedEffectiveLevel = ArenaHandler.getEffectiveLevelFromXp(data.totalXp);
        const expectedPrestigeResult = ProgressionSystem.calculatePrestigeAndLevel(expectedEffectiveLevel);
        const expectedDisplayLevel = expectedPrestigeResult.level;

        // For validation, we compare the saved display level with what it should be
        // But allow some flexibility since prestige calculations might have edge cases
        if (Math.abs(data.level - expectedDisplayLevel) > 2) {
            console.warn('[Arena Handler] Level/XP mismatch - Saved Level:', data.level, 'Expected Display Level:', expectedDisplayLevel, 'Effective Level:', expectedEffectiveLevel, 'TotalXP:', data.totalXp);
            // Allow discrepancies but log them - prestige system can have edge cases
        }

        // Check game version if present
        if (data.gameVersion && typeof data.gameVersion !== 'string') {
            console.warn('[Arena Handler] Invalid game version format:', data.gameVersion);
            // Don't fail validation for this, just warn
        }

        // Check timestamps if present
        if (data.lastSaved && typeof data.lastSaved === 'string') {
            try {
                const date = new Date(data.lastSaved);
                if (isNaN(date.getTime())) {
                    console.warn('[Arena Handler] Invalid lastSaved timestamp:', data.lastSaved);
                }
            } catch (e) {
                console.warn('[Arena Handler] Error parsing lastSaved timestamp:', e);
            }
        }

        // Validate new unlock arrays if present (backward compatibility)
        if (data.unlockedWeapons && !Array.isArray(data.unlockedWeapons)) {
            console.warn('[Arena Handler] Invalid unlockedWeapons format (should be array):', data.unlockedWeapons);
            return false;
        }

        if (data.unlockedCrosshairs && !Array.isArray(data.unlockedCrosshairs)) {
            console.warn('[Arena Handler] Invalid unlockedCrosshairs format (should be array):', data.unlockedCrosshairs);
            return false;
        }

        if (data.unlockedLasers && !Array.isArray(data.unlockedLasers)) {
            console.warn('[Arena Handler] Invalid unlockedLasers format (should be array):', data.unlockedLasers);
            return false;
        }

        if (data.weaponSlots && !Array.isArray(data.weaponSlots)) {
            console.warn('[Arena Handler] Invalid weaponSlots format (should be array):', data.weaponSlots);
            return false;
        }

        //console.log('[Arena Handler] Save data validation passed:', {
        //    level: data.level,
        //    totalXp: data.totalXp,
        //    prestige: data.prestige,
        //    gameVersion: data.gameVersion,
        //    lastSaved: data.lastSaved,
        //    hasUnlockData: !!(data.unlockedWeapons || data.unlockedCrosshairs || data.unlockedLasers)
        //});

        return true;
    }

    /**
     * Get currently unlocked permanent weapons for save persistence
     * @returns {Array} Array of unlocked weapon names
     */
    getUnlockedWeapons() {
        const unlocked = ['default']; // Akimbo always available
        const weapons = ['rifle', 'shotgun', 'sniper'];

        for (const weapon of weapons) {
            if (ProgressionSystem.isWeaponPermanent(weapon, this.level, this.prestige)) {
                unlocked.push(weapon);
            }
        }

        return unlocked;
    }

    /**
     * Get currently unlocked crosshairs for save persistence
     * @returns {Array} Array of weapon types with unlocked crosshairs
     */
    getUnlockedCrosshairs() {
        const unlocked = [];
        const weaponTypes = ['default', 'rifle', 'shotgun', 'sniper'];

        for (const weaponType of weaponTypes) {
            if (ProgressionSystem.isCrosshairUnlocked(weaponType, this.level, this.prestige)) {
                unlocked.push(weaponType);
            }
        }

        return unlocked;
    }

    /**
     * Get currently unlocked lasers for save persistence
     * @returns {Array} Array of weapon types with unlocked lasers
     */
    getUnlockedLasers() {
        const unlocked = [];
        const weaponTypes = ['default', 'rifle', 'shotgun', 'sniper'];

        for (const weaponType of weaponTypes) {
            if (ProgressionSystem.isLaserUnlocked(weaponType, this.level, this.prestige)) {
                unlocked.push(weaponType);
            }
        }

        return unlocked;
    }

    /**
     * Restore unlocks and weapon slots from save data
     * @param {Object} data - Save data containing unlock information
     */
    restoreUnlocksFromSave(data) {
        if (!data) {
            this._isLoadingProgress = false;
            return;
        }
        // Update player's movement component with permanent weapons after delay to ensure it's initialized
        const self = this;
        setTimeout(() => {
            if (self.playerMovement && self.playerMovement.updateProgressionUnlocks) {
                self.playerMovement.updateProgressionUnlocks(self.level, self.prestige);
            }

            // Restore weapon slots if available in save data
            if (data.weaponSlots && Array.isArray(data.weaponSlots) && self.playerMovement) {
                // Validate that saved weapons are still unlocked
                const validSlots = [];
                for (const weapon of data.weaponSlots) {
                    if (!weapon) {
                        validSlots.push(null);
                    } else if (weapon === 'default' || ProgressionSystem.isWeaponPermanent(weapon, self.level, self.prestige)) {
                        validSlots.push(weapon);
                    } else {
                        validSlots.push(null); // Weapon no longer unlocked, clear slot
                    }
                }

                self.playerMovement._weaponSlots = validSlots;
                self.playerMovement._updateWeaponMeshes();
                self.playerMovement._updateIconHighlights();
            }
            // Loading fully complete — re-enable unlock notifications
            self._isLoadingProgress = false;
        }, 500); // Small delay to ensure movement component is ready
    }

    // Reset game state for new round after win
    resetGameState() {
        // Reset player stats   
        this.kills = 0;
        this.deaths = 0;
        this.health = 100;

        // Reset medal tracking (but keep progression unlocks)
        this.resetMedalTracking();

        // Reset UI elements
        if (this.killsText) this.killsText.text = '0';
        if (this.healthBar) this.healthBar.setScalingLocal([0.5, 0.01, 0.5]);
        // Reset progression bar for new round
        if (this.progressionBar) {
            const totalXpForCurrentLevel = ArenaHandler.getTotalXpForLevel(this.level);
            const totalXpForNextLevel = ArenaHandler.getTotalXpForLevel(this.level + 1);
            const xpProgress = this.totalXp - totalXpForCurrentLevel;
            const xpNeededForNext = totalXpForNextLevel - totalXpForCurrentLevel;
            const progressionScale = Math.min(xpProgress / xpNeededForNext, 1) * 0.5;
            this.progressionBar.setScalingLocal([progressionScale, this._progressScale[1], 0.5]);
        }
        // Note: victoryText is intentionally left visible here so it
        // fades to black with the scene. _resetSession() will clear it.
        if (this.xpDetailText) {
            this.xpDetailText.text = '';
        }

        // Clear player stats map and team assignments for new round
        this.playerStats.clear();
        this._teamMap.clear();
        this._playerColorMap.clear();

        // Reset game state flags
        this.inSummary = false;
        this._matchActive = false;
        this._isDead = false;
        this.justRespawned = false;

        // Ensure all remote avatars are re-enabled and shootable for the new round
        try {
            for (const [id, remote] of this.remotePlayerMap.entries()) {
                if (!remote) continue;
                try {
                    // Reactivate the avatar object so it becomes visible/updated
                    remote.active = true;

                    // Re-enable PhysX hit collider if present
                    const physx = remote._physxCache || (remote.getComponent ? remote.getComponent(PhysXComponent) : null);
                    if (physx) physx.active = true;

                    // Reset any respawn timers used to gate shooting
                    if (remote.justRespawnedTimer) remote.justRespawnedTimer = 0;

                    // Show weapons for remote if they've been assigned a weapon
                    const weaponSync = remote.getComponent ? remote.getComponent('weapon-sync') : null;
                    if (weaponSync && typeof weaponSync.showWeapons === 'function') {
                        weaponSync.showWeapons();
                    }
                } catch (e) {
                    // Protect reset from one-off remote errors
                    // console.warn(`[arena-handler] Failed to re-enable remote ${id}:`, e);
                }
            }
        } catch (e) {
            // Swallow top-level errors during reset to avoid breaking flow
        }

        // Fade to black, then return to main menu (lobby).
        // _resetSession() handles: stopping bots, abandoning lobby, showing lobby UI.
        this._startFade(1, [0, 0, 0], 0.5, () => {
            this._resetSession();
        });
    }

    /**
     * Check for AFK (Away From Keyboard) timeout and disconnect if inactive
     */
    _checkAfk(dt) {
        // Only check if connected and game is active
        if (!this.isConnected || !this.lobby || this.inSummary) {
            return;
        }

        // Update gamepad references if needed
        if (this.rightInput && this.rightInput.xrInputSource) {
            this.rightGamepad = this.rightInput.xrInputSource.gamepad;
        }
        if (this.leftInput && this.leftInput.xrInputSource) {
            this.leftGamepad = this.leftInput.xrInputSource.gamepad;
        }

        // Check for player movement/rotation
        // QUEST 2 OPTIMIZATION: Reuse pre-allocated vectors instead of creating new ones every frame
        this.player.getPositionWorld(this._afkCurrentPos);
        this.head.getRotationWorld(this._afkCurrentRot);

        let inputDetected = false;

        // Check position change
        if (!vec3.equals(this._afkCurrentPos, this._previousPlayerPos)) {
            inputDetected = true;
        }

        // Check rotation change
        if (!vec4.equals(this._afkCurrentRot, this._previousPlayerRot)) {
            inputDetected = true;
        }

        // Check controller inputs
        if (this.rightGamepad) {
            // Check buttons
            for (let i = 0; i < this.rightGamepad.buttons.length; i++) {
                if (this.rightGamepad.buttons[i].pressed) {
                    inputDetected = true;
                    break;
                }
            }
            // Check axes (with small threshold to ignore drift)
            if (!inputDetected) {
                for (let i = 0; i < this.rightGamepad.axes.length; i++) {
                    if (Math.abs(this.rightGamepad.axes[i]) > 0.1) {
                        inputDetected = true;
                        break;
                    }
                }
            }
        }

        if (this.leftGamepad && !inputDetected) {
            // Check buttons
            for (let i = 0; i < this.leftGamepad.buttons.length; i++) {
                if (this.leftGamepad.buttons[i].pressed) {
                    inputDetected = true;
                    break;
                }
            }
            // Check axes
            if (!inputDetected) {
                for (let i = 0; i < this.leftGamepad.axes.length; i++) {
                    if (Math.abs(this.leftGamepad.axes[i]) > 0.1) {
                        inputDetected = true;
                        break;
                    }
                }
            }
        }
        if (inputDetected) {
            // Reset AFK timer
            this._afkTimer = 0;
            // Update previous position/rotation
            vec3.copy(this._previousPlayerPos, this._afkCurrentPos);
            vec4.copy(this._previousPlayerRot, this._afkCurrentRot);

            // Send activity on input — throttled to once per second via the 5s setInterval
            // (removed per-frame sendToAll to cut network chatter)
        } else {
            // Accumulate AFK time using dt
            this._afkTimer += dt;
            if (this._afkTimer >= this._afkTimeoutMs / 1000) {
                this.handlePlayerDisconnect(this.userId);
                return; // Skip further checks
            }
        }
    }

    onDestroy() {
        // Clean up proximity voice chat
        if (this.proximityChat) {
            this.proximityChat.destroy();
        }
        if (this._activityInterval) {
            clearInterval(this._activityInterval);
            this._activityInterval = null;
        }
        if (this._boundOnXRSessionStart && this.engine && this.engine.onXRSessionStart) {
            this.engine.onXRSessionStart.remove(this._boundOnXRSessionStart);
        }
        if (this._boundOnXRSessionEnd && this.engine && this.engine.onXRSessionEnd) {
            this.engine.onXRSessionEnd.remove(this._boundOnXRSessionEnd);
        }
        if (this._boundOnDesktopSessionEnd) {
            window.removeEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
            window.removeEventListener('mobile-session-end',   this._boundOnDesktopSessionEnd);
        }
        if (this._boundOnDesktopSessionStart) {
            window.removeEventListener('desktop-session-start', this._boundOnDesktopSessionStart);
            window.removeEventListener('mobile-session-start',  this._boundOnDesktopSessionStart);
        }
        if (this._boundOnVisibilityChange) {
            document.removeEventListener('visibilitychange', this._boundOnVisibilityChange);
            window.removeEventListener('pagehide', this._boundOnVisibilityChange);
        }
        // Abort the saved-progress loader if it's still waiting on Arena.ready
        this._loadSaveAbort = true;
        if (this._afkKickTimeout) {
            clearTimeout(this._afkKickTimeout);
            this._afkKickTimeout = null;
        }
        // Clear any pending mode transition timer or notification timeout
        if (this._modeTransitionTimer != null) {
            clearTimeout(this._modeTransitionTimer);
            this._modeTransitionTimer = null;
        }
        if (this._modeNotifTimeout) {
            clearTimeout(this._modeNotifTimeout);
            this._modeNotifTimeout = null;
        }
    }
}
